// ═══════════════════════════════════════════════════════════════
// FEMA Reimbursement Package Builder
// Assembles ticket summaries, invoices, reconciliations, and
// disposal site data into exportable documentation packages
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect, Progress,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconRefresh, IconFileTypePdf, IconTrash, IconPencil,
  IconChevronDown, IconChevronUp, IconEye, IconCheck,
  IconArchive, IconDownload, IconFileText, IconDatabase,
} from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { notifications } from '../../lib/notifications';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { DEBRIS_TYPE_LABELS, decodeFaDocs, decodeTicketDocs } from '../../types/debris';
import { useClinDescriptions } from '../../hooks/useClinDescriptions';
import type { FEMAPackage, FEMAPackageStatus, SubcontractorInvoice } from '../../types/debris';
import { fetchRecords, queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { getV2FileDownloadUrl } from '../../lib/uploadFile';

// ── Force account record types ──────────────────────────────
interface FALaborRecord {
  employeeName: string; classification: string; dateWorked: string;
  hoursRegular: number; hoursOvertime: number; hourlyRate: number; totalCost: number;
  notes: string;
}
interface FAEquipmentRecord {
  fleetNumber: string; equipmentType: string; dateUsed: string;
  hoursUsed: number; hourlyRate: number; totalCost: number;
  notes: string;
}
interface DocRecord {
  id: number;
  documentType: string;
  title: string;
  fileId: string;
  notes: string;
}
interface TruckRecord {
  id: number;
  truckNumber: string;
  licensePlate: string;
  truckType: string;
  capacityCY: string;
  certStatus: string;
  certifiedDate: string;
  certExpiresAt: string;
  placardNumber: string;
  driverName: string;
  driverPhone: string;
  contractorId: string;
  notes: string;
}

// TRMETA decode for truck AI-extracted fields
const TRMETA_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
function decodeTruckMeta(raw: string): Record<string, string> {
  if (!raw) return {};
  const m = raw.match(TRMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}

// ── All-tickets record (loaded directly from API) ──────────
interface AllTicketRecord {
  id: number;
  ticketNumber: string;
  debrisType: string;
  calculatedCubicYards: string;
  contractorId: string;
  status: string;
  disposalSiteId: string;
  clinCode: string;
  clinDescription: string;
  extendedCost: string;
  transactionRate: string;
  subcontractorName: string;
  truckNumber: string;
  loadDate: string;
  disposalSiteName: string;
  pickupAddress: string;
  zoneId: string;
  pickupLat: string;
  pickupLng: string;
  notes: string;
}

// ── Status config ───────────────────────────────────────────
const STATUS_OPTIONS: { value: FEMAPackageStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready_for_review', label: 'Ready for Review' },
  { value: 'submitted_to_fema', label: 'Submitted to FEMA' },
  { value: 'approved', label: 'Approved' },
  { value: 'amended', label: 'Amended' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598',
  ready_for_review: '#3b82f6',
  submitted_to_fema: '#C68700',
  approved: 'var(--color-success)',
  amended: '#A627FF',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  ready_for_review: 'Ready for Review',
  submitted_to_fema: 'Submitted to FEMA',
  approved: 'Approved',
  amended: 'Amended',
};

// ── Empty form ──────────────────────────────────────────────
interface PackageForm {
  packageName: string;
  femaDisasterNumber: string;
  pwReference: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  contractorIds: string[];
  zoneIds: string[];
  notes: string;
  status: FEMAPackageStatus;
}

const EMPTY_FORM: PackageForm = {
  packageName: '',
  femaDisasterNumber: '',
  pwReference: '',
  dateRangeStart: '',
  dateRangeEnd: '',
  contractorIds: [],
  zoneIds: [],
  notes: '',
  status: 'draft',
};

// ── Component ───────────────────────────────────────────────
export default function FEMAPackages() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const { clinLabel } = useClinDescriptions();
  const incidentId = activeIncident?.id?.toString() ?? '';

  // Stores
  const {
    femaPackages, loadingFemaPackages, loadFemaPackages,
    createFemaPackage, updateFemaPackage, deleteFemaPackage,
    reconciliations, loadReconciliations,
    subInvoices, loadSubInvoices,
  } = useTampaDebrisStore();
  const { invoices, loadInvoices } = useDebrisOpsStore();
  const { contractors, zones, disposalSites, loadContractors, loadZones, loadDisposalSites } = useDebrisStore();

  // ── Load ALL tickets directly from API (not paginated store) ──
  const [allTickets, setAllTickets] = useState<AllTicketRecord[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);

  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;
    (async () => {
      setTicketsLoading(true);
      try {
        const FT = FIELDS.debrisLoadTickets;
        const PAGE = 500;
        let offset = 0, total = Infinity;
        const all: AllTicketRecord[] = [];
        while (offset < total) {
          const res = await queryRecords(TABLES.debrisLoadTickets, {
            limit: PAGE, offset,
          });
          if (cancelled) return;
          total = res.total;
          // GAB filter API is unreliable on this table — filter client-side
          const incidentRecords = res.records.filter((r: any) =>
            String(r[FT.incidentId] ?? '') === incidentId
          );
          incidentRecords.forEach((r: any) => {
            all.push({
              id: Number(r.record_id) || 0,
              ticketNumber: String(r[FT.ticketNumber] || ''),
              debrisType: String(r[FT.debrisType] || ''),
              calculatedCubicYards: String(parseFloat(String(r[FT.calculatedCubicYards] || '0')) || 0),
              contractorId: String(r[FT.contractorId] || ''),
              status: String(r[FT.status] || ''),
              disposalSiteId: String(r[FT.disposalSiteId] || ''),
              clinCode: String(r[FT.clinCode] || ''),
              clinDescription: String(r[FT.clinDescription] || ''),
              extendedCost: String(r[FT.extendedCost] || ''),
              transactionRate: String(r[FT.transactionRate] || ''),
              subcontractorName: String(r[FT.subcontractorName] || ''),
              truckNumber: String(r[FT.truckNumber] || ''),
              loadDate: String(r[FT.loadDate] || ''),
              disposalSiteName: String(r[FT.disposalSiteName] || ''),
              pickupAddress: String(r[FT.pickupAddress] || ''),
              zoneId: String(r[FT.zoneId] || ''),
              pickupLat: String(r[FT.pickupLat] || ''),
              pickupLng: String(r[FT.pickupLng] || ''),
              notes: String(r[FT.notes] || ''),
            });
          });
          offset += PAGE;
        }
        if (!cancelled) {
          setAllTickets(all);
          setTicketsLoaded(true);
        }
      } catch (e) {
        console.error('[FEMA] Failed to load tickets:', e);
      } finally {
        if (!cancelled) setTicketsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  // Force account + evidence + truck cache (loaded on demand)
  const [faLaborCache, setFaLaborCache] = useState<FALaborRecord[]>([]);
  const [faEquipCache, setFaEquipCache] = useState<FAEquipmentRecord[]>([]);
  const [docCache, setDocCache] = useState<DocRecord[]>([]);
  const [truckCache, setTruckCache] = useState<TruckRecord[]>([]);
  const [faCacheLoaded, setFaCacheLoaded] = useState(false);

  // State
  const [modalOpen, setModalOpen] = useState(false);
  const [wizStep, setWizStep] = useState(1);
  const [editTarget, setEditTarget] = useState<FEMAPackage | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FEMAPackage | null>(null);
  const [form, setForm] = useState<PackageForm>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [exporting, setExporting] = useState<number | null>(null);
  const [htmlExporting, setHtmlExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');

  // Index building
  const [indexBuilding, setIndexBuilding] = useState(false);
  const [indexProgress, setIndexProgress] = useState(0);
  const [indexStatus, setIndexStatus] = useState('');
  const [indexesLoaded, setIndexesLoaded] = useState(false);
  const [ticketPageIndex, setTicketPageIndex] = useState<Record<string, { file: string; page: number }> | null>(null);
  const [truckCertIndex, setTruckCertIndex] = useState<Record<string, { docId: string; cap: string; plate: string; sub: string }> | null>(null);

  // Filter ticket page index to only entries for this incident's tickets
  const filteredTicketPageIndex = useMemo(() => {
    if (!ticketPageIndex) return null;
    if (!allTickets.length) return ticketPageIndex;
    const ticketSet = new Set(allTickets.map(t => t.ticketNumber));
    const filtered = Object.fromEntries(
      Object.entries(ticketPageIndex).filter(([tn]) => ticketSet.has(tn))
    );
    return Object.keys(filtered).length > 0 ? filtered : ticketPageIndex;
  }, [ticketPageIndex, allTickets]);

  // Sorting
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIndicator = (col: string) =>
    sortField === col ? (sortDir === 'desc' ? ' \u2193' : ' \u2191') : '';

  // Load data
  useEffect(() => {
    loadFemaPackages(); loadContractors(); loadZones(); loadDisposalSites();
    loadInvoices(); loadReconciliations(); loadSubInvoices();
  }, [loadFemaPackages, loadContractors, loadZones, loadDisposalSites, loadInvoices, loadReconciliations, loadSubInvoices]);

  // Load force account + evidence + truck data — scoped to active incident
  const ensureFACache = useCallback(async () => {
    if (faCacheLoaded) return;
    if (!incidentId) return;
    const FL = FIELDS.debrisForceAccountLabor;
    const FE = FIELDS.debrisForceAccountEquipment;
    const FD = FIELDS.debrisDocuments;
    const FTK = FIELDS.debrisTrucks;
    const incFilter = (field: string) => [{ field, operator: 'eq' as const, value: incidentId }];
    try {
      // Load each independently — some Tampa tables may 403 depending on user role
      // All filtered by incidentId (server + client-side guard)
      const laborRes = await queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, { limit: 500, filters: incFilter(FL.incidentId) }).catch(() => ({ records: [] as any[], total: 0 }));
      const equipRes = await queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, { limit: 500, filters: incFilter(FE.incidentId) }).catch(() => ({ records: [] as any[], total: 0 }));
      const docRes = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 500, filters: incFilter(FD.incidentId) }).catch(() => ({ records: [] as any[], total: 0 }));
      const truckRes = await queryRecords<Record<string, unknown>>(TABLES.debrisTrucks, { limit: 500 }).catch(() => ({ records: [] as any[], total: 0 }));
      // Client-side guards (GAB server filter unreliable)
      const incContractorIds = new Set(contractors.map(c => String(c.id)));
      const laborRows = laborRes.records.filter((r: any) => String(r[FL.incidentId] ?? '') === incidentId);
      const equipRows = equipRes.records.filter((r: any) => String(r[FE.incidentId] ?? '') === incidentId);
      const docRows = docRes.records.filter((r: any) => String(r[FD.incidentId] ?? '') === incidentId);
      const truckRows = truckRes.records.filter((r: any) => {
        const cid = String(r[FTK.contractorId] ?? '');
        return !cid || cid === '0' || incContractorIds.has(cid);
      });
      setFaLaborCache(laborRows.map(r => ({
        employeeName: String(r[FL.employeeName] ?? ''),
        classification: String(r[FL.classification] ?? ''),
        dateWorked: String(r[FL.dateWorked] ?? ''),
        hoursRegular: Number(r[FL.hoursRegular]) || 0,
        hoursOvertime: Number(r[FL.hoursOvertime]) || 0,
        hourlyRate: Number(r[FL.hourlyRate]) || 0,
        totalCost: Number(r[FL.totalCost]) || 0,
        notes: String(r[FL.notes] ?? ''),
      })));
      setFaEquipCache(equipRows.map(r => ({
        fleetNumber: String(r[FE.fleetNumber] ?? ''),
        equipmentType: String(r[FE.equipmentType] ?? ''),
        dateUsed: String(r[FE.dateUsed] ?? ''),
        hoursUsed: Number(r[FE.hoursUsed]) || 0,
        hourlyRate: Number(r[FE.hourlyRate]) || 0,
        totalCost: Number(r[FE.totalCost]) || 0,
        notes: String(r[FE.notes] ?? ''),
      })));
      setDocCache(docRows.map(r => {
        const rawNotes = String(r[FD.notes] ?? '');
        const docIdMatch = rawNotes.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
        const documentId = docIdMatch ? docIdMatch[1] : '';
        return {
          id: Number(r.record_id) || 0,
          documentType: String(r[FD.documentType] ?? ''),
          title: String(r[FD.title] ?? ''),
          fileId: documentId || String(r[FD.file] ?? ''),
          notes: rawNotes,
        };
      }));
      setTruckCache(truckRows.map(r => ({
        id: Number(r.record_id) || 0,
        truckNumber: String(r[FTK.truckNumber] ?? ''),
        licensePlate: String(r[FTK.licensePlate] ?? ''),
        truckType: String(r[FTK.truckType] ?? ''),
        capacityCY: String(r[FTK.capacityCY] ?? ''),
        certStatus: String(r[FTK.certStatus] ?? ''),
        certifiedDate: String(r[FTK.certifiedDate] ?? ''),
        certExpiresAt: String(r[FTK.certExpiresAt] ?? ''),
        placardNumber: String(r[FTK.placardNumber] ?? ''),
        driverName: String(r[FTK.driverName] ?? ''),
        driverPhone: String(r[FTK.driverPhone] ?? ''),
        contractorId: String(r[FTK.contractorId] ?? ''),
        notes: String(r[FTK.notes] ?? ''),
      })));
      console.log('[FEMA Cache] Loaded (incident-scoped):', laborRows.length, 'labor,', equipRows.length, 'equip,', docRows.length, 'docs,', truckRows.length, 'trucks');
      setFaCacheLoaded(true);
    } catch (err) {
      console.error('Failed to load FA/evidence/truck data:', err);
    }
  }, [faCacheLoaded, incidentId, contractors]);

  // Pre-load truck + doc cache on mount
  useEffect(() => { ensureFACache(); }, [ensureFACache]);

  // ── Load existing indexes from GAB ───────────────────────────
  const loadIndexes = useCallback(async (): Promise<{
    ticketPageIndex: Record<string, { file: string; page: number }> | null;
    truckCertIndex: Record<string, { docId: string; cap: string; plate: string; sub: string }> | null;
  }> => {
    const FD = FIELDS.debrisDocuments;
    try {
      // Load documents filtered by incidentId (server + client-side guard)
      const allDocsRes = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
        limit: 500,
        ...(incidentId ? { filters: [{ field: FD.incidentId, operator: 'eq' as const, value: incidentId }] } : {}),
      });
      const allDocs = (allDocsRes.records || []).filter((r: any) =>
        !incidentId || String(r[FD.incidentId] ?? '') === incidentId
      );

      // Find ticket page index records
      const ticketIndexRecs = allDocs.filter((r: any) => String(r[FD.documentType] || '') === 'ticket_page_index');
      let tpi: Record<string, { file: string; page: number }> | null = null;
      if (ticketIndexRecs.length > 0) {
        // Check if chunked
        const chunks = ticketIndexRecs.filter((r: any) =>
          String(r[FD.notes] || '').startsWith('|||CHUNK|||')
        );
        if (chunks.length > 0) {
          // Reassemble chunked JSON
          const sorted = chunks.sort((a: any, b: any) => {
            const aMatch = String(a[FD.notes]).match(/\|\|\|CHUNK\|\|\|(\d+)\//);
            const bMatch = String(b[FD.notes]).match(/\|\|\|CHUNK\|\|\|(\d+)\//);
            return (parseInt(aMatch?.[1] || '0') - parseInt(bMatch?.[1] || '0'));
          });
          const fullJson = sorted.map((r: any) => {
            const notes = String(r[FD.notes]);
            return notes.replace(/^\|\|\|CHUNK\|\|\|\d+\/\d+\|\|\|/, '');
          }).join('');
          try { tpi = JSON.parse(fullJson); } catch { console.warn('[FEMA] Failed to parse chunked ticket page index'); }
        } else {
          // Single record — use the most recent one
          const notes = String(ticketIndexRecs[ticketIndexRecs.length - 1][FD.notes] || '');
          try { tpi = JSON.parse(notes); } catch { console.warn('[FEMA] Failed to parse ticket page index'); }
        }
      }

      // Find truck cert index records
      const truckIndexRecs = allDocs.filter((r: any) => String(r[FD.documentType] || '') === 'truck_cert_index');
      let tci: Record<string, { docId: string; cap: string; plate: string; sub: string }> | null = null;
      if (truckIndexRecs.length > 0) {
        const notes = String(truckIndexRecs[truckIndexRecs.length - 1][FD.notes] || '');
        try { tci = JSON.parse(notes); } catch { console.warn('[FEMA] Failed to parse truck cert index'); }
      }

      if (tpi) setTicketPageIndex(tpi);
      if (tci) setTruckCertIndex(tci);
      setIndexesLoaded(true);
      console.log(`[FEMA Indexes] Loaded: ${tpi ? Object.keys(tpi).length : 0} ticket pages, ${tci ? Object.keys(tci).length : 0} truck certs`);
      return { ticketPageIndex: tpi, truckCertIndex: tci };
    } catch (err) {
      console.warn('[FEMA] Failed to load indexes:', err);
      return { ticketPageIndex: null, truckCertIndex: null };
    }
  }, [incidentId]);

  // Reset caches when incident changes so stale data from other incidents is cleared
  useEffect(() => {
    setFaCacheLoaded(false);
    setFaLaborCache([]);
    setFaEquipCache([]);
    setDocCache([]);
    setTruckCache([]);
    setIndexesLoaded(false);
    setTicketPageIndex(null);
    setTruckCertIndex(null);
  }, [incidentId]);

  // Load indexes on mount / incident change
  useEffect(() => { if (incidentId) loadIndexes(); }, [loadIndexes, incidentId]);

  // ── Build document indexes (browser-side PDF scanning) ───────
  const buildDocumentIndexes = useCallback(async () => {
    setIndexBuilding(true);
    setIndexProgress(0);
    setIndexStatus('Loading PDF.js...');
    try {
      // Load bundled PDF.js (Vite resolves worker at build time)
      const pdfjsLib = await import('pdfjs-dist').then(m => m.default ?? m);
      if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url,
        ).href;
      }

      // 1. Find ticket image documents
      setIndexStatus('Finding ticket image PDFs...');
      const ticketImageDocs = (docCache || []).filter(d =>
        d.documentType === 'ticket_image' || d.documentType === 'tdms_scan'
      ).filter(d => d.fileId);

      if (ticketImageDocs.length === 0) {
        notifications.show({ message: 'No ticket image PDFs found in Documentation Archive. Upload ticket images first.', color: '#C68700' });
        return;
      }

      // 2. Scan each PDF for ticket numbers
      const newTicketIndex: Record<string, { file: string; page: number }> = {};
      let totalPages = 0;
      let scannedPages = 0;

      // First pass: estimate total pages by downloading all PDFs
      const pdfBuffers: { batchNum: number; data: ArrayBuffer; title: string }[] = [];
      for (let i = 0; i < ticketImageDocs.length; i++) {
        const doc = ticketImageDocs[i];
        setIndexStatus(`Downloading PDF ${i + 1}/${ticketImageDocs.length}: ${doc.title}...`);
        setIndexProgress(Math.round((i / ticketImageDocs.length) * 20)); // 0-20% for downloads

        try {
          const dlRes = await fetch(`/api/v2proxy/v2/apps/8q39q8q5n/documents/${doc.fileId}/download-url`);
          if (!dlRes.ok) continue;
          const dlData = await dlRes.json();
          const presignedUrl = dlData.presignedUrl ?? dlData.url;
          if (!presignedUrl) continue;
          const proxyUrl = `/api/filepreview?url=${encodeURIComponent(presignedUrl)}`;
          const res = await fetch(proxyUrl);
          if (!res.ok) continue;
          const data = await res.arrayBuffer();

          // Parse batch number from title
          const batchMatch = doc.title.match(/_(\d+)\.pdf$/i) || doc.title.match(/batch\s*(\d+)/i);
          const batchNum = batchMatch ? parseInt(batchMatch[1], 10) : (i + 1);
          pdfBuffers.push({ batchNum, data, title: doc.title });
        } catch (err) {
          console.warn(`[Index] Failed to download ${doc.title}:`, err);
        }
      }

      // Count total pages (slice buffer so PDF.js doesn't detach the original)
      for (const buf of pdfBuffers) {
        const pdfDoc = await pdfjsLib.getDocument({ data: new Uint8Array(buf.data.slice(0)) }).promise;
        totalPages += pdfDoc.numPages;
      }

      setIndexStatus(`Scanning ${totalPages.toLocaleString()} pages across ${pdfBuffers.length} PDFs...`);

      // Collect all occurrences: for each ticket number, track every (file, page, coCount) seen.
      // "coCount" = how many distinct ticket numbers appear on the same page.
      // We'll pick the page with the lowest coCount (dedicated scan pages have 1 ticket;
      // summary/table pages have dozens). Ties broken by first occurrence.
      const ticketCandidates: Record<string, { file: string; page: number; coCount: number }> = {};

      // Scan each PDF (slice buffer again — PDF.js transfers ArrayBuffers on load)
      for (const buf of pdfBuffers) {
        const pdfDoc = await pdfjsLib.getDocument({ data: new Uint8Array(buf.data.slice(0)) }).promise;
        const numPages = pdfDoc.numPages;

        for (let p = 1; p <= numPages; p++) {
          const page = await pdfDoc.getPage(p);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');

          // Ticket number format: flexible dash-separated numbers (Tampa: 7-4-6, standard: 6-5-5)
          const ticketMatches = pageText.match(/\d{4,8}-\d{3,7}-\d{4,8}/g);
          if (ticketMatches) {
            const unique = [...new Set(ticketMatches)] as string[];
            const coCount = unique.length; // how many distinct tickets on this page
            unique.forEach((tn: string) => {
              const existing = ticketCandidates[tn];
              // Prefer the page where this ticket appears with the fewest co-occurring tickets
              // (a solo page is almost certainly the dedicated scan; a page with 50 tickets is a summary)
              if (!existing || coCount < existing.coCount) {
                ticketCandidates[tn] = { file: buf.title, page: p, coCount };
              }
            });
          }

          scannedPages++;
          if (scannedPages % 25 === 0 || scannedPages === totalPages) {
            const pct = 20 + Math.round((scannedPages / totalPages) * 60); // 20-80% for scanning
            setIndexProgress(pct);
            setIndexStatus(`Scanned ${scannedPages.toLocaleString()}/${totalPages.toLocaleString()} pages (${Object.keys(ticketCandidates).length} tickets found)...`);
          }
        }
      }

      // Flatten candidates to final index (drop coCount)
      for (const [tn, candidate] of Object.entries(ticketCandidates)) {
        newTicketIndex[tn] = { file: candidate.file, page: candidate.page };
      }

      // 3. Build truck cert index from truck cache
      setIndexStatus('Building truck cert index...');
      setIndexProgress(85);
      const newTruckIndex: Record<string, { docId: string; cap: string; plate: string; sub: string }> = {};
      const TRMETA = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
      const DOCID = /\|\|\|DOCID\|\|\|([^|]+)\|\|\|/;
      truckCache.forEach(t => {
        if (!t.truckNumber) return;
        const mm = t.notes.match(TRMETA);
        let meta: Record<string, string> = {};
        if (mm) try { meta = JSON.parse(mm[1]); } catch { /* */ }
        const docIdMatch = t.notes.match(DOCID);
        newTruckIndex[t.truckNumber] = {
          docId: docIdMatch ? docIdMatch[1] : (meta.documentId || ''),
          cap: t.capacityCY,
          plate: t.licensePlate,
          sub: meta.subcontractor || meta.subcontractorName || '',
        };
      });

      // 4. Store indexes in GAB
      setIndexStatus('Storing ticket page index...');
      setIndexProgress(90);
      const FD = FIELDS.debrisDocuments;
      const { createRecord: createRec, deleteRecord: deleteRec, queryRecords: queryRec } = await import('../../api/client');
      const MAX_NOTES = 60000;

      // Delete all existing ticket_page_index and truck_cert_index records first
      // (so old-format chunks don't get mixed with new ones on reassembly)
      setIndexStatus('Clearing old index records...');
      try {
        const existingDocs = await queryRec<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 500 });
        const toDelete = (existingDocs.records || []).filter((r: any) =>
          String(r[FD.documentType] || '') === 'ticket_page_index' ||
          String(r[FD.documentType] || '') === 'truck_cert_index'
        );
        await Promise.all(toDelete.map((r: any) => deleteRec(TABLES.debrisDocuments, r.record_id).catch(() => {})));
      } catch (e) { console.warn('[Index] Failed to clear old records:', e); }

      // Store ticket page index
      const ticketJson = JSON.stringify(newTicketIndex);
      if (ticketJson.length <= MAX_NOTES) {
        await createRec(TABLES.debrisDocuments, {
          [FD.title]: `Ticket Page Index — ${femaPackages[0]?.femaDisasterNumber || activeIncident?.name || 'All'}`,
          [FD.documentType]: 'ticket_page_index',
          [FD.notes]: ticketJson,
          [FD.uploadedBy]: 'browser-indexer',
        });
      } else {
        // Split into chunks
        const chunks: string[] = [];
        for (let i = 0; i < ticketJson.length; i += MAX_NOTES) {
          chunks.push(ticketJson.slice(i, i + MAX_NOTES));
        }
        for (let i = 0; i < chunks.length; i++) {
          await createRec(TABLES.debrisDocuments, {
            [FD.title]: `Ticket Page Index — ${femaPackages[0]?.femaDisasterNumber || activeIncident?.name || 'All'} [${i + 1}/${chunks.length}]`,
            [FD.documentType]: 'ticket_page_index',
            [FD.notes]: `|||CHUNK|||${i + 1}/${chunks.length}|||${chunks[i]}`,
            [FD.uploadedBy]: 'browser-indexer',
          });
        }
      }

      // Store truck cert index
      setIndexStatus('Storing truck cert index...');
      setIndexProgress(95);
      await createRec(TABLES.debrisDocuments, {
        [FD.title]: `Truck Cert Index — ${femaPackages[0]?.femaDisasterNumber || activeIncident?.name || 'All'}`,
        [FD.documentType]: 'truck_cert_index',
        [FD.notes]: JSON.stringify(newTruckIndex),
        [FD.uploadedBy]: 'browser-indexer',
      });

      // 5. Update local state
      setTicketPageIndex(newTicketIndex);
      setTruckCertIndex(newTruckIndex);
      setIndexesLoaded(true);
      setIndexProgress(100);
      setIndexStatus(`Complete: ${Object.keys(newTicketIndex).length} ticket pages, ${Object.keys(newTruckIndex).length} truck certs`);

      notifications.show({
        message: `Document indexes built: ${Object.keys(newTicketIndex).length} ticket pages mapped, ${Object.keys(newTruckIndex).length} truck certs indexed`,
        color: 'var(--color-success)',
      });
    } catch (err) {
      console.error('[FEMA] Index build failed:', err);
      setIndexStatus(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      notifications.show({ message: `Index build failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    } finally {
      setIndexBuilding(false);
    }
  }, [docCache, truckCache]);

  // Lookup helpers
  const contractorName = useCallback(
    (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? id,
    [contractors],
  );

  const contractorSelect = useMemo(() =>
    contractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [contractors]);

  const zoneSelect = useMemo(() =>
    zones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `Zone #${z.id}` })),
  [zones]);

  // Filter out PW and submission records (managed in PW Register page)
  const PWMETA_RE_GLOBAL = /\|\|\|PWMETA\|\|\|/;
  const SUBMETA_RE_GLOBAL = /\|\|\|SUBMETA\|\|\|/;
  const regularPackages = useMemo(
    () => femaPackages.filter(p =>
      !PWMETA_RE_GLOBAL.test(p.notes || '') &&
      !SUBMETA_RE_GLOBAL.test(p.notes || '')
    ),
    [femaPackages],
  );

  // Sorted packages
  const sorted = useMemo(() => {
    const list = [...regularPackages];
    if (!sortField) return list.sort((a, b) => (b.id || 0) - (a.id || 0));
    return list.sort((a, b) => {
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [regularPackages, sortField, sortDir]);

  // ── Filter tickets for a package ──────────────────────────
  const getMatchedTickets = useCallback((pkg: FEMAPackage) => {
    const ds = normDate(pkg.dateRangeStart);
    const de = normDate(pkg.dateRangeEnd);
    return allTickets.filter(t => {
      if (pkg.contractorIds.length > 0 && t.contractorId && !pkg.contractorIds.includes(t.contractorId)) return false;
      if (t.status === 'voided') return false;
      const td = normDate(t.loadDate);
      if (ds && td && td < ds) return false;
      if (de && td && td > de) return false;
      return true;
    });
  }, [allTickets]);

  // ── Compute totals from tickets ───────────────────────────
  // Normalize date strings for comparison (handles MM/DD/YYYY, YYYY-MM-DD, ISO)
  const normDate = (d: string): string => {
    if (!d) return '';
    // If MM/DD/YYYY format
    const mdy = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (mdy) return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
    // Already YYYY-MM-DD or ISO
    return d.slice(0, 10);
  };

  const computeTotals = useCallback((contractorIds: string[], dateStart: string, dateEnd: string) => {
    const ds = normDate(dateStart);
    const de = normDate(dateEnd);

    // ── 1. Load tickets (volume + per-ticket extended cost) ──
    const matched = allTickets.filter(t => {
      if (contractorIds.length > 0 && t.contractorId && !contractorIds.includes(t.contractorId)) return false;
      if (t.status === 'voided') return false;
      const td = normDate(t.loadDate);
      if (ds && td && td < ds) return false;
      if (de && td && td > de) return false;
      return true;
    });
    const totalCY = matched.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);

    // ── 2. Contractor invoices (all types: CY-based, lump sum, hourly) ──
    const matchedInvoices = invoices.filter(inv => {
      if (contractorIds.length > 0 && !contractorIds.includes(inv.contractorId)) return false;
      if (dateStart && inv.periodEnd && inv.periodEnd < dateStart) return false;
      if (dateEnd && inv.periodStart && inv.periodStart > dateEnd) return false;
      return true;
    });
    const invoiceCost = matchedInvoices.reduce((s, inv) => s + (parseFloat(inv.totalAmount) || 0), 0);

    // ── 3. Force Account — labor (city/county employees) ──
    const matchedLabor = faLaborCache.filter(l => {
      const ld = normDate(l.dateWorked);
      if (ds && ld && ld < ds) return false;
      if (de && ld && ld > de) return false;
      return true;
    });
    const laborCost = matchedLabor.reduce((s, l) => s + (l.totalCost || 0), 0);

    // ── 4. Force Account — equipment (city/county owned) ──
    const matchedEquip = faEquipCache.filter(e => {
      const ed = normDate(e.dateUsed);
      if (ds && ed && ed < ds) return false;
      if (de && ed && ed > de) return false;
      return true;
    });
    const equipCost = matchedEquip.reduce((s, e) => s + (e.totalCost || 0), 0);

    const grandTotal = invoiceCost + laborCost + equipCost;

    return {
      totalCY:       Math.round(totalCY * 10) / 10,
      ticketCount:   matched.length,
      invoiceCost:   Math.round(invoiceCost * 100) / 100,
      invoiceCount:  matchedInvoices.length,
      laborCost:     Math.round(laborCost * 100) / 100,
      laborCount:    matchedLabor.length,
      equipCost:     Math.round(equipCost * 100) / 100,
      equipCount:    matchedEquip.length,
      grandTotal:    Math.round(grandTotal * 100) / 100,
      // legacy alias
      totalCost:     Math.round(grandTotal * 100) / 100,
    };
  }, [allTickets, invoices, faLaborCache, faEquipCache]);

  // ── Package detail data ───────────────────────────────────
  const getPackageDetail = useCallback((pkg: FEMAPackage) => {
    const matchedTickets = getMatchedTickets(pkg);

    // Group tickets by CLIN code
    const byClin: Record<string, { count: number; totalCY: number; totalCost: number; description: string; rate: string }> = {};
    matchedTickets.forEach(t => {
      const clin = t.clinCode || 'UNCODED';
      if (!byClin[clin]) byClin[clin] = { count: 0, totalCY: 0, totalCost: 0, description: t.clinDescription || t.debrisType || '', rate: t.transactionRate || '' };
      byClin[clin].count++;
      byClin[clin].totalCY += parseFloat(t.calculatedCubicYards) || 0;
      byClin[clin].totalCost += parseFloat(t.extendedCost) || 0;
    });

    // Group tickets by debris type (fallback view)
    const byType: Record<string, { count: number; totalCY: number; totalCost: number }> = {};
    matchedTickets.forEach(t => {
      const dt = t.debrisType || 'unknown';
      if (!byType[dt]) byType[dt] = { count: 0, totalCY: 0, totalCost: 0 };
      byType[dt].count++;
      byType[dt].totalCY += parseFloat(t.calculatedCubicYards) || 0;
      byType[dt].totalCost += parseFloat(t.extendedCost) || 0;
    });

    // Invoices in range
    const matchedInvoices = invoices.filter(inv => {
      if (!pkg.contractorIds.includes(inv.contractorId)) return false;
      if (pkg.dateRangeStart && inv.periodEnd && inv.periodEnd < pkg.dateRangeStart) return false;
      if (pkg.dateRangeEnd && inv.periodStart && inv.periodStart > pkg.dateRangeEnd) return false;
      return true;
    });

    // Reconciliations in range
    const matchedRecons = reconciliations.filter(rec => {
      if (!pkg.contractorIds.includes(rec.contractorId)) return false;
      if (pkg.dateRangeStart && rec.periodEnd && rec.periodEnd < pkg.dateRangeStart) return false;
      if (pkg.dateRangeEnd && rec.periodStart && rec.periodStart > pkg.dateRangeEnd) return false;
      return true;
    });

    // Disposal sites used — match by ID (FK) or by site name (text from import)
    const siteIds = new Set<string>();
    const siteNames = new Set<string>();
    const ticketsBySite: Record<string, number> = {};
    const cyBySite: Record<string, number> = {};
    matchedTickets.forEach(t => {
      const sid = t.disposalSiteId && t.disposalSiteId !== '0' ? t.disposalSiteId : null;
      const sname = t.disposalSiteName?.trim().toLowerCase();
      if (sid) {
        siteIds.add(sid);
        ticketsBySite[sid] = (ticketsBySite[sid] || 0) + 1;
        cyBySite[sid] = (cyBySite[sid] || 0) + (parseFloat(t.calculatedCubicYards) || 0);
      } else if (sname) {
        siteNames.add(sname);
      }
    });
    const matchedSites = disposalSites.filter(s =>
      siteIds.has(String(s.id)) ||
      siteNames.has((s.siteName || '').trim().toLowerCase())
    );

    // Total cost from CLIN
    const clinTotalCost = Object.values(byClin).reduce((s, c) => s + c.totalCost, 0);
    // Total invoice cost
    const invoiceTotalCost = matchedInvoices.reduce((s, inv) => s + (parseFloat(inv.totalAmount) || 0), 0);
    // Recon variance
    const reconVariance = matchedRecons.length > 0
      ? matchedRecons.reduce((s, r) => s + Math.abs(r.totalInvoiced - r.totalVerified), 0)
      : 0;
    const reconVariancePct = matchedRecons.length > 0
      ? matchedRecons.reduce((s, r) => s + r.variancePercent, 0) / matchedRecons.length
      : 0;

    // Truck certs for matched contractors
    const matchedTrucks = truckCache.filter(t => pkg.contractorIds.includes(t.contractorId));

    // Document counts
    const docsByType: Record<string, DocRecord[]> = {};
    docCache.forEach(d => {
      const t = d.documentType || 'other';
      if (!docsByType[t]) docsByType[t] = [];
      docsByType[t].push(d);
    });

    // Force account — filter by package date range (FA is city-wide, not contractor-specific)
    const matchedLabor = faLaborCache.filter(l => {
      const ld = normDate(l.dateWorked);
      if (pkg.dateRangeStart && ld && ld < normDate(pkg.dateRangeStart)) return false;
      if (pkg.dateRangeEnd && ld && ld > normDate(pkg.dateRangeEnd)) return false;
      return true;
    }).sort((a, b) => a.dateWorked.localeCompare(b.dateWorked));

    const matchedEquip = faEquipCache.filter(e => {
      const ed = normDate(e.dateUsed);
      if (pkg.dateRangeStart && ed && ed < normDate(pkg.dateRangeStart)) return false;
      if (pkg.dateRangeEnd && ed && ed > normDate(pkg.dateRangeEnd)) return false;
      return true;
    }).sort((a, b) => a.dateUsed.localeCompare(b.dateUsed));

    const faLaborTotal = matchedLabor.reduce((s, l) => s + l.totalCost, 0);
    const faEquipTotal = matchedEquip.reduce((s, e) => s + e.totalCost, 0);

    return {
      matchedTickets,
      clinSummary: Object.entries(byClin).map(([code, data]) => ({
        clinCode: code, ...data,
      })).sort((a, b) => b.totalCost - a.totalCost),
      ticketsByType: Object.entries(byType).map(([type, data]) => ({
        type,
        label: DEBRIS_TYPE_LABELS[type] || type,
        ...data,
        avgCY: data.count > 0 ? Math.round((data.totalCY / data.count) * 10) / 10 : 0,
      })).sort((a, b) => b.totalCY - a.totalCY),
      invoices: matchedInvoices,
      reconciliations: matchedRecons,
      sites: matchedSites.map(s => ({
        ...s,
        ticketCount: ticketsBySite[String(s.id)] || 0,
        receivedCY: Math.round((cyBySite[String(s.id)] || 0) * 10) / 10,
      })),
      totalTickets: matchedTickets.length,
      clinTotalCost,
      invoiceTotalCost,
      reconVariance,
      reconVariancePct,
      truckCertCount: matchedTrucks.length,
      trucks: matchedTrucks,
      docsByType,
      totalDocs: docCache.length,
      faLabor: matchedLabor,
      faLaborTotal: Math.round(faLaborTotal * 100) / 100,
      faEquip: matchedEquip,
      faEquipTotal: Math.round(faEquipTotal * 100) / 100,
    };
  }, [allTickets, invoices, reconciliations, disposalSites, truckCache, docCache, faLaborCache, faEquipCache, getMatchedTickets]);

  // ── Modal handlers ────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setWizStep(1);
    setModalOpen(true);
  };

  const openEdit = (pkg: FEMAPackage) => {
    setEditTarget(pkg);
    setForm({
      packageName: pkg.packageName,
      femaDisasterNumber: pkg.femaDisasterNumber,
      pwReference: pkg.pwReference,
      dateRangeStart: pkg.dateRangeStart,
      dateRangeEnd: pkg.dateRangeEnd,
      contractorIds: pkg.contractorIds || [],
      zoneIds: pkg.zoneIds || [],
      notes: pkg.notes,
      status: pkg.status,
    });
    setWizStep(1);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.packageName.trim()) {
      notifications.show({ message: 'Package name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const { totalCY, grandTotal: totalCost } = computeTotals(form.contractorIds, form.dateRangeStart, form.dateRangeEnd);
      const payload: Record<string, unknown> = {
        incidentId,
        packageName: form.packageName,
        femaDisasterNumber: form.femaDisasterNumber,
        pwReference: form.pwReference,
        dateRangeStart: form.dateRangeStart,
        dateRangeEnd: form.dateRangeEnd,
        contractorIds: form.contractorIds,
        zoneIds: form.zoneIds,
        totalCubicYards: totalCY,
        totalCost,
        notes: form.notes,
        status: form.status,
      };

      if (editTarget) {
        payload.version = (editTarget.version || 1);
        await updateFemaPackage(editTarget.id, payload);
        notifications.show({ message: 'Package updated', color: 'var(--color-success)' });
      } else {
        payload.version = 1;
        await createFemaPackage(payload);
        notifications.show({ message: 'Package created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      // Explicit reload as safety net — store's internal reload may race with GAB write propagation
      loadFemaPackages();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteFemaPackage(deleteTarget.id);
      notifications.show({ message: `Package "${deleteTarget.packageName}" deleted`, color: '#939598' });
      setDeleteTarget(null);
      if (expandedId === deleteTarget.id) setExpandedId(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (pkg: FEMAPackage, newStatus: string) => {
    try {
      await updateFemaPackage(pkg.id, { status: newStatus });
      notifications.show({ message: `Status updated to ${STATUS_LABELS[newStatus] || newStatus}`, color: 'var(--color-success)' });
    } catch {
      notifications.show({ message: 'Status update failed', color: 'var(--color-error)' });
    }
  };

  // ── PDF Helpers ───────────────────────────────────────────
  const pdfSafe = (s: string) => s.replace(/[^\x20-\x7E]/g, c => {
    if (c === '\u2014' || c === '\u2013') return '-';
    if (c === '\u2018' || c === '\u2019') return "'";
    if (c === '\u201C' || c === '\u201D') return '"';
    return '';
  });

  const fmtMon = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtCY = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const fmtDate = (d: string) => {
    if (!d) return '-';
    const s = d.slice(0, 10);
    if (s.includes('-')) {
      const [y, m, day] = s.split('-');
      return `${m}/${day}/${y}`;
    }
    return s;
  };

  // ── View document in new tab ──────────────────────────────
  const viewDocument = async (doc: DocRecord) => {
    if (!doc.fileId) {
      notifications.show({ message: 'No file attached to this document', color: '#C68700' });
      return;
    }
    try {
      const presignedUrl = await getV2FileDownloadUrl(doc.fileId);
      const proxyUrl = `/api/filepreview?url=${encodeURIComponent(presignedUrl)}`;
      window.open(proxyUrl, '_blank');
    } catch (err) {
      notifications.show({ message: 'Failed to get document URL', color: 'var(--color-error)' });
    }
  };

  // ── Fetch document bytes for ZIP ──────────────────────────
  const fetchDocBytes = async (doc: DocRecord): Promise<{ name: string; data: ArrayBuffer } | null> => {
    if (!doc.fileId) return null;
    try {
      const presignedUrl = await getV2FileDownloadUrl(doc.fileId);
      const proxyUrl = `/api/filepreview?url=${encodeURIComponent(presignedUrl)}`;
      const resp = await fetch(proxyUrl);
      if (!resp.ok) return null;
      const data = await resp.arrayBuffer();
      // Derive a filename from the title
      const safeName = (doc.title || `document_${doc.id}`).replace(/[^a-zA-Z0-9_\-. ]/g, '_');
      return { name: safeName, data };
    } catch {
      return null;
    }
  };

  // ── Generate narrative PDF (FEMA PW Backup Documentation) ──
  const generateCoverPDF = (pkg: FEMAPackage, detail: ReturnType<typeof getPackageDetail>): ArrayBuffer => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const BLURPLE: [number, number, number] = [75, 63, 255];
    const ALT_ROW: [number, number, number] = [245, 246, 250];
    const HEAD_BG = BLURPLE;
    const pwRef = pkg.pwReference || '-';
    const totalCY = detail.matchedTickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);
    const contractorNames = pkg.contractorIds.map(id => pdfSafe(contractorName(id))).join(', ') || '-';

    // Count DEP-authorized sites
    const depSiteCount = detail.sites.filter(s =>
      (s.siteType || '').toLowerCase().includes('dep') || (s.notes || '').toLowerCase().includes('dep')
    ).length;

    // ═══════════════════════════════════════════════════════════
    // PAGE 1: Executive Summary
    // ═══════════════════════════════════════════════════════════
    // Title bar
    doc.setFillColor(...BLURPLE);
    doc.rect(0, 0, pageW, 28, 'F');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('FEMA PROJECT WORKSHEET BACKUP DOCUMENTATION', 14, 18);
    doc.setTextColor(0);

    let y = 38;
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    const summaryFields = [
      ['Disaster', pdfSafe(pkg.femaDisasterNumber || 'TBD') + (activeIncident?.name ? ` - ${pdfSafe(activeIncident.name)}` : '')],
      ['Applicant', 'City of Tampa, FL'],
      ['Contractor', pdfSafe(contractorNames)],
      ['Period', `${fmtDate(pkg.dateRangeStart)} - ${fmtDate(pkg.dateRangeEnd)}`],
      ['PW Reference', pdfSafe(pwRef)],
    ];
    summaryFields.forEach(([label, val]) => {
      doc.setFont('helvetica', 'bold'); doc.text(`${label}:`, 14, y);
      doc.setFont('helvetica', 'normal'); doc.text(val, 55, y);
      y += 7;
    });

    y += 6;
    doc.setFillColor(245, 246, 250);
    doc.roundedRect(14, y - 4, pageW - 28, 60, 3, 3, 'F');
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY', 20, y + 6); y += 14;
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    const summaryLines = [
      `Total Loads Verified: ${detail.totalTickets.toLocaleString()}`,
      `Total Cubic Yards: ${fmtCY(totalCY)} CY`,
      `Total Cost: ${fmtMon(detail.clinTotalCost)}`,
      `Independent Monitor: Thompson Consulting Services (TDMS)`,
      `Reconciliation Result: ${fmtMon(detail.reconVariance)} variance (${detail.reconVariancePct.toFixed(1)}%) - ${Math.abs(detail.reconVariancePct) <= 5 ? 'within acceptable threshold' : 'exceeds threshold'}`,
    ];
    summaryLines.forEach(line => { doc.text(line, 20, y); y += 6.5; });

    y += 10;
    const certLines = [
      `Truck Certifications: ${detail.truckCertCount} trucks verified`,
      `Disposal Sites: ${detail.sites.length} sites${depSiteCount > 0 ? ` (${depSiteCount} DEP pre-authorized)` : ''}`,
    ];
    certLines.forEach(line => { doc.text(line, 14, y); y += 6.5; });

    // ═══════════════════════════════════════════════════════════
    // PAGE 2-3: CLIN Rate Breakdown
    // ═══════════════════════════════════════════════════════════
    doc.addPage();
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLURPLE);
    doc.text('CLIN RATE BREAKDOWN', 14, 18);
    doc.setTextColor(0);

    if (detail.clinSummary.length > 0) {
      const grandTotalCost = detail.clinTotalCost || 1;
      autoTable(doc, {
        startY: 24,
        head: [['CLIN Code', 'Description', 'Quantity (CY)', 'Unit Rate', 'Extended Cost', '% of Total']],
        body: detail.clinSummary.map(row => [
          pdfSafe(row.clinCode) + (row.description ? ' — ' + pdfSafe(row.description) : ''),
          pdfSafe(row.description),
          fmtCY(row.totalCY),
          row.rate ? fmtMon(parseFloat(row.rate) || 0) : '-',
          fmtMon(row.totalCost),
          `${((row.totalCost / grandTotalCost) * 100).toFixed(1)}%`,
        ]),
        foot: [[
          'GRAND TOTAL', '',
          fmtCY(detail.clinSummary.reduce((s, r) => s + r.totalCY, 0)),
          '',
          fmtMon(detail.clinTotalCost),
          '100.0%',
        ]],
        headStyles: { fillColor: HEAD_BG, textColor: 255, fontStyle: 'bold', fontSize: 8 },
        footStyles: { fillColor: [230, 230, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9 },
        styles: { fontSize: 9, halign: 'center', cellPadding: 3 },
        columnStyles: { 0: { halign: 'left' }, 1: { halign: 'left', cellWidth: 55 } },
        alternateRowStyles: { fillColor: ALT_ROW },
      });
    } else {
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text('No CLIN-coded tickets found for this period.', 14, 30);
    }

    // ═══════════════════════════════════════════════════════════
    // PAGE: Reconciliation Detail
    // ═══════════════════════════════════════════════════════════
    doc.addPage();
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLURPLE);
    doc.text('RECONCILIATION DETAIL', 14, 18);
    doc.setTextColor(0);

    if (detail.reconciliations.length > 0) {
      autoTable(doc, {
        startY: 24,
        head: [['CLIN / Period', 'Contractor Claimed', 'Monitor Verified', 'Variance', 'Variance %']],
        body: detail.reconciliations.map(rec => {
          const variance = rec.totalInvoiced - rec.totalVerified;
          return [
            pdfSafe(`${contractorName(rec.contractorId)} (${fmtDate(rec.periodStart)} - ${fmtDate(rec.periodEnd)})`),
            fmtMon(rec.totalInvoiced),
            fmtMon(rec.totalVerified),
            fmtMon(Math.abs(variance)),
            `${rec.variancePercent.toFixed(1)}%`,
          ];
        }),
        headStyles: { fillColor: HEAD_BG, textColor: 255, fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 9, halign: 'center', cellPadding: 3 },
        columnStyles: { 0: { halign: 'left', cellWidth: 65 } },
        alternateRowStyles: { fillColor: ALT_ROW },
      });

      const reconY = (doc as any).lastAutoTable?.finalY ?? 60;
      const rY = reconY + 10;
      doc.setFontSize(10); doc.setFont('helvetica', 'bold');
      doc.text('Summary:', 14, rY);
      doc.setFont('helvetica', 'normal');
      const totalInvoiced = detail.reconciliations.reduce((s, r) => s + r.totalInvoiced, 0);
      const totalVerified = detail.reconciliations.reduce((s, r) => s + r.totalVerified, 0);
      doc.text(`Total Invoiced: ${fmtMon(totalInvoiced)}`, 14, rY + 7);
      doc.text(`Total Verified: ${fmtMon(totalVerified)}`, 14, rY + 14);
      doc.text(`Recommended Payment: ${fmtMon(totalVerified)}`, 14, rY + 21);
    } else {
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text('No reconciliations found for this period.', 14, 30);
    }

    // ═══════════════════════════════════════════════════════════
    // PAGE: Truck Certification Summary
    // ═══════════════════════════════════════════════════════════
    doc.addPage();
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLURPLE);
    doc.text('TRUCK CERTIFICATION SUMMARY', 14, 18);
    doc.setTextColor(0);

    if (detail.trucks.length > 0) {
      const truckRows = detail.trucks.map(t => {
        const meta = decodeTruckMeta(t.notes);
        const tagState = meta.tagState || meta.state || (t.licensePlate ? 'FL' : '-');
        const subName = meta.subcontractorName || meta.subcontractor || contractorName(t.contractorId);
        return [
          pdfSafe(t.truckNumber),
          t.capacityCY ? `${t.capacityCY} CY` : '-',
          pdfSafe(t.licensePlate || '-'),
          pdfSafe(tagState),
          pdfSafe(t.truckType || '-'),
          pdfSafe(subName),
          pdfSafe(t.driverName || '-'),
          fmtDate(t.certifiedDate),
        ];
      });
      autoTable(doc, {
        startY: 24,
        head: [['Truck #', 'Capacity', 'License Plate', 'State', 'Type', 'Subcontractor', 'Driver', 'Cert Date']],
        body: truckRows,
        headStyles: { fillColor: HEAD_BG, textColor: 255, fontStyle: 'bold', fontSize: 7 },
        styles: { fontSize: 7, halign: 'center', cellPadding: 2 },
        columnStyles: { 0: { halign: 'left' }, 4: { halign: 'left' }, 5: { halign: 'left' }, 6: { halign: 'left' } },
        alternateRowStyles: { fillColor: ALT_ROW },
      });
      const truckFinalY = (doc as any).lastAutoTable?.finalY ?? 60;
      doc.setFontSize(8); doc.setFont('helvetica', 'italic');
      doc.setTextColor(100);
      doc.text('Individual truck certification PDFs with photographs available on request.', 14, truckFinalY + 6);
      doc.setTextColor(0);
    } else {
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text('No truck certifications on file for this period.', 14, 30);
    }

    // ═══════════════════════════════════════════════════════════
    // PAGE: Disposal Site Summary
    // ═══════════════════════════════════════════════════════════
    doc.addPage();
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLURPLE);
    doc.text('DISPOSAL SITE SUMMARY', 14, 18);
    doc.setTextColor(0);

    if (detail.sites.length > 0) {
      const siteRows = detail.sites.map(s => {
        const sType = (s.siteType || '-').toUpperCase();
        let acceptedTypes = '-';
        try {
          const parsed = JSON.parse(s.acceptedTypes || '[]');
          if (Array.isArray(parsed) && parsed.length > 0) acceptedTypes = parsed.join(', ');
        } catch { /* ignore */ }
        const depAuth = (s.notes || '').toLowerCase().includes('dep') ? 'DEP Authorized' : '-';
        return [
          pdfSafe(s.siteName || '-'),
          sType,
          depAuth,
          acceptedTypes,
          String(s.ticketCount),
          fmtCY(s.receivedCY),
        ];
      });
      autoTable(doc, {
        startY: 24,
        head: [['Site Name', 'Type', 'DEP Auth', 'Accepted Waste Types', 'Loads Received', 'CY Received']],
        body: siteRows,
        headStyles: { fillColor: HEAD_BG, textColor: 255, fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 9, halign: 'center', cellPadding: 3 },
        columnStyles: { 0: { halign: 'left', cellWidth: 40 }, 3: { halign: 'left', cellWidth: 45 } },
        alternateRowStyles: { fillColor: ALT_ROW },
      });
      const siteFinalY = (doc as any).lastAutoTable?.finalY ?? 60;
      doc.setFontSize(8); doc.setFont('helvetica', 'italic');
      doc.setTextColor(100);
      doc.text('DEP compliance status verified through FL DEP Waste Management Division.', 14, siteFinalY + 6);
      doc.setTextColor(0);
    } else {
      doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text('No disposal sites used during this period.', 14, 30);
    }

    // ═══════════════════════════════════════════════════════════
    // PAGE: Source Document Index
    // ═══════════════════════════════════════════════════════════
    doc.addPage();
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLURPLE);
    doc.text('SOURCE DOCUMENT INDEX', 14, 18);
    doc.setTextColor(0);

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    let docY = 26;
    let docIdx = 1;

    // Build document index from actual docs + computed items
    const docIndexItems: string[] = [];
    detail.invoices.forEach(inv => {
      docIndexItems.push(`Contractor Invoice: ${pdfSafe(inv.invoiceNumber || 'Invoice #' + inv.id)} (${fmtMon(parseFloat(inv.totalAmount) || 0)})`);
    });
    detail.reconciliations.forEach(rec => {
      docIndexItems.push(`Monitor Reconciliation Report: ${pdfSafe(contractorName(rec.contractorId))} (${fmtDate(rec.periodStart)} - ${fmtDate(rec.periodEnd)})`);
    });
    if (detail.totalTickets > 0) {
      docIndexItems.push(`TDMS Load Ticket Data: ${detail.totalTickets.toLocaleString()} records`);
      docIndexItems.push(`Transaction Detail: Ticket-level CLIN breakdown`);
    }
    if (detail.truckCertCount > 0) {
      docIndexItems.push(`Truck Certifications: ${detail.truckCertCount} individual PDFs`);
    }
    docCache.forEach(d => {
      const typeLbl = (d.documentType || 'document').replace(/_/g, ' ');
      docIndexItems.push(`${typeLbl}: ${pdfSafe(d.title || 'Document #' + d.id)}`);
    });
    docIndexItems.push('DDMS Site Pre-Authorization Letter (FL DEP)');
    docIndexItems.push('Emergency Management Master Plan 2023-2024');

    docIndexItems.forEach(item => {
      if (docY > 270) { doc.addPage(); docY = 20; }
      doc.text(`${docIdx}. ${item}`, 14, docY);
      docIdx++;
      docY += 6;
    });

    // ═══════════════════════════════════════════════════════════
    // Footer on every page
    // ═══════════════════════════════════════════════════════════
    const pageCount = doc.getNumberOfPages();
    const disasterNum = pdfSafe(pkg.femaDisasterNumber || 'TBD');
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageH = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200);
      doc.line(14, pageH - 16, pageW - 14, pageH - 16);
      doc.setFontSize(7);
      doc.setTextColor(120);
      doc.text(`City of Tampa - FEMA ${disasterNum} - PW ${pdfSafe(pwRef)} - Page ${i} of ${pageCount}`, 14, pageH - 10);
      doc.text(`Generated ${new Date().toLocaleDateString()} | OpenGov EMC`, pageW - 14, pageH - 10, { align: 'right' });
      doc.setTextColor(0);
    }

    return doc.output('arraybuffer');
  };

  // ── Generate ticket CSV ───────────────────────────────────
  const generateTicketCSV = (tickets: AllTicketRecord[]): string => {
    const headers = [
      'Ticket Number', 'Load Date', 'Debris Type', 'CLIN Code', 'CLIN Description',
      'Contractor ID', 'Subcontractor', 'Truck Number', 'Disposal Site ID',
      'Disposal Site Name', 'Cubic Yards', 'Rate', 'Extended Cost',
      'Pickup Address', 'Zone', 'Status',
    ];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = tickets.map(t => [
      t.ticketNumber, t.loadDate, t.debrisType, t.clinCode, t.clinDescription,
      t.contractorId, t.subcontractorName, t.truckNumber, t.disposalSiteId,
      t.disposalSiteName, t.calculatedCubicYards, t.transactionRate, t.extendedCost,
      t.pickupAddress, t.zoneId, t.status,
    ].map(v => csvEscape(String(v || ''))));
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  // ── Generate truck cert manifest CSV (with TRMETA AI-extracted fields) ──
  const generateTruckManifestCSV = (trucks: TruckRecord[]): string => {
    const headers = [
      'Truck #', 'Capacity CY', 'License Plate', 'Tag State', 'Vehicle Type',
      'Subcontractor', 'Driver', 'Driver Phone', 'Cert Date', 'Cert Expires',
      'Certified By', 'Has Tailgate',
    ];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = trucks.map(t => {
      const meta = decodeTruckMeta(t.notes);
      const tagState = meta.tagState || meta.state || (t.licensePlate ? 'FL' : '');
      const subName = meta.subcontractorName || meta.subcontractor || contractorName(t.contractorId);
      const certBy = meta.certifiedByName || meta.certifiedBy || '';
      const hasTailgate = meta.hasTailgate || '';
      return [
        t.truckNumber, t.capacityCY, t.licensePlate, tagState, t.truckType,
        subName, t.driverName, t.driverPhone, t.certifiedDate, t.certExpiresAt,
        certBy, hasTailgate,
      ].map(v => csvEscape(String(v || '')));
    });
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  // ── Generate reconciliation CSV ──────────────────────────
  const generateReconCSV = (recons: typeof reconciliations): string => {
    const headers = [
      'CLIN Code', 'Description', 'Invoiced Qty', 'Verified Qty', 'Variance',
      'Variance %', 'Invoiced $', 'Verified $',
    ];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = recons.map(rec => {
      const variance = rec.totalInvoiced - rec.totalVerified;
      return [
        contractorName(rec.contractorId),
        `${fmtDate(rec.periodStart)} - ${fmtDate(rec.periodEnd)}`,
        '', '',
        variance.toFixed(2),
        `${rec.variancePercent.toFixed(1)}%`,
        rec.totalInvoiced.toFixed(2),
        rec.totalVerified.toFixed(2),
      ].map(v => csvEscape(String(v || '')));
    });
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  // ── Generate site summary CSV ────────────────────────────
  const generateSiteSummaryCSV = (sites: ReturnType<typeof getPackageDetail>['sites']): string => {
    const headers = [
      'Site Name', 'Type', 'DEP Auth ID', 'Address', 'Accepted Types',
      'Loads', 'CY Received', 'Status',
    ];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = sites.map(s => {
      let acceptedTypes = '';
      try {
        const parsed = JSON.parse(s.acceptedTypes || '[]');
        if (Array.isArray(parsed)) acceptedTypes = parsed.join('; ');
      } catch { /* ignore */ }
      const depAuth = (s.notes || '').toLowerCase().includes('dep') ? 'DEP Authorized' : '';
      return [
        s.siteName || '', (s.siteType || '').toUpperCase(), depAuth,
        s.address || '', acceptedTypes,
        String(s.ticketCount), String(s.receivedCY),
        (s.status || ''),
      ].map(v => csvEscape(String(v || '')));
    });
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  // ── Download helper (trigger browser download of a string as file) ──
  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ── Generate force account CSVs ───────────────────────────
  const generateLaborCSV = (labor: FALaborRecord[]): string => {
    const headers = ['Employee', 'Classification', 'Date', 'Regular Hours', 'OT Hours', 'Hourly Rate', 'Total Cost'];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = labor.map(l => [
      l.employeeName, l.classification, l.dateWorked,
      l.hoursRegular.toFixed(1), l.hoursOvertime.toFixed(1),
      l.hourlyRate.toFixed(2), l.totalCost.toFixed(2),
    ].map(v => csvEscape(v)));
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  const generateEquipmentCSV = (equip: FAEquipmentRecord[]): string => {
    const headers = ['Fleet Number', 'Type', 'Date', 'Hours Used', 'Hourly Rate', 'Total Cost'];
    const csvEscape = (v: string) => {
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return `"${v.replace(/"/g, '""')}"`;
      return v;
    };
    const rows = equip.map(e => [
      e.fleetNumber, e.equipmentType, e.dateUsed,
      e.hoursUsed.toFixed(1), e.hourlyRate.toFixed(2), e.totalCost.toFixed(2),
    ].map(v => csvEscape(v)));
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  // ── Export ZIP Bundle ─────────────────────────────────────
  const exportZipBundle = async (pkg: FEMAPackage) => {
    setExporting(pkg.id);
    try {
      await ensureFACache();
      const detail = getPackageDetail(pkg);
      const zip = new JSZip();

      const pkgSlug = (pkg.pwReference || pkg.packageName).replace(/[^a-zA-Z0-9]/g, '');
      const disasterSlug = (pkg.femaDisasterNumber || 'DR').replace(/[^a-zA-Z0-9]/g, '');

      // 0. Cover page text file (COVER_PAGE.txt)
      {
        const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const coverLines: string[] = [
          'FEMA COST RECOVERY PACKAGE',
          '='.repeat(60),
          '',
          `Package Name:      ${pkg.packageName}`,
          `Disaster Number:   ${pkg.femaDisasterNumber || 'TBD'}`,
          `PW Reference:      ${pkg.pwReference || 'TBD'}`,
          `Date Range:        ${pkg.dateRangeStart || 'TBD'} \u2013 ${pkg.dateRangeEnd || 'TBD'}`,
          `Generated:         ${today}`,
          `Total Cost Claimed: $${(pkg.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
          '',
          '='.repeat(60),
          'TABLE OF CONTENTS',
          '='.repeat(60),
          '',
          '00  COVER PAGE & SUMMARY             (COVER_PAGE.txt)',
          '01  TICKET DATA                       (01_Ticket_Data/)',
          '01b LOAD TICKET PDFs                  (01_Load_Ticket_PDFs/)',
          '02  INVOICE DOCUMENTS                 (02_Invoices/)',
          '03  RECONCILIATION DOCUMENTS          (03_Reconciliation/)',
          '04  TRUCK CERTIFICATIONS              (truck-certs/)',
          '05  SITE DOCUMENTATION                (05_Site_Documentation/)',
          '06  FORCE ACCOUNT                     (06_Force_Account/)',
          '     · Labor summary                  (06_Force_Account/labor_summary.csv)',
          '     · Equipment summary              (06_Force_Account/equipment_summary.csv)',
          '07  DOCUMENT COMPLETENESS REPORT      (completeness-report.csv)',
          '',
          '='.repeat(60),
          'COST SUMMARY BY FEMA CATEGORY',
          '='.repeat(60),
          '',
        ];

        // Contractor ticket breakdown (Category A)
        if (pkg.contractorIds.length > 0) {
          coverLines.push('CATEGORY A (Contractor Debris Removal):');
          pkg.contractorIds.forEach((cid: string) => {
            const cName = contractors.find(c => String(c.id) === cid)?.companyName || cid;
            const cTickets = detail.matchedTickets.filter(t => String(t.contractorId) === cid);
            const totalCost = cTickets.reduce((s, t) => s + (parseFloat(t.extendedCost || '0')), 0);
            coverLines.push(`  ${cName}: ${cTickets.length} tickets, $${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
          });
          coverLines.push('');
        }

        // Force Account breakdown (Category Z)
        if (detail.faLabor.length > 0 || detail.faEquip.length > 0) {
          coverLines.push('CATEGORY Z (Force Account):');
          coverLines.push(`  Labor:     ${detail.faLabor.length} records, $${detail.faLaborTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
          coverLines.push(`  Equipment: ${detail.faEquip.length} records, $${detail.faEquipTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
          coverLines.push('');
        }

        zip.file('COVER_PAGE.txt', coverLines.join('\n'));
      }

      // 1. Cover summary PDF
      notifications.show({ message: 'Generating cover summary...', color: '#3b82f6' });
      const coverPdfBytes = generateCoverPDF(pkg, detail);
      zip.file('00_Cover_Summary.pdf', coverPdfBytes);

      // 2. Ticket data CSV
      if (detail.matchedTickets.length > 0) {
        const ticketFolder = zip.folder('01_Ticket_Data');
        const csv = generateTicketCSV(detail.matchedTickets);
        ticketFolder!.file(`ticket_data_${detail.totalTickets}_records.csv`, csv);
      }

      // 2b. Load ticket source PDFs
      {
        const loadTicketDocs = (docCache || []).filter(d =>
          d.documentType === 'load_tickets' || d.documentType === 'ticket_image'
        );
        if (loadTicketDocs.length > 0) {
          const ltFolder = zip.folder('01_Load_Ticket_PDFs');
          notifications.show({ message: `Fetching ${loadTicketDocs.length} load ticket PDF(s)...`, color: '#3b82f6' });
          for (const ltDoc of loadTicketDocs) {
            const bytes = await fetchDocBytes(ltDoc);
            if (bytes) {
              const ext = bytes.name.includes('.') ? '' : '.pdf';
              ltFolder!.file(bytes.name + ext, bytes.data);
            }
          }
        }
      }

      // 3. Invoice documents from Evidence Archive
      const invoiceDocs = (docCache || []).filter(d =>
        d.documentType === 'invoice' || d.documentType === 'invoice_backup'
      );
      if (invoiceDocs.length > 0) {
        const invoiceFolder = zip.folder('02_Invoices');
        notifications.show({ message: `Fetching ${invoiceDocs.length} invoice document(s)...`, color: '#3b82f6' });
        for (const invDoc of invoiceDocs) {
          const bytes = await fetchDocBytes(invDoc);
          if (bytes) {
            const ext = bytes.name.includes('.') ? '' : '.pdf';
            invoiceFolder!.file(bytes.name + ext, bytes.data);
          }
        }
      }

      // 4. Reconciliation documents from Evidence Archive
      const reconDocs = (docCache || []).filter(d =>
        d.documentType === 'reconciliation' || d.documentType === 'recon_report'
      );
      if (reconDocs.length > 0) {
        const reconFolder = zip.folder('03_Reconciliation');
        notifications.show({ message: `Fetching ${reconDocs.length} reconciliation document(s)...`, color: '#3b82f6' });
        for (const reconDoc of reconDocs) {
          const bytes = await fetchDocBytes(reconDoc);
          if (bytes) {
            const ext = bytes.name.includes('.') ? '' : '.pdf';
            reconFolder!.file(bytes.name + ext, bytes.data);
          }
        }
      }

      // 5. Truck certifications — fetch individual cert PDFs
      if (detail.trucks.length > 0) {
        const certFolder = zip.folder('truck-certs');
        const manifest = generateTruckManifestCSV(detail.trucks);
        certFolder!.file('truck-manifest.csv', manifest);

        // Get unique truck numbers from matched tickets
        const usedTruckNums = new Set<string>();
        detail.matchedTickets.forEach(t => { if (t.truckNumber) usedTruckNums.add(t.truckNumber); });

        // Get cert document IDs from truck TRMETA + fallback to document search
        const certsToFetch: { truckNum: string; docId: string }[] = [];
        const TRMETA = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
        const certDocs = (docCache || []).filter(d =>
          d.documentType === 'truck_certification' || d.documentType === 'truck_cert'
        );
        detail.trucks.forEach(t => {
          if (!usedTruckNums.has(t.truckNumber)) return;
          // Source 1: TRMETA documentId
          const mm = t.notes.match(TRMETA);
          if (mm) {
            try {
              const meta = JSON.parse(mm[1]);
              if (meta.documentId) { certsToFetch.push({ truckNum: t.truckNumber, docId: meta.documentId }); return; }
            } catch {}
          }
          // Source 2: search documents by type + truck number
          const matchDoc = certDocs.find(d => {
            const title = (d.title || '').toLowerCase();
            const notes = (d.notes || '').toLowerCase();
            return title.includes(t.truckNumber.toLowerCase()) || notes.includes(t.truckNumber.toLowerCase());
          });
          if (matchDoc) {
            const docIdMatch = (matchDoc.notes || '').match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
            if (docIdMatch) certsToFetch.push({ truckNum: t.truckNumber, docId: docIdMatch[1] });
          }
        });

        if (certsToFetch.length > 0) {
          setExportStatus(`Downloading ${certsToFetch.length} truck certification PDFs...`);
          let fetched = 0, failed = 0;
          // Batch downloads 5 at a time to avoid overwhelming the browser
          for (let i = 0; i < certsToFetch.length; i += 5) {
            const batch = certsToFetch.slice(i, i + 5);
            const results = await Promise.all(batch.map(async (cert) => {
              try {
                const bytes = await fetchDocumentPDF(cert.docId);
                if (bytes) {
                  certFolder!.file(`Truck-${cert.truckNum}.pdf`, bytes);
                  fetched++;
                  return true;
                }
                failed++;
                return false;
              } catch { failed++; return false; }
            }));
            setExportStatus(`Downloading truck certs: ${fetched + failed} of ${certsToFetch.length} (${fetched} OK, ${failed} failed)`);
          }
          console.log(`[FEMA] Truck certs: ${fetched} fetched, ${failed} failed out of ${certsToFetch.length}`);
        }
      }

      // 6. Site documentation
      const siteDocs = (docCache || []).filter(d =>
        d.documentType === 'site_authorization' || d.documentType === 'dep_authorization' ||
        d.documentType === 'ddms' || d.documentType === 'site_permit'
      );
      if (siteDocs.length > 0 || detail.sites.length > 0) {
        const siteFolder = zip.folder('05_Site_Documentation');
        // Fetch actual site docs
        for (const siteDoc of siteDocs) {
          const bytes = await fetchDocBytes(siteDoc);
          if (bytes) {
            const ext = bytes.name.includes('.') ? '' : '.pdf';
            siteFolder!.file(bytes.name + ext, bytes.data);
          }
        }
        // Site summary as CSV
        const siteHeaders = ['Site Name', 'Type', 'Tickets Received', 'CY Received'];
        const csvEscape = (v: string) => v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
        const siteRows = detail.sites.map(s => [
          csvEscape(s.siteName || ''), csvEscape(s.siteType || ''),
          String(s.ticketCount), String(s.receivedCY),
        ]);
        siteFolder!.file('disposal_site_summary.csv', [siteHeaders.join(','), ...siteRows.map(r => r.join(','))].join('\n'));
      }

      // 7. Force account data
      const pkgLabor = faLaborCache.filter(l => {
        if (pkg.dateRangeStart && l.dateWorked && l.dateWorked < pkg.dateRangeStart) return false;
        if (pkg.dateRangeEnd && l.dateWorked && l.dateWorked > pkg.dateRangeEnd) return false;
        return true;
      });
      const pkgEquip = faEquipCache.filter(e => {
        if (pkg.dateRangeStart && e.dateUsed && e.dateUsed < pkg.dateRangeStart) return false;
        if (pkg.dateRangeEnd && e.dateUsed && e.dateUsed > pkg.dateRangeEnd) return false;
        return true;
      });
      if (pkgLabor.length > 0 || pkgEquip.length > 0) {
        const faFolder = zip.folder('06_Force_Account');
        if (pkgLabor.length > 0) faFolder!.file('labor_summary.csv', generateLaborCSV(pkgLabor));
        if (pkgEquip.length > 0) faFolder!.file('equipment_summary.csv', generateEquipmentCSV(pkgEquip));
      }

      // Build completeness report CSV
      {
        const csvEsc = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;
        const csvRows: string[] = [
          'Cost Item Type,ID/Number,Contractor,Amount,Haul Ticket Doc,Weigh Ticket Doc,Monitor Log Doc,Sub Invoice Doc,Notes',
        ];

        // Tickets
        detail.matchedTickets.forEach(t => {
          const docs = decodeTicketDocs(t.notes || '');
          const cName = contractors.find(c => String(c.id) === String(t.contractorId))?.companyName || t.contractorId || '-';
          const cost = parseFloat(t.extendedCost || '0');
          csvRows.push([
            'Ticket',
            csvEsc(t.ticketNumber || String(t.id)),
            csvEsc(cName),
            cost > 0 ? cost.toFixed(2) : '0.00',
            docs.haulTicketDocId ? 'Y' : 'N',
            docs.weighTicketDocId ? 'Y' : 'N',
            docs.monitoringLogDocId ? 'Y' : 'N',
            '-',
            csvEsc(t.debrisType || ''),
          ].join(','));
        });

        // FA Labor rows
        detail.faLabor.forEach(r => {
          const docs = decodeFaDocs(r.notes || '');
          csvRows.push([
            'FA Labor',
            csvEsc(r.employeeName || String((r as any).id)),
            csvEsc('City of Tampa'),
            r.totalCost > 0 ? r.totalCost.toFixed(2) : '0.00',
            docs.timesheetDocId ? 'Y' : 'N',
            docs.payrollDocId ? 'Y' : 'N',
            '-',
            '-',
            csvEsc(r.classification || ''),
          ].join(','));
        });

        // FA Equipment rows
        detail.faEquip.forEach(r => {
          const docs = decodeFaDocs(r.notes || '');
          csvRows.push([
            'FA Equipment',
            csvEsc(r.fleetNumber || String((r as any).id)),
            csvEsc('City of Tampa'),
            r.totalCost > 0 ? r.totalCost.toFixed(2) : '0.00',
            '-',
            '-',
            '-',
            docs.femaRateDocId ? 'Y' : 'N',
            csvEsc(r.equipmentType || ''),
          ].join(','));
        });

        // Sub-invoices for contractors in this package
        subInvoices
          .filter(s => pkg.contractorIds.some(cid => String(s.primeContractorId) === String(cid)))
          .forEach(s => {
            csvRows.push([
              'Sub Invoice',
              csvEsc(s.subInvoiceNumber || String(s.id)),
              csvEsc(s.subName),
              s.subAmount > 0 ? s.subAmount.toFixed(2) : '0.00',
              '-',
              '-',
              '-',
              s.documentId ? 'Y' : 'N',
              csvEsc(s.workType || ''),
            ].join(','));
          });

        zip.file('completeness-report.csv', csvRows.join('\n'));
      }

      // Generate the interactive HTML audit index
      try {
        notifications.show({ message: 'Building audit index page...', color: '#3b82f6' });
        // Call generateFEMAPackage's HTML builder inline — reuse the same data
        const htmlBlob = await (async () => {
          // Trigger generateFEMAPackage but capture the HTML instead of downloading
          // For now, build a simple index linking to the ZIP contents
          const indexHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>FEMA Audit Package - ${pkg.packageName}</title>
<style>body{font-family:sans-serif;max-width:900px;margin:40px auto;padding:20px;color:#1e293b}
h1{color:#4B3FFF}table{width:100%;border-collapse:collapse;margin:20px 0}
th,td{padding:8px 12px;border-bottom:1px solid #e2e8f0;text-align:left}
th{background:#f8fafc;font-size:12px;color:#64748b}.mono{font-family:monospace}</style></head>
<body><h1>FEMA Cost Recovery Package — ${esc(pkg.packageName)}</h1>
<p>Disaster: ${esc(pkg.femaDisasterNumber || 'TBD')} | PW: ${esc(pkg.pwReference || '-')} | Period: ${pkg.dateRangeStart?.slice(0,10) || '-'} — ${pkg.dateRangeEnd?.slice(0,10) || '-'}</p>
<p><strong>Total Claimed: $${(pkg.totalCost || 0).toLocaleString(undefined, {minimumFractionDigits:2})}</strong></p>
<h2>Package Contents</h2>
<table><tr><th>Folder</th><th>Contents</th></tr>
<tr><td>00_Cover_Summary.pdf</td><td>Executive summary</td></tr>
<tr><td>01_Ticket_Data/</td><td>Load ticket data CSV</td></tr>
<tr><td>01_Load_Ticket_PDFs/</td><td>Original load ticket source PDFs</td></tr>
<tr><td>02_Invoices/</td><td>Contractor invoice documents</td></tr>
<tr><td>03_Reconciliation/</td><td>Reconciliation reports</td></tr>
<tr><td>truck-certs/</td><td>Truck certification PDFs</td></tr>
<tr><td>05_Site_Documentation/</td><td>Disposal site documentation</td></tr>
<tr><td>06_Force_Account/</td><td>Force account labor & equipment CSVs</td></tr>
<tr><td>completeness-report.csv</td><td>Per-ticket document completeness</td></tr>
</table>
<p style="color:#94a3b8;font-size:12px;margin-top:40px">OpenGov Emergency Management Console — Tampa Cost Recovery | Generated ${new Date().toLocaleDateString()}</p>
</body></html>`;
          return indexHtml;
        })();
        zip.file('index.html', htmlBlob);
      } catch { /* non-critical */ }

      // Generate the ZIP
      notifications.show({ message: 'Assembling ZIP bundle...', color: '#3b82f6' });
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEMA-Package-${pkgSlug}-${disasterSlug}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notifications.show({ message: 'ZIP bundle exported successfully', color: 'var(--color-success)' });

      // Update exportedAt
      updateFemaPackage(pkg.id, { exportedAt: new Date().toISOString() }).catch(() => {});
    } catch (err) {
      console.error('ZIP export failed:', err);
      notifications.show({ message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    } finally {
      setExporting(null);
    }
  };

  // ── Legacy PDF-only export ────────────────────────────────
  const generatePDF = async (pkg: FEMAPackage) => {
    try {
      await ensureFACache();
      const detail = getPackageDetail(pkg);
      const pdfBytes = generateCoverPDF(pkg, detail);

      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEMA-Package-${pkg.packageName.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notifications.show({ message: 'PDF exported successfully', color: 'var(--color-success)' });
      updateFemaPackage(pkg.id, { exportedAt: new Date().toISOString() }).catch(() => {});
    } catch (err) {
      console.error('PDF generation failed:', err);
      notifications.show({ message: `PDF generation failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    }
  };

  // ── Generate complete FEMA ZIP Package ─────────────────────
  const generateFEMAPackage = async (pkg: FEMAPackage) => {
    setHtmlExporting(true);
    setExportStatus('Loading data...');
    try {
      await ensureFACache();
      const detail = getPackageDetail(pkg);
      const totalCY = detail.matchedTickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0);
      const contractorNames = pkg.contractorIds.map(id => contractorName(id)).join(', ') || '-';
      const sanitize = (s: string) => (s || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');

      const zip = new JSZip();

      // ── 1. Narrative PDF ──────────────────────────────────
      setExportStatus('Generating narrative PDF...');
      const pdfBytes = generateCoverPDF(pkg, detail);
      zip.file('narrative/PW-Backup-Summary.pdf', pdfBytes);

      // ── 2. CSV Data Files ─────────────────────────────────
      setExportStatus('Generating CSV data files...');
      zip.file('data/tickets.csv', generateTicketCSV(detail.matchedTickets));
      zip.file('data/truck-manifest.csv', generateTruckManifestCSV(detail.trucks));
      zip.file('data/reconciliation.csv', generateReconCSV(detail.reconciliations));
      zip.file('data/disposal-sites.csv', generateSiteSummaryCSV(detail.sites));

      // ── 3. Source Documents from Evidence Archive ──────────
      // Helper to fetch a PDF from GAB storage
      // Fetch document PDF — uses proxy which falls back to admin token
      const fetchDocumentPDF = async (fileId: string): Promise<ArrayBuffer | null> => {
        if (!fileId) return null;
        try {
          // Don't send credentials — let proxy use admin token fallback (more reliable)
          const dlRes = await fetch(`/api/v2proxy/v2/apps/8q39q8q5n/documents/${fileId}/download-url`);
          if (!dlRes.ok) { console.warn(`[FEMA] Doc ${fileId.slice(0, 12)}... download-url: ${dlRes.status}`); return null; }
          const dlData = await dlRes.json();
          const presignedUrl = dlData.presignedUrl ?? dlData.url;
          if (!presignedUrl) return null;
          const proxyUrl = `/api/filepreview?url=${encodeURIComponent(presignedUrl)}`;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          const res = await fetch(proxyUrl, { signal: controller.signal });
          clearTimeout(timeout);
          if (!res.ok) return null;
          return await res.arrayBuffer();
        } catch (e) { console.warn(`[FEMA] Doc fetch failed:`, e); return null; }
      };

      // Track what we successfully fetched for the HTML index
      const fetchedDocs: { title: string; type: string; path: string; hasFile: boolean }[] = [];

      // Invoices
      const invoiceDocs = (docCache || []).filter(d =>
        d.documentType === 'invoice' || d.documentType === 'invoice_backup'
      );
      if (invoiceDocs.length > 0) {
        setExportStatus(`Fetching ${invoiceDocs.length} invoice document(s)...`);
        for (const doc of invoiceDocs) {
          if (doc.fileId) {
            const bytes = await fetchDocumentPDF(doc.fileId);
            const fname = sanitize(doc.title) + (doc.title.includes('.') ? '' : '.pdf');
            if (bytes) {
              zip.file(`invoices/${fname}`, bytes);
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: `invoices/${fname}`, hasFile: true });
            } else {
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
            }
          } else {
            fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
          }
        }
      }

      // Reconciliation
      const reconDocs = (docCache || []).filter(d =>
        d.documentType === 'reconciliation' || d.documentType === 'recon_report'
      );
      if (reconDocs.length > 0) {
        setExportStatus(`Fetching ${reconDocs.length} reconciliation document(s)...`);
        for (const doc of reconDocs) {
          if (doc.fileId) {
            const bytes = await fetchDocumentPDF(doc.fileId);
            const fname = sanitize(doc.title) + (doc.title.includes('.') ? '' : '.pdf');
            if (bytes) {
              zip.file(`reconciliation/${fname}`, bytes);
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: `reconciliation/${fname}`, hasFile: true });
            } else {
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
            }
          } else {
            fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
          }
        }
      }

      // Ticket images (large — fetch individually, don't merge)
      const ticketImageDocs = (docCache || []).filter(d =>
        d.documentType === 'ticket_image' || d.documentType === 'tdms_scan'
      );
      // Load PDF.js once for HTML generation
      let pdfjsForHtml: any = null;
      try {
        pdfjsForHtml = await import('pdfjs-dist').then(m => m.default ?? m);
        if (!pdfjsForHtml.GlobalWorkerOptions.workerSrc) {
          pdfjsForHtml.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url,
          ).href;
        }
      } catch (e) { console.warn('[Export] PDF.js load failed, skipping HTML generation', e); }

      if (ticketImageDocs.length > 0) {
        setExportStatus(`Fetching ${ticketImageDocs.length} ticket image(s) (this may take a moment)...`);
        for (const doc of ticketImageDocs) {
          if (doc.fileId) {
            const bytes = await fetchDocumentPDF(doc.fileId);
            const fname = sanitize(doc.title) + (doc.title.toLowerCase().endsWith('.pdf') ? '' : '.pdf');
            if (bytes) {
              zip.file(`ticket-images/${fname}`, bytes);
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: `ticket-images/${fname}`, hasFile: true });

              // Generate HTML viewer alongside original PDF
              if (pdfjsForHtml) {
                try {
                  setExportStatus(`Generating ticket viewer for ${doc.title}...`);
                  const pdfDoc = await pdfjsForHtml.getDocument({ data: new Uint8Array(bytes.slice(0)) }).promise;
                  const TN_RE = /\d{4,8}-\d{3,7}-\d{4,8}/g;
                  const esc2 = (s: string) => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
                  const sections: string[] = [];

                  for (let p = 1; p <= pdfDoc.numPages; p++) {
                    const page = await pdfDoc.getPage(p);
                    const tc = await page.getTextContent();
                    const raw = tc.items.map((i: any) => i.str).join(' ');
                    const tnMatches = raw.match(TN_RE);
                    if (!tnMatches) continue;
                    const tn = [...new Set(tnMatches)][0];

                    // Format: insert newlines before known field labels for readability
                    const formatted = raw
                      .replace(/\s*(Contractor:|Subcontractor:|Project No:|Project:|Driver:|Truck No:|Truck Capacity:|LOADING|DROP OFF|Date:|Latitude:|Longitude:|APN-PPDR:|Address:|Reference #:|Material:|Monitor:|ADMS Device:|Dumpsite:|Load Call:|Load CYD:|Gross Tonnage:|Tare Tonnage:|Net Tonnage:|Mileage:)/g, '\n$1')
                      .trim();

                    sections.push(
                      `<section id="tn-${esc2(tn)}">\n` +
                      `<div class="tn">DEBRIS LOAD TICKET &nbsp;<span class="tn-num">${esc2(tn)}</span></div>\n` +
                      `<pre class="fields">${esc2(formatted)}</pre>\n` +
                      `<div class="src"><a href="${esc2(fname)}" target="_blank" class="pl">&#x1F4C4; View original PDF</a></div>\n` +
                      `</section>`
                    );
                  }

                  const htmlFname = fname.replace(/\.pdf$/i, '.html');
                  const htmlOut =
                    `<!DOCTYPE html>\n<html lang="en"><head>\n` +
                    `<meta charset="utf-8">\n` +
                    `<title>${esc2(doc.title)} — Ticket Index</title>\n` +
                    `<style>\n` +
                    `body{font-family:system-ui,sans-serif;max-width:760px;margin:0 auto;padding:20px;background:#f8fafc;color:#1e293b}\n` +
                    `h1{font-size:15px;font-weight:700;margin-bottom:2px}\n` +
                    `.meta{font-size:12px;color:#94a3b8;margin-bottom:20px}\n` +
                    `section{background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:16px;margin-bottom:14px}\n` +
                    `.tn{font-size:13px;font-weight:600;color:#4B3FFF;margin-bottom:8px;letter-spacing:.3px}\n` +
                    `.tn-num{font-size:15px;font-weight:800}\n` +
                    `pre.fields{font-size:12px;white-space:pre-wrap;word-break:break-word;color:#334155;margin:0 0 10px;font-family:monospace}\n` +
                    `.src{font-size:11px}\n` +
                    `.pl{color:#4B3FFF;text-decoration:none}\n` +
                    `@media print{section{page-break-inside:avoid}}\n` +
                    `</style></head><body>\n` +
                    `<h1>${esc2(doc.title)}</h1>\n` +
                    `<div class="meta">${sections.length} tickets &mdash; original PDF included in this package as <a href="${esc2(fname)}" class="pl">${esc2(fname)}</a></div>\n` +
                    sections.join('\n') +
                    `\n</body></html>`;

                  zip.file(`ticket-images/${htmlFname}`, htmlOut);
                } catch (htmlErr) {
                  console.warn('[Export] HTML gen failed for', doc.title, htmlErr);
                }
              }
            } else {
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
            }
          } else {
            fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
          }
        }
      }

      // Reference / regulatory docs
      const refDocs = (docCache || []).filter(d =>
        d.documentType === 'regulatory' || d.documentType === 'reference' ||
        d.documentType === 'site_authorization' || d.documentType === 'dep_authorization' ||
        d.documentType === 'ddms' || d.documentType === 'site_permit'
      );
      if (refDocs.length > 0) {
        setExportStatus(`Fetching ${refDocs.length} reference document(s)...`);
        for (const doc of refDocs) {
          if (doc.fileId) {
            const bytes = await fetchDocumentPDF(doc.fileId);
            const fname = sanitize(doc.title) + (doc.title.includes('.') ? '' : '.pdf');
            if (bytes) {
              zip.file(`reference/${fname}`, bytes);
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: `reference/${fname}`, hasFile: true });
            } else {
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
            }
          } else {
            fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
          }
        }
      }

      // Any remaining doc types not yet categorized
      const categorizedTypes = new Set([
        'invoice', 'invoice_backup', 'reconciliation', 'recon_report',
        'ticket_image', 'tdms_scan', 'regulatory', 'reference',
        'site_authorization', 'dep_authorization', 'ddms', 'site_permit',
      ]);
      // Skip truck_cert docs (handled by manifest), training, roster — too many to fetch individually
      const skipTypes = new Set(['truck_cert', 'training', 'roster', 'roster_data']);
      const otherDocs = (docCache || []).filter(d => !categorizedTypes.has(d.documentType) && !skipTypes.has(d.documentType));
      if (otherDocs.length > 0) {
        setExportStatus(`Fetching ${otherDocs.length} other document(s)...`);
        for (const doc of otherDocs.slice(0, 10)) { // Cap at 10 to avoid hanging
          if (doc.fileId) {
            const bytes = await fetchDocumentPDF(doc.fileId);
            const fname = sanitize(doc.title) + (doc.title.includes('.') ? '' : '.pdf');
            if (bytes) {
              zip.file(`reference/${fname}`, bytes);
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: `reference/${fname}`, hasFile: true });
            } else {
              fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
            }
          } else {
            fetchedDocs.push({ title: doc.title, type: doc.documentType, path: '', hasFile: false });
          }
        }
      }
      // Note: truck cert manifest CSV is in truck-certs/ folder but not linked as a document — it's a supporting file

      // ── 4. Truck Cert PDFs ────────────────────────────────────
      setExportStatus('Building truck certification manifest...');
      zip.file('truck-certs/truck-cert-manifest.csv', generateTruckManifestCSV(detail.trucks));

      // Download individual cert PDFs for trucks that hauled in this package
      if (detail.trucks.length > 0) {
        const usedTruckNums = new Set<string>();
        detail.matchedTickets.forEach(t => { if (t.truckNumber) usedTruckNums.add(t.truckNumber); });
        const FAMETA_RE = /\|\|\|TRMETA\|\|\|([\s\S]*?)\|\|\|ENDTRMETA\|\|\|/;
        const DOCID_RE = /\|\|\|DOCID\|\|\|([^|]+)\|\|\|/;
        const certsToFetch: { truckNum: string; docId: string }[] = [];
        detail.trucks.forEach(t => {
          if (!usedTruckNums.has(t.truckNumber)) return;
          // Try DOCID marker first, then TRMETA JSON
          const docIdMatch = t.notes.match(DOCID_RE);
          if (docIdMatch) {
            certsToFetch.push({ truckNum: t.truckNumber, docId: docIdMatch[1].trim() });
            return;
          }
          const mm = t.notes.match(FAMETA_RE);
          if (mm) {
            try {
              const meta = JSON.parse(mm[1]);
              if (meta.documentId) certsToFetch.push({ truckNum: t.truckNumber, docId: meta.documentId });
            } catch {}
          }
        });

        if (certsToFetch.length > 0) {
          setExportStatus(`Downloading ${certsToFetch.length} truck certification PDFs...`);
          let fetched = 0, failed = 0;
          for (let i = 0; i < certsToFetch.length; i += 5) {
            const batchSlice = certsToFetch.slice(i, i + 5);
            await Promise.all(batchSlice.map(async (cert) => {
              const bytes = await fetchDocumentPDF(cert.docId);
              if (bytes) { zip.file(`truck-certs/Truck-${cert.truckNum}.pdf`, bytes); fetched++; }
              else failed++;
            }));
            setExportStatus(`Downloading truck certs: ${fetched + failed}/${certsToFetch.length} (${fetched} OK, ${failed} failed)`);
          }
          console.log(`[FEMA] Truck certs: ${fetched} OK, ${failed} failed of ${certsToFetch.length}`);
        }
      }

      // ── 5. Build index.html (interactive audit guide) ─────
      setExportStatus('Building interactive audit guide...');

      // Build truck lookup by truck number
      const truckLookup: Record<string, TruckRecord & { meta: Record<string, string> }> = {};
      detail.trucks.forEach(t => {
        truckLookup[t.truckNumber] = { ...t, meta: decodeTruckMeta(t.notes) };
      });

      // Build site lookup by id
      const siteLookup: Record<string, typeof detail.sites[0]> = {};
      detail.sites.forEach(s => { siteLookup[String(s.id)] = s; });

      // Prepare ticket data for embedding (strip to essential fields)
      const ticketsJSON = detail.matchedTickets.map(t => ({
        id: t.id,
        tn: t.ticketNumber,
        dt: t.debrisType,
        cy: t.calculatedCubicYards,
        clin: t.clinCode,
        clinDesc: t.clinDescription,
        rate: t.transactionRate,
        cost: t.extendedCost,
        sub: t.subcontractorName,
        truck: t.truckNumber,
        date: t.loadDate,
        site: t.disposalSiteName,
        siteId: t.disposalSiteId,
        addr: t.pickupAddress,
        zone: t.zoneId,
        status: t.status,
        lat: t.pickupLat,
        lng: t.pickupLng,
      }));

      const trucksJSON = detail.trucks.map(t => {
        const meta = decodeTruckMeta(t.notes);
        return {
          id: t.id,
          num: t.truckNumber,
          cap: t.capacityCY,
          plate: t.licensePlate,
          state: meta.tagState || meta.state || (t.licensePlate ? 'FL' : ''),
          type: t.truckType,
          sub: meta.subcontractorName || meta.subcontractor || contractorName(t.contractorId),
          driver: t.driverName,
          driverPhone: t.driverPhone,
          certDate: t.certifiedDate,
          certExpires: t.certExpiresAt,
          certBy: meta.certifiedByName || meta.certifiedBy || '',
          certStatus: t.certStatus,
          placard: t.placardNumber,
          tailgate: meta.hasTailgate || '',
        };
      });

      const sitesJSON = detail.sites.map(s => {
        let accepted: string[] = [];
        try { const p = JSON.parse(s.acceptedTypes || '[]'); if (Array.isArray(p)) accepted = p; } catch { /* */ }
        const depAuth = (s.notes || '').toLowerCase().includes('dep');
        return {
          id: s.id,
          name: s.siteName,
          type: s.siteType,
          addr: s.address || '',
          depAuth,
          accepted,
          loads: s.ticketCount,
          cy: s.receivedCY,
          status: s.status || '',
          cap: s.capacityCubicYards || '',
          hours: s.operatingHours || '',
          monitor: s.monitorName || '',
        };
      });

      const reconcJSON = detail.reconciliations.map(rec => ({
        id: rec.id,
        contractor: contractorName(rec.contractorId),
        periodStart: rec.periodStart,
        periodEnd: rec.periodEnd,
        invoiced: rec.totalInvoiced,
        verified: rec.totalVerified,
        variancePct: rec.variancePercent,
      }));

      const invoicesJSON = detail.invoices.map(inv => ({
        id: inv.id,
        num: inv.invoiceNumber,
        contractor: contractorName(inv.contractorId),
        periodStart: inv.periodStart,
        periodEnd: inv.periodEnd,
        amount: inv.totalAmount,
        status: inv.status,
      }));

      const docsJSON = fetchedDocs.length > 0 ? fetchedDocs.map(d => ({
        type: d.type,
        title: d.title,
        path: d.path || null,
        hasFile: d.hasFile,
      })) : docCache.map(d => ({
        type: d.documentType,
        title: d.title,
        path: null as string | null,
        hasFile: !!d.fileId,
      }));

      // GPS points for map
      const debrisColors: Record<string, string> = { vegetative: '#08A847', c_and_d: '#C68700', mixed: '#546574', soil_mud_sand: '#CD4F09' };
      const mapPoints = detail.matchedTickets
        .filter(t => t.pickupLat && t.pickupLng && !isNaN(parseFloat(t.pickupLat)) && !isNaN(parseFloat(t.pickupLng)))
        .map(t => ({
          lat: parseFloat(t.pickupLat),
          lng: parseFloat(t.pickupLng),
          tn: t.ticketNumber,
          dt: t.debrisType,
          cy: t.calculatedCubicYards,
          sub: t.subcontractorName,
          site: t.disposalSiteName,
          color: debrisColors[t.debrisType] || '#546574',
        }));

      // Disposal sites with known coordinates
      const siteCoords = [
        { name: 'McKay Bay Refuse-to-Energy', lat: 27.9523, lng: -82.4185 },
        { name: 'River Tower Park', lat: 27.9760, lng: -82.4440 },
        { name: '22nd St Park', lat: 28.0180, lng: -82.4300 },
        { name: 'Tampa Brush Site #1 (Manhattan Ave)', lat: 27.8780, lng: -82.4920 },
        { name: 'Gadsden Park', lat: 27.8830, lng: -82.4730 },
        { name: 'Angelo Recycled Materials FDS', lat: 27.9500, lng: -82.4500 },
        { name: 'Cemex Plant DMS', lat: 27.9600, lng: -82.4100 },
        { name: 'AB Tampa PD Impound Lot DMS', lat: 27.9550, lng: -82.4350 },
      ];

      // Build ticketImagePaths from fetchedDocs
      const ticketImagePaths = fetchedDocs
        .filter(d => (d.type === 'ticket_image' || d.type === 'tdms_scan') && d.hasFile && d.path)
        .map(d => d.path);

      // Filter force account by package date range for HTML embedding
      const pkgLaborJSON = faLaborCache
        .filter(l => {
          if (pkg.dateRangeStart && l.dateWorked && l.dateWorked < pkg.dateRangeStart) return false;
          if (pkg.dateRangeEnd && l.dateWorked && l.dateWorked > pkg.dateRangeEnd) return false;
          return true;
        })
        .map(l => ({ name: l.employeeName, class: l.classification, date: l.dateWorked, hrs: l.hoursRegular, ot: l.hoursOvertime, rate: l.hourlyRate, cost: l.totalCost }));
      const pkgEquipJSON = faEquipCache
        .filter(e => {
          if (pkg.dateRangeStart && e.dateUsed && e.dateUsed < pkg.dateRangeStart) return false;
          if (pkg.dateRangeEnd && e.dateUsed && e.dateUsed > pkg.dateRangeEnd) return false;
          return true;
        })
        .map(e => ({ fleet: e.fleetNumber, type: e.equipmentType, date: e.dateUsed, hrs: e.hoursUsed, rate: e.hourlyRate, cost: e.totalCost }));

      // Load indexes if available (try local state first, then fetch from GAB)
      let tpiForExport = filteredTicketPageIndex;
      let tciForExport = truckCertIndex;
      if (!tpiForExport || !tciForExport) {
        setExportStatus('Loading document indexes...');
        const loaded = await loadIndexes();
        if (loaded.ticketPageIndex) tpiForExport = loaded.ticketPageIndex;
        if (loaded.truckCertIndex) tciForExport = loaded.truckCertIndex;
      }

      const DATA_OBJ = {
        package: {
          name: pkg.packageName,
          disaster: pkg.femaDisasterNumber || 'TBD',
          pw: pkg.pwReference || '-',
          periodStart: pkg.dateRangeStart,
          periodEnd: pkg.dateRangeEnd,
          contractors: contractorNames,
          status: pkg.status,
          version: pkg.version || 1,
        },
        totals: {
          tickets: detail.totalTickets,
          cy: Math.round(totalCY * 10) / 10,
          clinCost: Math.round(detail.clinTotalCost * 100) / 100,
          invoiceCost: Math.round(detail.invoiceTotalCost * 100) / 100,
          laborCost: Math.round(detail.faLaborTotal * 100) / 100,
          equipCost: Math.round(detail.faEquipTotal * 100) / 100,
          grandTotal: Math.round((detail.invoiceTotalCost + detail.faLaborTotal + detail.faEquipTotal) * 100) / 100,
          trucks: detail.truckCertCount,
          sites: detail.sites.length,
          reconVariance: Math.round(detail.reconVariance * 100) / 100,
          reconVariancePct: Math.round(detail.reconVariancePct * 10) / 10,
          docs: detail.totalDocs,
        },
        clinSummary: detail.clinSummary,
        tickets: ticketsJSON,
        trucks: trucksJSON,
        sites: sitesJSON,
        reconciliation: reconcJSON,
        invoices: invoicesJSON,
        documents: docsJSON,
        fetchedDocs: fetchedDocs.map(d => ({ title: d.title, type: d.type, path: d.path, hasFile: d.hasFile })),
        ticketImagePaths: ticketImagePaths,
        ticketPageIndex: tpiForExport || {},
        truckCertIndex: tciForExport || {},
        mapPoints: mapPoints,
        siteCoords: siteCoords,
        labor: pkgLaborJSON,
        equipment: pkgEquipJSON,
      };

      // Escape the JSON for embedding in a script tag
      const jsonStr = JSON.stringify(DATA_OBJ);

      const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>FEMA Audit Package - ${pkg.femaDisasterNumber || 'TBD'} - ${pkg.packageName}</title>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
<link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css" />
<link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css" />
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></${'script'}>
<script src="https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js"></${'script'}>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f5f5f5;color:#1e293b;font-size:14px;line-height:1.5}
.container{max-width:1200px;margin:0 auto;padding:20px}
.header{background:#fff;color:#1e293b;padding:24px;border-radius:12px;margin-bottom:20px;border:2px solid #e2e8f0;border-top:4px solid #4B3FFF}
.header-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.header-logo{height:96px;flex-shrink:0}
.header-title{flex:1;text-align:center;padding:0 16px}
.header h1{font-size:20px;font-weight:700;margin-bottom:4px;color:#4B3FFF}
.header p{opacity:0.85;font-size:13px}
.stat-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;margin-bottom:20px}
.stat-card{background:white;border:1px solid #e2e8f0;border-radius:8px;text-align:center;padding:16px}
.stat-value{font-size:20px;font-weight:800;color:#4B3FFF;word-break:break-word;overflow:hidden;text-overflow:ellipsis}
.stat-label{font-size:12px;color:#64748b;margin-top:2px}
.card{background:white;border:1px solid #e2e8f0;border-radius:8px;padding:16px;margin-bottom:12px}
.card h2{font-size:15px;font-weight:700;color:#4B3FFF;margin-bottom:12px;border-bottom:2px solid #e2e8f0;padding-bottom:8px}
.search{padding:10px 16px;border:2px solid #e2e8f0;border-radius:8px;width:100%;font-size:14px;margin-bottom:12px}
.search:focus{border-color:#4B3FFF;outline:none}
.filter-info{font-size:12px;color:#64748b;margin-bottom:8px}
table{width:100%;border-collapse:collapse;font-size:13px}
th{background:#f8fafc;padding:8px 12px;text-align:left;border-bottom:2px solid #e2e8f0;position:sticky;top:0;z-index:1;font-weight:600;color:#64748b;white-space:nowrap;cursor:pointer;user-select:none}
th:hover{color:#4B3FFF}
td{padding:8px 12px;border-bottom:1px solid #f1f5f9}
tr:hover{background:#f8fafc}
.expandable{cursor:pointer}
.expandable:hover{background:#f0f4ff}
.detail{display:none;background:#fafbfc;border:1px solid #e2e8f0;border-radius:8px;margin:4px 12px 12px;padding:16px}
.detail.open{display:block}
.detail-grid{display:grid;grid-template-columns:1fr;gap:12px}
.detail-section h3{font-size:13px;font-weight:700;color:#4B3FFF;margin-bottom:8px}
.detail-row{display:flex;gap:8px;font-size:12px;margin-bottom:4px}
.detail-row .label{color:#64748b;min-width:100px;font-weight:500}
.detail-row .value{color:#1e293b;font-weight:500}
.timeline{border-left:3px solid #4B3FFF;padding-left:20px;margin:12px 0}
.timeline-item{position:relative;margin-bottom:16px}
.timeline-item::before{content:'';width:12px;height:12px;border-radius:50%;background:#4B3FFF;position:absolute;left:-26px;top:4px}
.timeline-item .tl-title{font-weight:700;font-size:12px;color:#1e293b}
.timeline-item .tl-detail{font-size:11px;color:#64748b}
.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600}
.badge-ok{background:#ecfdf5;color:#037730}
.badge-warn{background:#fef3c7;color:#885604}
.badge-err{background:#fef2f2;color:#D33423}
.warn{color:#D33423;font-weight:600}
.ok{color:#037730}
.mono{font-family:'SF Mono',Consolas,'Liberation Mono',monospace;font-size:12px}
.text-right{text-align:right}
.text-center{text-align:center}
.fw-bold{font-weight:700}
.nav{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap}
.nav-btn{padding:8px 16px;border:2px solid #e2e8f0;border-radius:8px;background:white;cursor:pointer;font-size:13px;font-weight:600;color:#64748b;transition:all 0.15s}
.nav-btn:hover{border-color:#4B3FFF;color:#4B3FFF}
.nav-btn.active{background:#4B3FFF;color:white;border-color:#4B3FFF}
.scroll-table{overflow-x:auto;overflow-y:auto;max-height:70vh}
.total-row{border-top:2px solid #cbd5e1;background:#f0f4f8;font-weight:700}
.print-note{font-size:11px;color:#94a3b8;text-align:center;margin-top:20px;padding:12px}
.sort-active{color:#4B3FFF!important}
@media print{body{background:white}.container{max-width:100%;padding:10px}.header{border-radius:0}.scroll-table{max-height:none;overflow:visible}}
@media(max-width:768px){.detail-grid{grid-template-columns:1fr}.stat-row{grid-template-columns:repeat(2,1fr)}}
#scan-overlay{display:none;position:fixed;inset:0;background:rgba(10,10,20,0.93);z-index:9999;flex-direction:column}
#scan-overlay.open{display:flex}
#scan-toolbar{display:flex;align-items:center;gap:10px;padding:10px 16px;background:rgba(0,0,0,0.4);flex-shrink:0;flex-wrap:wrap}
#scan-toolbar .scan-info{flex:1;min-width:0}
#scan-toolbar .scan-title{color:white;font-weight:700;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
#scan-toolbar .scan-sub{color:rgba(255,255,255,0.6);font-size:11px;margin-top:1px}
.scan-btn{padding:7px 13px;border:none;border-radius:6px;cursor:pointer;font-size:12px;font-weight:600;white-space:nowrap}
.scan-btn-primary{background:#4B3FFF;color:white}
.scan-btn-secondary{background:rgba(255,255,255,0.15);color:white}
.scan-btn-close{background:rgba(220,50,50,0.8);color:white}
.scan-btn:disabled{opacity:0.4;cursor:default}
#scan-page-label{color:rgba(255,255,255,0.8);font-size:12px;white-space:nowrap}
#scan-canvas-wrap{flex:1;overflow:hidden;display:flex;flex-direction:column}
</style>
</head>
<body>
<div class="container">
<div id="app"></div>
</div>
<div id="scan-overlay">
  <div id="scan-toolbar">
    <div class="scan-info">
      <div class="scan-title" id="scan-title">Ticket Scan</div>
      <div class="scan-sub" id="scan-sub"></div>
    </div>
    <button class="scan-btn scan-btn-close" onclick="closeScan()">&#10005; Close</button>
  </div>
  <div id="scan-canvas-wrap"></div>
</div>
<script>
var DATA=${jsonStr};
var currentSection='tickets';
var sortCol='',sortDir='asc';
var _clickBound=false;

// ── Ticket scan viewer ────────────────────────────────────────
var _scanTn='';
function openScanFromBtn(btn){
  openScan(btn.getAttribute('data-pdf'),parseInt(btn.getAttribute('data-page'))||1,btn.getAttribute('data-tn')||'');
}
function openScan(path,page,tn){
  _scanTn=tn;
  document.getElementById('scan-title').textContent='Ticket #'+tn;
  document.getElementById('scan-sub').textContent=(path.split('/').pop()||path)+' \u2014 Page '+page;
  document.getElementById('scan-canvas-wrap').innerHTML='<embed src="'+path+'#page='+page+'" type="application/pdf" style="width:100%;height:100%;border:none;display:block">';
  document.getElementById('scan-overlay').classList.add('open');
}
function closeScan(){document.getElementById('scan-overlay').classList.remove('open');document.getElementById('scan-canvas-wrap').innerHTML='';}
function esc(s){if(s==null)return'';return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')}
function fmtMon(n){n=parseFloat(n)||0;return'$'+n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
function fmtM(n){n=parseFloat(n)||0;return'$'+Math.round(n).toLocaleString()}
function fmtCY(n){n=parseFloat(n)||0;return Math.round(n).toLocaleString()}
function fmtDate(d){if(!d)return'-';var s=String(d).slice(0,10);if(s.indexOf('-')>-1){var p=s.split('-');return p[1]+'/'+p[2]+'/'+p[0]}return s}

function render(){
  var app=document.getElementById('app');
  var P=DATA.package,T=DATA.totals;
  var h='';
  // Header
  h+='<div class="header">';
  h+='<div class="header-top">';
  h+='<img class="header-logo" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAACFCAYAAAAenrcsAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAyKADAAQAAAABAAAAhQAAAAA7OKFtAAABdGlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNi4wLjAiPgogICA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPgogICAgICA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIgogICAgICAgICAgICB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iPgogICAgICAgICA8eG1wOkNyZWF0b3JUb29sPkFkb2JlIFBob3Rvc2hvcCAyMy41IChNYWNpbnRvc2gpPC94bXA6Q3JlYXRvclRvb2w+CiAgICAgIDwvcmRmOkRlc2NyaXB0aW9uPgogICA8L3JkZjpSREY+CjwveDp4bXBtZXRhPgprfBE7AAAnW0lEQVR4Ae2dCXxU1dnw7z57JpmsZGEJYd8EwiKIbFYF6UtbC/WrCsWqoLYupVq37zPUvdpicWkRweV1qfjaT0WsArIIIkvYZCcQQiD7JDOZ9c7ce+e+z3MnM5mZZLKBtr/2HH6ZmXvvWf/nPOc85znnXCiKOEKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQoAQIAQIAUKAECAECAFCgBAgBAgBQuDfjQD9zyyQSlE0BR8RR8ddRe6Sb0Lgn0fgexUQVS1hlr0y1XjayVr8ftFMyS4zF6J1YkBV9TqaDgUVkUrN9JiMJs+MUb3dN1/9Vz9Nl4T+eXhIyv/pBL4XAfnT2p2GHYf8WQzlzwuqusF+SR0alJQBIUXJkRTVEgopFMuwFMeyTpajaw0CX2YxMCcZyXeC5U2VN47Pb5g9e0DgP72ySPm/fwLfqYCUlqr8cx9t7s1w3NgGt3hNszswxSMGCmWFYZWQSoVCODjE6FigcTEMA4JCUzqWko1G4YzFpP8yPZXdQMv0ofcem3aepmnl+8dEUvxPJfCdCcivV3yVWe+Qihua5UUNTt8snxgyK4oM8hCiYK6hORUEAh1eqy1yEpmGaNc0CAvHUWYD25Sdpv/EauDeKszRHXzmzikOLSD5IAS+YwLfiYAsfvR/BtSFrDdW1vsWN/vkHEUORoUCf8AooIkGfCkMRcsqDeMDpfIhVWVBMFiUFRUlpEVq8IvlBCrdyp/Nsxlf4gz0+x88OrPqO2ZDoicEWrrwSwRCXbuW/cVh/eBqn/DIhQbfDf5AEGQgMjQwFAPiyLCcUy/wTTpOcel5voFjVY8oKU6DjrMFJMUCE/aMgEKnBCUpHeYoFhCaVkGhWMqo54K5NuOrVp28/H+emXMWB59LlH2MiKZKtD/1UsZ7qfL3rxAPVIfWu10KPmoJNIkS6l/aCAPlvDQOwd37500jjlYpz56tcl2ryHJYdQKeDIgJx3ONFh1VaTELX/XOSdkzqCDl2JTxOVVTinL81L5XA9TY2/Vf7q437j1Tl3/krGN4VUPz5R4/PcnlCxZIipxKwZwFZQE/eZaj8jKNbxemiY+tKbm+/GJKACMVffeL2zMuNAXTGNFpgvzpvD5FDDE278AcfdNTd41vghEvqRDe9dLudI5nTZSYkAs9XIsiFaJD/n45nOc38yf5E3zEXf5uZakVblghSMcO4jVKivfpX01ojPW4ZYvKfXjk6yw2pOco8MOHAuJzSyY1tJf325/fkmGwWIyYZ1pPSXMHuhumT58O+m/7bu3ao8LmC82Z5VVuS57RbXVJVJAyZXr6GkKO55dOa2wvjfZiAtbM4j/uszV4nDbV2WRMzdDrnXZRlM2p3uICm+Ox2z8F1u1bLUte22nzK4IZ+bAcdJuZsn15O0wxjQde2ZsV5BgBy8cIQWXwOKF+cXGx1F6eOrt3yQRkyVObJx+o9P2p0S2OlyWpRTgY7PF9VpPwRZqF+p8Rhb12PDPwt9X09K1JKyOSYbV0Jf/w/sv6HTjVMMXpC/3U6QnOCAQVAecw2FpxbpKRot80odCw9M/3X/VtJFx3vh9Zs6dgz9GG63wiNT2oUIWKIqWBMBuUEOUDgXbwLHU6xch8OXN47rr7F4yqT4z7kTcP5u3YV/28yrBZdDsdITYcSVJhtKTOpRlDe2cOK9h4540j28yfXnlne9oHe5ufUWi+iIbydexgJKbUuskjzMue+OUVJyN+Fz65+boKe2iJqkpG6JKQv2NgBvvyq49ctSXiB7/veeGrkfvP+B5mWDoTzCRgEOF9+Rnsn996aPqmWH/4G83yt/5hRvHZOu9sv6iOF2U5F9K2QfMM8izv4Dj6tFGgNl8xLGd9yS/HVCeGj71+YPX+3MOnGuY2u+WpskoVBYNyCsdSJlmhvBzHOqCdnE0xUl9eOdq27oH542tjw64rrTb+5f1v/+yRmUJaVaD+OdnASDs+/+Osp4FxXFv69YodUw6fE3+jykoKRYdAmHTimELD8ufvvLxN+WLTSPabS/agO/cfXlna6+ujdQ8mCkeqWefKsOrX9M4yvbG6cMURev4HyrNdjJguXowSf0pVt5Tf8jR35Fy960K1Q5wXEGUrNkZZkqlGd+Cqb6uYpctfP3DPfYtGO7sYteatZM23hTuPVf+21iH90C8G80EowvMeTfxAiwB9kKOpcT6/MOnLI7WDln94bPl91w+tiU1DFsV+Te7ADb4AGtbaDjLY+0BDBRM2FXSJhlP//2D1sJLV+1cmNqZTTi6nsTnwC29AFNqLJzZN/K3XCVRIydwAP6MCUlHvnVrjVOcoUtgazrBgNqcN3KFDtXtGjcrxRuJw+0IT7O7Az4JBxAtNjddTHMUdhIu4BqSWlDA/Xzbu6vONnrubmv1TAnLIHIJRXNUEGOeRQSxXsUGnm7TzWN2IR17b/8cnbx1zLpJO7PetK3blf3OweqnLr8zz+YN5soLxIC/8w7hoyuVlxrs8wsQtpc5BT7x5cPmjCy+LzjEHW3Vck1ucXe9ScrHuVegA0i3syLe/OPcaRBCtE4iN/kmt7/rqBv+PcN6LTtDJlCeb+0i76MEH04MwcUGgoOzhiqZFdU7xmtiRw2zgmvvkGF+aOy7ruTUPTjmEwhEXsIsXND1dfv3hKbuvHJn1dG6aYZVBz4maGgwNTwIhqXNK1+8ob5yP+mwXo6RWrqs27jp+4d6qRvEWtzeQLyuQNeiZED7OmbTRANZmJLjv9AT6VNnFOzftrbijrKxMF5uGKjE6FCwYeaDBym3+0GongaopBmSh0ekbXm333bPnZMO9a7ccNcfG4w2GdJKs8MniSYxbDkEOQ3J2bBxuT0BAtTbiVwoEqfrmwMzXtp29PNafX1ah1w6Bv3CeJVmi/AEJFcI4d4dl+qiKBurR2ia0QEpmjDfMqIUP8EJubp/Yu6rJf9veozX3vLw2vlwY4dqd5w1nKxrva3CKtzV7xDzk0YY1xIUC6/SKfWDuumTPibolR48ehc4i4nYHOJa5gMsCWvmg8fuDoazj52oHR3xo3+pKrtknjQlAXBEOrCopsFxwOs5fNy663KiSxfnrZzcNrWn0LxaDMo+9JfYIOp6Ve2XoV8+e2Gf5r/9Px0NvsngT75fcMq788tGpL+Sk6//G85qhS2vM0PubLti9d//W/GVBYphk1zuPlF1R1Sjd5PUFdGGhCPvEXhfyDoYEVruBxcHnbq9oqrHLt/1lg2NsbJwhtLlpvWDsXdB7YfRhcQQCNZAGU7XGBRqB1ydZLzSKt320o/rq2BAMds1Ra0brExbCcxzf5k/HQTfOMpWtPuFXwjwJL71+xXyy0rEoVrDhNnbbUReusvg5VlmZqquo8d7T4PRPDnd6LUE0sztLsRofba6u8YFy6WualYWl5XUzoxG3/Ph4Y9kke7N0ixhUTKg+aunBszas4QGqT25fwFRpF29btcETZV30zmzJliIcomHIQoc8gzLNVNb7R2o3Wj7+cWBGqtsXLKJUEEJwITC1GHSMc+LgvFMtXrr9dVECglarsgZpodMr9ca5AToa4GWl6beO75f5wuIfDrJ3O0cdBHhy4aSqkQXmP2RaDfsoOqwdqgC1yS0POd0YmgfVGOGfNBbUq2sbvagWpWkjRYtPqDDVlqLflpdhfjUjxbAdVvajDQn9uUQl53yt63rNipMkdgxgEDjZZtH/Daxyq7LSTJ+YdGwD6GtaCE3YfFJqtV28VU0YjeKjpCmBZwPZqcZ1WWmGDzNj/rLSjR/mpOlW62XPN/Fh2l7hiNTgCF7zxjbnqLZPk995Z/eB/nXO4FxUY8OdHviFMliMfG16iu7NdKvwvl7PuyO4sVxgirRdaAgsAJU4qrajmlbnEOe7/UpqLGuW4dTsNMM3uenGVTaLbjOTwNrtU7Ir7I6fRuoTLV3WFKGUh0XkiEM1zeEVR8fWx5793n6ipGbQmvoGuaNhkiMwZfOY03EGjUgcXfmOFqYrnhP9PKMMKLC7K+fJMNMKt0yaMuk5x4CCtOeeuGv8hScTA1yC6+XNU0/elL15udMbXOXzKzAhVSlZDjG1Du+CF9d89SZ1y5UNHSbTMNXoD3onyDDUR3CrADLNxJcNyBKWzRzb9+S2AxVDDyvyyw4XNRCWarTosIw1Duix5mvB2lUXsV4MBi4494refzxSbq/OtaVlHz3feMPZas/9EupF4EA1gp6dm/j3elMOXJ7TIk/4QEHTC4x3xljbk/4A5w0Gw/o0ehMEM5WRLrl+N6/4fMkdCQHbXKqUO6Ck7zthXwiq8D7Q9dvNd2Kw81Xucd5AKBW69JZHNKXnuWBhL9PLg/NT3m7yqvqy802u8w2h27TFX/CFKluzW7x836nJqXAZ7hgfe4z13PFRcQhUukjPpbFO4cuH5Bt+P2ls/rcbN5cNVFVdb7srAAaKFtagzrl98kTq6FqeGjZfK3xehnWfwDkCwSANo74Kaq1MuTzScKriLKi9/cBeRVHnm5qHg7GFjyTGcgwFgryXmjY9PDFDT910FyUgR8udV3lEJR/waMnSYH7NTuE/X33/pK30A61D+Y0lu1IcXr/RR/m6mT0wacK/oVk29/P3j9ImmtibfLwjd11lg+cbf0CZSUGDU+HP7ecGn3DSEyGBdR0mkjlNR6ufZmqTzZZaw+G+V6Zx0zvMNdvouTAyl1C1c9M37nD5XANDIBjosCd1gZ5PrYXCRmq7nYRgl0xozoTeNY8u0FTL6hUfH6VWf3RiaUDGOS3EA1IUlEOp52oaM+HyXDtRaLd4lpWunJp14gf9+ze35+fB9m4m3MNs4igAc5G5j7/6zV/h8nCCl3YvFSXUS5vntTzFAltNbN3NUwyrr7/2cm1S/OCqXSvtO6tudvtovaYdQrlg+1B6VZU9HYJpAnL6NMUEZNWsCVqLrCHrrFT9htf6X7WRvppSkPWc1M+2NHnlIrA8hVNEY0CIsuHKMNzQBGRMgf7slwJT5/HRvVGtxRHJG1AKPzgdygI/lRA9/SOHfwwUN1o9LIw4Gam6Uqi7iKSH4+/GZ48FRN1Swl29zjs7KKm4/qdl2aRjpKH9bG9BTxXt8tS189jZpU23nWuUiigJVtSZaLfUpWyqrEzTnFgKJVwD6WgFnXvFYPeiZ7e+Y29umgETYDSCUGBl4WE4nw3A16MQJYvc44ERIKw+R72woD6kGLnz9KPhcBh+0TO8nb4QGWPCXqEBYM106hrcwfAkBnzmpuo9UE9oemEjYZVQiPb4AlE/7UUITYQuTEtr71E378Ecyh/KO1Dp/jnAe/jGLoRWYG0IR8NWR8N2H77xJ7rNURP1HRMGl2/ZV+30iHwONlYG9H2siAAN1o0WBwOfChPruJgYYA1GglP0/PDQjKyn30cfx/vRgBheq9VITBQ1T1fvetXIlzW45N4wDENLgI5GUlMPnnUOAV+VFEzQ3ffJI3Aij1WEn3pODRb1SjnSGkv3f/VYQL7J+mWK279vDKoM2IyAKZUKjewHI4XSFbH5mDePEg6wGQIjzfWpTI4MXWl3BBoW4SiOYZs00YhpnOMHWrccPuOs94lMNgP2blSBwMw8kVp6XkeVFHS4KBebvchv2EkcEzuYZnGW3UNnsgjRuq5ucJsV2EsTiQxbCws7+/UmXuooepyGv/LxiV6z719vQH849maajNSiG6c5Zg+gu6wyYLpoqbpg98/7w3s7XlRO+qN5w3i76jT1v2+r794jrF6Ya73lV9QJKlq4wEOqiTpnNqht1otaQ7X8UpjwLLrlUhTB5NaZmzZdsW3btI+tD87ENqd1igpNVzW5cX71xT+24wT9yAAVJugaa5yg65m6sSMtlZ1F3dHzHgvInuPOfDEQ6qUtbEGOYAsJlWbR7ZvT8Gy0lwknPC80pKB0VVBprHJ4+B853f5JAVkxgNLaUb60Z9jpWvVMRbaV/RCAxPVESwqUqr/pucN2VsrGHgXz4QZjwd/PKqgDd1tAOs1MFzxgxYDlk/5m/4WsX/xhi2JQeevnu+vmi1IoOhThIh7IYmPfVH1d8ihVyheQTZu/rnkoIKkgG+GiO3iZ+uvqDQdgIrwGzd/Jwyc8ATbN/lC/PUfEOTBH7FAwE0ImvQSRDz7yl4MrQmdrB4pgeEeL3YDe5upZE4pg8t6xU5nwfCzqS+s/4qo3+ijyA+v/tue4XdBfqlofCw8UgN3o8I+GkPRDJxx9gXN4gg4VgRavFL1wfA61tV0VNRJvZ989FpCaGk9eEJZ+IwngFnWDnj2YuN4BZceSl4PVZtXNH1QdqqpV5zj9yhxQMYbCxBWkPak2ROkEJpSfrluz8v4Zh159IJJS+JuGrQNzHvzHMbA2XYU9CjYiGHIthy9our2mJ8eH+H6ufAFJ+GBbBcw5Qm4w0abCGsN0KSjjbhvNwdZ/KsXE7yi0upP2tOgV9qXpgc8C+BltOQFQUesc6rGtx0a/C/c9WoQdfmBMYa0Q81Bpdy0YXZS+FfSeDkMlfdjXhpoXfeG8qq+rq8HIHT8el7Vb85/bixrb64NAS30njeJiHhTmWA7vPm73igHajP0lzJUoWPcYipP5C393DwfTb+sEHc4XmY1MacuCc4+T7bGAXGgO6NF6H6140EhSTELS4YweoB142v7S348f27Sn/KBdZ/qx0xuY7hfljHADTygDw1G2FN3BEfkWnNO0O9xY9EINTWsGDC0w6LZ8eYXblBDT93aJLMDez0ty4Of4G+xrNFZitI2DtQwWUOv69DKtLu7C3iCc8oDDhqg5FeKC9aZM56mqTusNA4FFLeDzy2DlwUmvQjV7qLF2Z4Bvl3dLGh1+vbGfXrjs82l1KzZOUSQlmi8tDHOYsugsFXAG6N3iYvqSjFKJeZk4Rl+7frdwvtmnDMFJEloYvX65z/bG4ekOT+VICVpJJFMw0lC2NOPexDi6e90p6GQRurwBtaUCNS/YRcIpwE51yV/9ZEgjTNzX/urCPd+W1/n3NLrEmxvQfNpi4gunB6ORwPoKswwrnrhrcuWTv2o/FwYDL2K6KD3YIPEQFpw9ifa47Yf67u/KMAnHVCKVhVWJKqhex9bZjOzyn141eMubXcgGhom1J4RgIisItIfJNLTbYcRGqYLfnDT9virFNxb2sMGBZpyLKPqztZ7LejyCjBCYcweCSxo96mww60bVRq2sKkN5THz1l8e/3QXXJ2Pzcql+Tzta4k8x3HKEoaUhlApNDWoa1j0sX1/wj3J5g0NjJ+gGnvINLcw8drFp91hAQJcF5pEmAFYDyCys3HYpvrAa9sHR8+fV8kfe/crk8EgjZTBgRxw2jEwr/+WVQ1I/gkSSNngYffhwJxvuo3EFOzMVlkb+yY6D/CMazAhmHzY+us1G/YkUPbW2MCtr1Q+Lc3HOndRhgQWWCWZlmPaAhQ30xzACWEMI6Rh5a57e0zpsJomFhhHYwPMbUs1gG3UEJ0Y6INj0GbOFI0ngdm5DXauUIZdpcgWGuP20GQzIMbUPOYSBkuXYIpanCyH4dyIg2G5uevyuvRwnzsOtVij0AYliztV6rwaTfyGOkngPZsS4SbbyuqF0zdJ2ytKdW11q0O1FmJGiE8FwgXvONDULrXlOt9SrPb/J7hUU0P6lr3xTgfbqyIwTe1tQQ+xFeaY/L55fnHSCBU2GnivKOSGsGXRwg2dppU+WqdPGEw5w6T+xGcPI5023mjbAOotMw3ZVOhTyQl9yNtNmKl34X4O2XROzcTB5DnA+x3knDE57KiSrvggbjtOB9Us+1hX1DI8uw9pTVd/slNcdHueEAByyCTeesLAlT7v9J2h2po7Ww2r6aDvMr7SKx8XWqMMOAf44Ro3OS6PPLuGP3pkp+w+ecSgSWHAwPVwwPFfru1oMSr0iu20YmKBbDPy3gwb9LbpJs6dZ6LGA9M5LqxG+rZPBXKQBwQ1wYHkZidsL6JKSLs8CQS2Krgdg1eHeozyb7sMHf1KwY/XvOijWlhLW+3FwGOr4WqcBtMA06yrql5N08ttBbJfoEUPxHNN4xUD+cS9lCZgFgTJxlPfa2TkNxbm5vnf/b9eTAaOHtORnGV8PSB/g6nqoeJ+yzDCTB6V+Wl7jLq8PMv1xdeDiXIOSk2F4nReoWli1MNXa3df64QhCWPAuLuauhr4sjznxOU81gjRmhUfWEFVl9wwCax+YicLCz0HnYDbye5KdLelqWugvTo/sTsDhOYEqncDWg4lfC4bDm8MTLN597bUw/PbM4ca+VDN7Zmg/0ysDwpP6pBG92jQnE47zDm01F8OwqmMrFhTKPdp302ZpDLrMpIkne4AswGrwU13akRfvnnjs6SVjjj0KW8BROJIF6ej+mVo4/HQxTpX5X50cU5tj07/H81wPCtSaOGgKKjU6J3TrxH5rp4xIW/bjKblPmwwcTPjC9d/qs/NfoDbGseWiNr7Ow86bs7veJLCnsK2gw9TdXpmDDlrLCKrcAkspuVb9fs3DRX70WEBmKZ84jDp+P+q6YQcb+nxy4ft7mNE9yRMWTMfzcj4cp30u48WjncWx94z3So8YyoU1N80r7nxNswj7qb79OlxEM5uhohMaPy5zi345zvrl8Us61B9jHfRQnbcGWDG1zhjbY66R9FDn1xnNF9Xlo6qBK9XDBljfsxpZMH13nn1MH+S8jUcwORuooikMvn7piV+OO3lZP+MZoNPGXyT/kW/Q6+L8IFPYuo7bbKIuxSxkJ7KOPkz8QS+W06y6w0y03aGH2HqCFXSBcl0+3Ho6MWhPrntckfT0EjkvXb9RBxvCMHs4vPmCqv5oRf2NUNiI1HQ9T2C3Trfw+4b1Zt9JXEtJjGQnnDE4U+X+Oexp0tQzTF9gaTk3zbgBqiOWVmJQ2CV0MgCtzhVbb3D2nYKzIRPhMFMunmR88r9Le51v8I3C93VFHMqK0QAr5CWdN4pImIv5BqFlTx2pTvntc1+Ybk74W7mu1AjZiWt4HaX11EiuLD1F+BA7ka44gWGb4/cR4HqDnHP/S9mXq3BOA+pX+Mv6c5Ohvg3x/uJjHyqcpvU8G2fZRKZOf3BayduH8tXS2/mS1/fkwMbEKbGsUUIhDTflxiX6eAeFVnNTjaVYlPYqGq13KUbh7A1XmzvetBofbdKrrhFLEnzkoKxNJ6s8DfBuBugR4AAnlKeumZ2z5Pltr0OQb5IEi7sNk1kwaePEXPD2zTGteOKOGdVPxvloe7Hyq7IZDc3SVG2Lg/YYt2LT54bmmba39Z1wJ+Nvfh1XfAJGvv5UqKXuoNLszYGJGw+cf+hgWe7JJk9Vkb05OK5VfcOdFAxlsxjKQUDaq5eERC7uElu+X1RMb3x2+j5vQG5jqDh9VlG2/37zJ9T/m3GoKynhouodz29/A6xZP3d6ZFtnfYjFyH4LR25wD7GAecEuEHYppO465Xr4mgrPelU+yzT65Lkw8QeLWAc4ig7IJr3pDEUrwyNWNGTa4BDHbd517qGDR354vMlb27/eGRgbyxrPm4CR4hj1zrXtrqf0KbAd0B1vCARUSpfYTbBgQbSY+X3Usn5xgtkVTu356fEIgpE98LPA6Wybbh3L8mFMYFHyiHKvE5We37zw7uHs9hJMvAcm3ZCg01HZacLGWcUFn2IPkegn9nrpS1/3OX3eu9QXlK3oFT3zPEzs083v33vTpx1s3wjHAhM3GSaaHxl4Rm7VoHAVXjbbneLt5+zyo3XNgSWwdgAqVzgrKMAmPSvmpuvWd5a/2Lz2/LeKK+kGe7N4p0+UH4ATfTF/8gNw/uZ39a7Aj7sT/ytLrzhsg53WNIzUnbneBdJhq4HdHzlzg/4VaNi1jb6ZNU3iI9XN4sPN3uAUPIvTkaPp+Yotlf/IINBgdIo4KFtQNtQ5/bdqrJ1iG9YGgfHn2gwfo3oYCRX7PSJfqYDjANU4WiQ6MBzCgiW/K1nYRP+dXbdNobMQMc9pulgakG95zWLioGGGowqB6a/eFZy9+Ujtks92laXEeG/3J0erp/LSTeuKck3Lb5rdscVmzWdlmQfLXXc3NAeuUEEtQoeHYlJNbPmAXNPbXbVajM2wrs+ycht5EMyokMAP2KoiONzB7CAcgY0KB3RRvADrMincJ8UjbFu1RL+HD9TJ4VCQoCiqAYoa86caYM+RCfa09etONkClDA7MTVllNnKeaJkjEWAPEOPu/dH05t7ZxhetZl0Tnt+IOLQYun1SOqxe21AtDTsM2ho8cc4xIJX5B6jOG3BtqDXdONZw3DfSEcGQpePhwB3/aX8LsyWSbuL3dTWPu6wW4TgctIp7hPHruJCUl2Xq0Us84iJrubgoAcE4lt81qbTApv9vnQDvYtEihQlvQDbC4s2da9ZX3r5yo/Y6m5bk2n4V5fMHLutvXHbD2NDOtk9b7zz31qGsj78qv6e2yX8Lnt/WRg9IEN6xFSzIMKx8NvP8qVbfHf+6b3FxzcBeumfh5ONGQYDNe9AI0BqDjRJXvPBbs87ARFCv48WsFN26okz22TvnxL+RBHR1MOK1hMXw0EnAYmW3mRrNhhBYZWAlDuOI/Qs3HeQa+4cWHJikRlqoVlheANNQTF5oUAn1sN87lsR10/vvyTTzXzGcDrrmcDpYdnjDSZw/nAJMGZr7Sd9M4a9Wk66G0TQE9I8Zgc8YPiYDX2vQsV4sO+ZdlOg4i91T915VV5BmeDI9xbARXjYBq4vJWLPIGt55Zlo/LEf4w0MdvD0T56hpZmEfbpCMlEPjBqdMjTrOPq03fy623Bfz+6LmIJgw9BjSgy9vWdno4cfDRror8Zgnngxz+YNZZ2rdv3F9IesefGXve0/fOe4s9DNxFYHhF/+wGIHuewYv2nFqCcXcbds1+IuDVQvqm4O3g8qRGm7EsGYCqhWoZp9NHJX3Fj3/6rgG005Ucbdefci546bHM5bBTuCT8NKEyVJQ6gc9pBEmnTxAD8IqlE+n052x6OntBdn6t1c+PPPAq4/ERUFZjeZqmPwegE4vW+tDWU5J0as7iorijzbEh2p7lWGk7Glm3TaDThioNcC2XuLu0Kzgzk5nw5sEW57kWo1HFDV0BnZ7G/AWK+gcvWy6uE4DV/Bvfmrbyx7Jn6tILKwjgD9e8KWb2TYHqe6aP8zzwseHV2zcWV3b5BWu8/kCw+FlfnDKEN+6Ba/7olmPySCUW43M2/CShKF6A3eNUVArBVouw3hj3XuPX/XNwt9vWFbZxJY3+5WJsL29ALaFmIC1AOfGg7C72QtvR6lINTI7++Xp33px6bR9L3W0BgaR56QZP7elSHPlIJ0RTYvh5Awr8/GsWc0JO8qjPrr9o3Vs7HbQ+ACLlm285nCNuLypWRyi7dfHx9BbGPVsE0j7x3De+8PrLk/bffu+4qau6IcoGM8NPZq1dW/FZIcn9DPQeWf5gzIYabGHB4UOt6PYDKUjepl+/dqj03H/T4/cvfDKolNl9vGSGhokh9QMI8+ZYB7l4Tm6AczOx8cNM+4tWTC53cXHz+Bc+dvvVlwbkELwvih8XxMdMhv0u19/ZPqh9jqDZBmEEYuZt2zTlWCxHkKFpGTetPuolMOpOdeYQX2+LFk0rDbiGV6Vk7fvZO1MhoGGB8cEQGuy/2CUbUPiboQtW87qX9lUNgtMIzkws4AOjvEOL7RtKFkU/y6qSLyQN+GXT28bW+t0j4JXBmUadHymLGtn/2qyLPzJebOHbPxk2+m8gE+ZodKhMx8s+8EW6DTbnTv8ae3BvO0HaiY4/Eoh7LxIh82mKW4x6BI4tjHNyJ2aNDp/b+KrlSL5SPxeC/9jwPuHXLOhAPC/BoT7Rji2JVst5q/W/K71fWGJ4bp7fckEBCv55ue2X1Z2tvkFsABNib6PF8ZrtOnr9boai4E9aTPzWwvzU76eOq7gxLzLYVFv65sU1bcPbO1xw+vEbuDf/cyT/vXRqiHlda6pjV7pSo9f7R8IBLPDh/lA9QHh4GGFGl4c8MnwHP2Dqx79wQlUCbpb8ET/kH/2ja0VPEwOWcWYLc8q+gCOKs7v0qiEwqzF9xgslFxEXqLxJGYu4TpZBwMQwq9OBf/J/ESiiqZVoi3udMoPd0hsnfoY47HUCLB6rlIX8oPz57fusoZ6AbnoWj1gW3nxH6f5QXkWTqlyy7NmFUnJhCqS32Tf0XKgh4vk314al0xAMHIs+C+e2TL6XH3wiWq799ogHBQDaGGHejNsvIUV3SaB5+16TrHju45gcS9ghKVPL7zLyOMPWcB0mONXmHSwdGTCK2fSYGIDym24Q0I9Uw8T5vws89pMY/D37/7+v451tVJackG+CIFuEYg0324F6sgzCsmCZeuHNvmExZV270LY4Qv/QQ7MS7RA8AktGgQFv0CPpQOgf8IeAeh6oA8LKSoHerSAPSFetwoG6MqsAGsdrD0/w7DKRnvfePOpH5dB+E57vo7ySp4RAp0RuOQCggnicLtw+c5cr8M3vdYRXNDU7J0K71YQ8NUwKCmYKLZsTTAwQIyLjNKagMB9/G8P4N22vnSrYVNuuv5NS6rx69fundjpekdMlOQnIdBjAt+JgERyA6ZZ01e7y/oGzeZJsH9/rsMjTvaCFUqGw2h4nhjFJPyu13AINF+iIov2bXx5IrxJo8FiFLbr9czfWYXec/3sAecXTQ+/AymSBvkmBL5LAt+pgEQy/sCzOyxnnN48eNtHUaNLGuaHbfHwapkiH7xlENYN0kEtA7M9Awfd1CZ4rWitTsedMXLcYXin0THYanF6WC9LdclizRwciZJ8EwLfC4HvRUAiJcEXEr/2RbP5gleyhrwNqSGR0WfkpKbA68XBMElRjY1OV1BkPAar1Z3TJ9W5/BeXeWBEabNhLRIf+SYE/m0JhCfieApD5aJ/YEr8ty0wKRghQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAChAAhQAgQAoQAIUAIEAKEACFACBAC/xIE/hfbgdKqP3EBdAAAAABJRU5ErkJggg==" alt="OpenGov">';
  h+='<div class="header-title">';
  h+='<h1>FEMA Audit Package &mdash; '+esc(P.disaster)+' &mdash; '+esc(P.name)+'</h1>';
  h+='<p>Cost Recovery Substantiation | PW: '+esc(P.pw)+' | Period: '+fmtDate(P.periodStart)+' &mdash; '+fmtDate(P.periodEnd)+' | Contractor: '+esc(P.contractors)+'</p>';
  h+='</div>';
  h+='<img class="header-logo" src="data:image/png;base64,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" alt="City of Tampa">';
  h+='</div>';
  h+='<p style="font-size:11px;color:#94a3b8;margin-top:4px">Generated '+new Date().toLocaleDateString()+' | Cost recovery substantiation package | OpenGov Emergency Management Console &mdash; Tampa Cost Recovery</p>';
  h+='</div>';

  // Stats — grand total banner + per-category breakdown
  h+='<div class="stat-row" style="margin-bottom:8px">';
  h+='<div class="stat-card" style="grid-column:1/-1;background:#f0fdf4;border-color:#86efac;display:flex;justify-content:space-between;align-items:center;padding:16px 24px;text-align:left">';
  h+='<div><div style="font-size:11px;font-weight:600;color:#64748b;letter-spacing:.05em;margin-bottom:2px">TOTAL REIMBURSEMENT CLAIMED</div><div style="font-size:28px;font-weight:800;color:#037730;font-family:monospace">'+fmtM(T.grandTotal)+'</div></div>';
  h+='<div style="display:flex;gap:24px;font-size:12px">';
  h+='<div style="text-align:right"><div style="color:#64748b">Contractor Invoices</div><div style="font-weight:700;font-family:monospace">'+fmtM(T.invoiceCost)+'</div></div>';
  h+='<div style="text-align:right"><div style="color:#64748b">FA Labor</div><div style="font-weight:700;font-family:monospace">'+fmtM(T.laborCost)+'</div></div>';
  h+='<div style="text-align:right"><div style="color:#64748b">FA Equipment</div><div style="font-weight:700;font-family:monospace">'+fmtM(T.equipCost)+'</div></div>';
  h+='</div>';
  h+='</div>';
  h+='</div>';
  h+='<div class="stat-row">';
  var stats=[
    {v:T.tickets.toLocaleString(),l:'Load Tickets'},
    {v:fmtCY(T.cy)+' CY',l:'Cubic Yards'},
    {v:(DATA.labor||[]).length+' records',l:'FA Labor Records'},
    {v:(DATA.equipment||[]).length+' records',l:'FA Equipment Records'},
    {v:String(T.trucks),l:'Trucks Certified'},
    {v:String(T.sites),l:'Disposal Sites'},
    {v:T.reconVariancePct+'%',l:'Recon Variance'},
  ];
  stats.forEach(function(s){h+='<div class="stat-card"><div class="stat-value">'+esc(s.v)+'</div><div class="stat-label">'+esc(s.l)+'</div></div>'});
  h+='</div>';

  // Search
  h+='<input type="text" id="search" class="search" placeholder="Search tickets by ticket #, truck #, or subcontractor...">';
  h+='<div class="filter-info" id="visible-count">'+T.tickets+' of '+T.tickets+' tickets</div>';

  // Nav
  h+='<div class="nav">';
  ['tickets','map','trucks','sites','clin','reconciliation','invoices','force_account'].forEach(function(sec){
    var faCount=(DATA.labor||[]).length+(DATA.equipment||[]).length;
    var labels={tickets:'Ticket Index ('+T.tickets+')',map:'Map',trucks:'Trucks ('+T.trucks+')',sites:'Sites ('+T.sites+')',clin:'CLIN Breakdown',reconciliation:'Reconciliation',invoices:'Invoices & Docs',force_account:'Force Account ('+faCount+')'};
    h+='<button class="nav-btn'+(currentSection===sec?' active':'')+'" onclick="switchSection(\\''+sec+'\\')" data-section="'+sec+'">'+labels[sec]+'</button>';
  });
  h+='</div>';

  // Sections
  h+=renderSection();

  h+='<div class="print-note">OpenGov Emergency Management Console &mdash; Tampa Cost Recovery | This file is part of a FEMA ZIP package. Supporting documents are included in the ZIP alongside this file. Open index.html from within the extracted ZIP folder to access linked documents.</div>';
  app.innerHTML=h;
  bindEvents();
}

function renderSection(){
  switch(currentSection){
    case 'tickets': return renderTickets();
    case 'map': return renderMap();
    case 'trucks': return renderTrucks();
    case 'sites': return renderSites();
    case 'clin': return renderCLIN();
    case 'reconciliation': return renderRecon();
    case 'invoices': return renderInvoices();
    case 'force_account': return renderForceAccount();
    case 'documents': return renderDocs();
    default: return '';
  }
}

function renderTickets(){
  var h='<div class="card"><h2>Ticket Index &mdash; Complete Audit Trail</h2>';
  h+='<div class="scroll-table"><table><thead><tr>';
  var cols=[
    {k:'tn',l:'Ticket #'},{k:'date',l:'Date'},{k:'truck',l:'Truck #'},{k:'sub',l:'Subcontractor'},
    {k:'dt',l:'Debris Type'},{k:'cy',l:'CYD',cls:'text-right mono'},{k:'clin',l:'CLIN'},
    {k:'rate',l:'Rate',cls:'text-right mono'},{k:'cost',l:'Cost',cls:'text-right mono'},{k:'site',l:'Disposal Site'}
  ];
  cols.forEach(function(c){
    var active=sortCol===c.k;
    var arrow=active?(sortDir==='asc'?' \\u2191':' \\u2193'):'';
    h+='<th class="'+(active?'sort-active':'')+'" onclick="sortTickets(\\''+c.k+'\\')" style="'+(c.cls&&c.cls.indexOf('text-right')>-1?'text-align:right':'')+'">'+c.l+arrow+'</th>';
  });
  h+='</tr></thead><tbody>';
  var tickets=getSortedTickets();
  tickets.forEach(function(t,i){
    var searchText=[t.tn,t.truck,t.sub,t.dt,t.clin,t.site,t.addr].join(' ').toLowerCase();
    h+='<tr class="ticket-row expandable" data-id="'+t.id+'" data-search-text="'+esc(searchText)+'">';
    h+='<td style="font-weight:600">'+esc(t.tn)+'</td>';
    h+='<td>'+fmtDate(t.date)+'</td>';
    h+='<td>'+esc(t.truck)+'</td>';
    h+='<td>'+esc(t.sub)+'</td>';
    h+='<td>'+esc(t.dt)+'</td>';
    h+='<td class="text-right mono">'+fmtCY(t.cy)+'</td>';
    h+='<td>'+esc(t.clin)+(t.clinDesc?' — '+esc(t.clinDesc):'')+'</td>';
    h+='<td class="text-right mono">'+(t.rate?fmtMon(t.rate):'-')+'</td>';
    h+='<td class="text-right mono fw-bold">'+(t.cost?fmtMon(t.cost):'-')+'</td>';
    h+='<td>'+esc(t.site)+'</td>';
    h+='</tr>';
    // Detail row
    h+='<tr><td colspan="10" style="padding:0"><div class="detail" id="detail-'+t.id+'">';
    h+=renderTicketDetail(t);
    h+='</div></td></tr>';
  });
  h+='</tbody></table></div></div>';
  return h;
}

function renderTicketDetail(t){
  var h='<div class="detail-grid">';
  // Chain of custody
  h+='<div class="detail-section"><h3>Chain of Custody</h3>';
  h+='<div class="timeline">';
  h+='<div class="timeline-item"><div class="tl-title">Pickup / Loading</div>';
  h+='<div class="tl-detail">Date: '+fmtDate(t.date)+'</div>';
  if(t.addr)h+='<div class="tl-detail">Location: '+esc(t.addr)+'</div>';
  if(t.zone)h+='<div class="tl-detail">Zone: '+esc(t.zone)+'</div>';
  h+='<div class="tl-detail">Monitor: TDMS (independent)</div>';
  h+='</div>';
  h+='<div class="timeline-item"><div class="tl-title">Transit</div>';
  h+='<div class="tl-detail">Truck: '+esc(t.truck)+'</div>';
  if(t.sub)h+='<div class="tl-detail">Subcontractor: '+esc(t.sub)+'</div>';
  h+='</div>';
  h+='<div class="timeline-item"><div class="tl-title">Disposal</div>';
  h+='<div class="tl-detail">Site: '+esc(t.site||'-')+'</div>';
  h+='<div class="tl-detail">Monitor: TDMS (tower monitor)</div>';
  h+='</div>';
  h+='</div></div>';

  // Billing + truck
  h+='<div class="detail-section"><h3>CLIN Billing</h3>';
  h+='<div class="detail-row"><span class="label">CLIN Code</span><span class="value">'+esc(t.clin||'UNCODED')+(t.clinDesc?' — '+esc(t.clinDesc):'')+'</span></div>';
  h+='<div class="detail-row"><span class="label">Description</span><span class="value">'+esc(t.clinDesc||'-')+'</span></div>';
  h+='<div class="detail-row"><span class="label">Cubic Yards</span><span class="value mono">'+fmtCY(t.cy)+'</span></div>';
  h+='<div class="detail-row"><span class="label">Rate</span><span class="value mono">'+(t.rate?fmtMon(t.rate):'-')+'</span></div>';
  h+='<div class="detail-row"><span class="label">Extended Cost</span><span class="value mono fw-bold">'+(t.cost?fmtMon(t.cost):'-')+'</span></div>';

  // Truck cert details
  var truck=null;
  DATA.trucks.forEach(function(tr){if(tr.num===t.truck)truck=tr});
  if(truck){
    h+='<h3 style="margin-top:12px">Truck Certification</h3>';
    h+='<div class="detail-row"><span class="label">Truck #</span><span class="value">'+esc(truck.num)+'</span></div>';
    h+='<div class="detail-row"><span class="label">Capacity</span><span class="value">'+esc(truck.cap)+' CY</span></div>';
    h+='<div class="detail-row"><span class="label">License Plate</span><span class="value">'+esc(truck.plate)+' ('+esc(truck.state)+')</span></div>';
    h+='<div class="detail-row"><span class="label">Type</span><span class="value">'+esc(truck.type)+'</span></div>';
    h+='<div class="detail-row"><span class="label">Driver</span><span class="value">'+esc(truck.driver||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Cert Date</span><span class="value">'+fmtDate(truck.certDate)+'</span></div>';
    h+='<div class="detail-row"><span class="label">Certified By</span><span class="value">'+esc(truck.certBy||'-')+'</span></div>';
    // Capacity check
    var ticketCY=parseFloat(t.cy)||0;
    var truckCap=parseFloat(truck.cap)||0;
    if(truckCap>0&&ticketCY>truckCap){
      h+='<div style="margin-top:6px"><span class="badge badge-err">CAPACITY EXCEEDED: '+fmtCY(ticketCY)+' CY loaded into '+fmtCY(truckCap)+' CY truck</span></div>';
    } else if(truckCap>0){
      h+='<div style="margin-top:6px"><span class="badge badge-ok">Load OK: '+fmtCY(ticketCY)+' / '+fmtCY(truckCap)+' CY ('+(ticketCY/truckCap*100).toFixed(0)+'%)</span></div>';
    }
    // Truck cert link from index
    var certInfo=DATA.truckCertIndex&&DATA.truckCertIndex[t.truck];
    if(certInfo&&certInfo.docId){
      h+='<div style="margin-top:6px"><a href="truck-certs/Truck-'+esc(t.truck)+'.pdf" target="_blank" style="color:#4B3FFF;font-weight:600;font-size:12px">\\ud83d\\udcc4 View Truck Certification PDF</a>';
      h+=' <span style="font-size:11px;color:#94a3b8">(if included in full package)</span></div>';
    } else {
      h+='<div style="margin-top:4px;font-size:11px;color:#94a3b8">Full cert PDF with photos available in City of Tampa Emergency Management Console (Truck Registry).</div>';
    }
  }
  h+='</div></div>';
  // Ticket image links — use page index to find the right batch, then link to HTML viewer
  var pageInfo=DATA.ticketPageIndex&&DATA.ticketPageIndex[t.tn];
  if(pageInfo&&DATA.ticketImagePaths&&DATA.ticketImagePaths.length>0){
    // Find the matching PDF path by filename
    var targetTitle=(pageInfo.file||'').replace(/[^a-zA-Z0-9._\-]/g,'_');
    var batchPath=null;
    DATA.ticketImagePaths.forEach(function(p){
      var fname=p.split('/').pop()||'';
      if(fname===targetTitle+'.pdf'||fname===targetTitle||p.indexOf(targetTitle)>-1)batchPath=p;
    });
    if(!batchPath&&DATA.ticketImagePaths.length>0)batchPath=DATA.ticketImagePaths[0];
    // Derive HTML viewer path (same name, .html extension)
    var htmlPath=batchPath?batchPath.replace(/\.pdf$/i,'.html'):null;
    h+='<div style="margin-top:10px;padding:8px 12px;background:#f0f4ff;border-radius:6px;font-size:12px">';
    h+='<strong style="color:#4B3FFF">\\ud83d\\udcc4 Original Ticket Scan Located</strong>';
    h+='<div style="margin-top:6px;color:#475569">'+esc(pageInfo.file||'')+' &mdash; Page '+pageInfo.page+'</div>';
    if(htmlPath){
      h+='<div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">';
      h+='<a href="'+esc(htmlPath)+'#tn-'+esc(t.tn)+'" target="_blank" style="padding:8px 16px;background:#4B3FFF;color:white;border-radius:6px;font-weight:600;text-decoration:none;font-size:13px">\\ud83d\\udcc4 View Ticket</a>';
      h+='<a href="'+esc(batchPath)+'" target="_blank" style="padding:8px 14px;background:#f1f5f9;color:#475569;border-radius:6px;font-weight:600;text-decoration:none;font-size:12px">Original PDF \\u2197</a>';
      h+='</div>';
      h+='<div style="margin-top:5px;font-size:11px;color:#94a3b8">Opens ticket viewer directly at this ticket. Original PDF batch also linked for reference.</div>';
    }
    h+='</div>';
  } else if(DATA.ticketImagePaths&&DATA.ticketImagePaths.length>0){
    var _idxSize=Object.keys(DATA.ticketPageIndex||{}).length;
    h+='<div style="margin-top:10px;padding:8px 12px;background:#fafbfc;border:1px solid #e2e8f0;border-radius:6px;font-size:12px;color:#64748b">';
    if(_idxSize===0){
      h+='\\ud83d\\udcc4 '+DATA.ticketImagePaths.length+' scan PDF'+(DATA.ticketImagePaths.length===1?'':'s')+' included. Page-level index not built &mdash; click <strong>Build Indexes</strong> in the EMC then re-export.';
    } else {
      h+='\\ud83d\\udcc4 Scan not found in '+DATA.ticketImagePaths.length+' uploaded PDF batch file'+(DATA.ticketImagePaths.length===1?'':'s')+'.'
       +' The physical ticket may not have been included in the uploaded scan batches, or the page text was unreadable.';
      h+='<div style="margin-top:6px">';
      DATA.ticketImagePaths.forEach(function(p){
        var fname=p.split('/').pop()||p;
        h+='<a href="'+esc(p)+'" target="_blank" style="color:#4B3FFF;margin-right:10px;font-size:11px">'+esc(fname)+'</a>';
      });
      h+='</div>';
      h+='<div style="margin-top:3px;font-size:11px">Search for ticket <span style="font-family:monospace">'+esc(t.tn)+'</span> within any of the above files.</div>';
    }
    h+='</div>';
  } else {
    h+='<div style="font-size:11px;color:#94a3b8;margin-top:8px">TDMS ticket scans available in City of Tampa Emergency Management Console. Ticket ID: '+t.id+'</div>';
  }
  return h;
}

function renderMap(){
  var h='<div class="card"><h2>Debris Collection Map &mdash; '+DATA.mapPoints.length+' Pickup Locations</h2>';
  h+='<div style="display:flex;gap:16px;margin-bottom:8px;font-size:12px;color:#64748b;flex-wrap:wrap">';
  h+='<span style="color:#08A847">&#9679; Vegetative</span> <span style="color:#C68700">&#9679; C&amp;D</span> <span style="color:#546574">&#9679; Mixed</span> <span style="color:#CD4F09">&#9679; Soil/Mud/Sand</span>';
  h+='<span style="color:#D33423">&#9679; Disposal Sites</span>';
  h+='</div>';
  if(DATA.mapPoints.length===0){
    h+='<p style="color:#94a3b8;padding:40px;text-align:center">No tickets with GPS coordinates found in this package.</p>';
  } else {
    h+='<div id="fema-map" style="height:600px;border-radius:8px;border:1px solid #e2e8f0"></div>';
  }
  h+='<p style="margin-top:8px;font-size:11px;color:#94a3b8">Map tiles from OpenStreetMap. Each marker represents a verified debris pickup location with GPS coordinates from the TDMS monitoring system. Disposal sites shown as large red markers.</p>';
  h+='</div>';
  return h;
}

function renderTrucks(){
  var h='<div class="card"><h2>Truck Index &mdash; '+DATA.trucks.length+' Certified Trucks</h2>';
  h+='<input type="text" id="truck-search" class="search" placeholder="Search trucks by number, plate, driver, or subcontractor...">';
  h+='<div class="scroll-table"><table><thead><tr>';
  ['Truck #','Capacity','Plate','State','Type','Subcontractor','Driver','Cert Date','Status'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  DATA.trucks.forEach(function(t){
    var searchText=[t.num,t.plate,t.driver,t.sub,t.type,t.state].join(' ').toLowerCase();
    h+='<tr class="truck-row expandable" data-id="truck-'+t.id+'" data-search-text="'+esc(searchText)+'">';
    h+='<td style="font-weight:600">'+esc(t.num)+'</td>';
    h+='<td class="text-right mono">'+esc(t.cap)+' CY</td>';
    h+='<td>'+esc(t.plate)+'</td>';
    h+='<td>'+esc(t.state)+'</td>';
    h+='<td>'+esc(t.type)+'</td>';
    h+='<td>'+esc(t.sub)+'</td>';
    h+='<td>'+esc(t.driver||'-')+'</td>';
    h+='<td>'+fmtDate(t.certDate)+'</td>';
    h+='<td>'+esc(t.certStatus||'-')+'</td>';
    h+='</tr>';
    // Detail
    h+='<tr><td colspan="9" style="padding:0"><div class="detail" id="detail-truck-'+t.id+'">';
    h+='<div class="detail-grid">';
    h+='<div class="detail-section"><h3>Certification Details</h3>';
    h+='<div class="detail-row"><span class="label">Placard #</span><span class="value">'+esc(t.placard||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Cert Expires</span><span class="value">'+fmtDate(t.certExpires)+'</span></div>';
    h+='<div class="detail-row"><span class="label">Certified By</span><span class="value">'+esc(t.certBy||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Has Tailgate</span><span class="value">'+esc(t.tailgate||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Driver Phone</span><span class="value">'+esc(t.driverPhone||'-')+'</span></div>';
    var trCertInfo=DATA.truckCertIndex&&DATA.truckCertIndex[t.num];
    if(trCertInfo&&trCertInfo.docId){
      h+='<div style="margin-top:6px"><a href="truck-certs/Truck-'+esc(t.num)+'.pdf" target="_blank" style="color:#4B3FFF;font-weight:600;font-size:12px">\\ud83d\\udcc4 View Truck Certification PDF</a>';
      h+=' <span style="font-size:11px;color:#94a3b8">(if included in full package)</span></div>';
    } else {
      h+='<div style="margin-top:6px;font-size:11px;color:#94a3b8">Full cert PDF with photos available in City of Tampa Emergency Management Console (Truck Registry).</div>';
    }
    h+='</div>';
    // Tickets hauled by this truck
    h+='<div class="detail-section"><h3>Tickets Hauled</h3>';
    var hauled=DATA.tickets.filter(function(tk){return tk.truck===t.num});
    if(hauled.length>0){
      h+='<div style="font-size:12px;margin-bottom:6px"><strong>'+hauled.length+'</strong> loads</div>';
      h+='<div style="max-height:200px;overflow-y:auto;font-size:11px">';
      hauled.slice(0,50).forEach(function(tk){
        h+='<div style="display:flex;justify-content:space-between;padding:2px 0;border-bottom:1px solid #f1f5f9">';
        h+='<span>'+esc(tk.tn)+' ('+fmtDate(tk.date)+')</span>';
        h+='<span class="mono">'+fmtCY(tk.cy)+' CY</span>';
        h+='</div>';
      });
      if(hauled.length>50)h+='<div style="color:#94a3b8;padding:4px 0">...and '+(hauled.length-50)+' more</div>';
      h+='</div>';
    } else {
      h+='<div style="font-size:12px;color:#94a3b8">No tickets found for this truck in this package period.</div>';
    }
    h+='</div></div></div></td></tr>';
  });
  h+='</tbody></table></div></div>';
  return h;
}

function renderSites(){
  var h='<div class="card"><h2>Disposal Site Index &mdash; '+DATA.sites.length+' Sites</h2>';
  h+='<div class="scroll-table"><table><thead><tr>';
  ['Site Name','Type','DEP Auth','Accepted Types','Loads','CY Received','Status'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  DATA.sites.forEach(function(s){
    h+='<tr class="expandable" data-id="site-'+s.id+'">';
    h+='<td style="font-weight:600">'+esc(s.name)+'</td>';
    h+='<td>'+esc((s.type||'-').toUpperCase())+'</td>';
    h+='<td>'+(s.depAuth?'<span class="badge badge-ok">DEP Authorized</span>':'-')+'</td>';
    h+='<td>'+esc(s.accepted.join(', ')||'-')+'</td>';
    h+='<td class="text-right">'+s.loads.toLocaleString()+'</td>';
    h+='<td class="text-right mono">'+fmtCY(s.cy)+'</td>';
    h+='<td>'+esc(s.status||'-')+'</td>';
    h+='</tr>';
    // Detail
    h+='<tr><td colspan="7" style="padding:0"><div class="detail" id="detail-site-'+s.id+'">';
    h+='<div class="detail-grid">';
    h+='<div class="detail-section"><h3>Site Details</h3>';
    h+='<div class="detail-row"><span class="label">Address</span><span class="value">'+esc(s.addr||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Capacity</span><span class="value">'+esc(s.cap?s.cap+' CY':'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Hours</span><span class="value">'+esc(s.hours||'-')+'</span></div>';
    h+='<div class="detail-row"><span class="label">Monitor</span><span class="value">'+esc(s.monitor||'-')+'</span></div>';
    h+='</div>';
    h+='<div class="detail-section"><h3>Volume Summary</h3>';
    h+='<div class="detail-row"><span class="label">Loads</span><span class="value">'+s.loads.toLocaleString()+'</span></div>';
    h+='<div class="detail-row"><span class="label">CY Received</span><span class="value mono">'+fmtCY(s.cy)+'</span></div>';
    h+='</div></div></div></td></tr>';
  });
  h+='</tbody></table></div></div>';
  return h;
}

function renderCLIN(){
  var h='<div class="card"><h2>CLIN Rate Breakdown</h2>';
  if(DATA.clinSummary.length===0)return h+'<p style="color:#94a3b8">No CLIN-coded tickets found.</p></div>';
  var grandCost=DATA.totals.clinCost||1;
  h+='<div class="scroll-table"><table><thead><tr>';
  ['CLIN Code','Description','Quantity','Total CY','Unit Rate','Extended Cost','% of Total'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  DATA.clinSummary.forEach(function(r){
    h+='<tr>';
    h+='<td style="font-weight:600">'+esc(r.clinCode)+(r.description?' — '+esc(r.description):'')+'</td>';
    h+='<td>'+esc(r.description)+'</td>';
    h+='<td class="text-right">'+r.count.toLocaleString()+'</td>';
    h+='<td class="text-right mono">'+fmtCY(r.totalCY)+'</td>';
    h+='<td class="text-right mono">'+(r.rate?fmtMon(parseFloat(r.rate)):'-')+'</td>';
    h+='<td class="text-right mono fw-bold">'+fmtMon(r.totalCost)+'</td>';
    h+='<td class="text-right">'+((r.totalCost/grandCost)*100).toFixed(1)+'%</td>';
    h+='</tr>';
  });
  // Total row
  var totCY=0;DATA.clinSummary.forEach(function(r){totCY+=r.totalCY});
  h+='<tr class="total-row">';
  h+='<td colspan="2">GRAND TOTAL</td>';
  h+='<td class="text-right">'+DATA.totals.tickets.toLocaleString()+'</td>';
  h+='<td class="text-right mono">'+fmtCY(totCY)+'</td>';
  h+='<td></td>';
  h+='<td class="text-right mono">'+fmtMon(DATA.totals.clinCost)+'</td>';
  h+='<td class="text-right">100.0%</td>';
  h+='</tr>';
  h+='</tbody></table></div></div>';
  return h;
}

function renderRecon(){
  var h='<div class="card"><h2>Reconciliation Summary</h2>';
  if(DATA.reconciliation.length===0)return h+'<p style="color:#94a3b8">No reconciliations found.</p></div>';
  h+='<div class="scroll-table"><table><thead><tr>';
  ['Contractor / Period','Invoiced','Verified','Variance','Variance %'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  var totalInv=0,totalVer=0;
  DATA.reconciliation.forEach(function(r){
    totalInv+=r.invoiced;totalVer+=r.verified;
    var v=r.invoiced-r.verified;
    h+='<tr>';
    h+='<td>'+esc(r.contractor)+' ('+fmtDate(r.periodStart)+' - '+fmtDate(r.periodEnd)+')</td>';
    h+='<td class="text-right mono">'+fmtMon(r.invoiced)+'</td>';
    h+='<td class="text-right mono">'+fmtMon(r.verified)+'</td>';
    h+='<td class="text-right mono">'+fmtMon(Math.abs(v))+'</td>';
    h+='<td class="text-right mono'+(Math.abs(r.variancePct)>5?' warn':'')+'">'+r.variancePct.toFixed(1)+'%</td>';
    h+='</tr>';
  });
  h+='<tr class="total-row">';
  h+='<td>TOTAL</td>';
  h+='<td class="text-right mono">'+fmtMon(totalInv)+'</td>';
  h+='<td class="text-right mono">'+fmtMon(totalVer)+'</td>';
  h+='<td class="text-right mono">'+fmtMon(Math.abs(totalInv-totalVer))+'</td>';
  h+='<td></td>';
  h+='</tr>';
  h+='</tbody></table></div>';
  h+='<div style="margin-top:12px;font-size:12px;color:#64748b">Recommended Payment: '+fmtMon(totalVer)+'</div>';
  h+='</div>';
  return h;
}

function renderInvoices(){
  var h='<div class="card"><h2>Invoice Records</h2>';
  if(DATA.invoices.length===0)return h+'<p style="color:#94a3b8">No invoices found.</p></div>';
  h+='<div class="scroll-table"><table><thead><tr>';
  ['Invoice #','Contractor','Period','Amount','Status'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  DATA.invoices.forEach(function(inv){
    h+='<tr>';
    h+='<td>'+esc(inv.num||'#'+inv.id)+'</td>';
    h+='<td>'+esc(inv.contractor)+'</td>';
    h+='<td>'+fmtDate(inv.periodStart)+' - '+fmtDate(inv.periodEnd)+'</td>';
    h+='<td class="text-right mono fw-bold">'+fmtMon(inv.amount)+'</td>';
    h+='<td><span class="badge '+(inv.status==='approved'?'badge-ok':inv.status==='draft'?'':'badge-warn')+'">'+esc(inv.status)+'</span></td>';
    h+='</tr>';
  });
  h+='</tbody></table></div></div>';
  // Append documents section below invoices
  h+=renderDocs();
  return h;
}

function renderDocs(){
  var docs=DATA.fetchedDocs||DATA.documents||[];
  var totalDocs=docs.length;
  var fetchedCount=0;docs.forEach(function(d){if(d.hasFile)fetchedCount++});
  var h='<div class="card"><h2>Document Index &mdash; '+totalDocs+' Documents ('+fetchedCount+' fetched)</h2>';
  if(totalDocs===0&&!DATA.documents.length)return h+'<p style="color:#94a3b8">No documents in archive.</p></div>';

  // Quick Links box — only show files that are actually in the ZIP
  h+='<div style="margin-bottom:12px;padding:10px 14px;background:#f0f4ff;border-radius:8px;font-size:12px;color:#4B3FFF">';
  h+='<strong>\\ud83d\\udcc1 Included Files in ZIP:</strong><br>';
  // Always-present files
  h+='<a href="narrative/PW-Backup-Summary.pdf" target="_blank" style="margin-right:12px">\\ud83d\\udcd8 Narrative PDF</a>';
  h+='<a href="data/tickets.csv" target="_blank" style="margin-right:12px">\\ud83d\\udcca Ticket Data CSV</a>';
  h+='<a href="data/truck-manifest.csv" target="_blank" style="margin-right:12px">\\ud83d\\ude9a Truck Manifest CSV</a>';
  h+='<a href="data/reconciliation.csv" target="_blank" style="margin-right:12px">\\ud83d\\udcb0 Reconciliation CSV</a>';
  h+='<a href="data/disposal-sites.csv" target="_blank" style="margin-right:12px">\\ud83c\\udfed Disposal Sites CSV</a>';
  // Fetched source documents
  docs.forEach(function(d){
    if(d.hasFile&&d.path){
      h+='<a href="'+esc(d.path)+'" target="_blank" style="margin-right:12px;display:inline-block;margin-top:4px">\\ud83d\\udcc4 '+esc(d.title)+'</a>';
    }
  });
  h+='</div>';

  // Full document table
  h+='<div class="scroll-table"><table><thead><tr>';
  ['#','Type','Title','Status','File'].forEach(function(c){h+='<th>'+c+'</th>'});
  h+='</tr></thead><tbody>';
  docs.forEach(function(d,i){
    var statusBadge=d.hasFile?'<span class="badge badge-ok">Fetched</span>':'<span class="badge badge-err">Not available</span>';
    h+='<tr>';
    h+='<td>'+(i+1)+'</td>';
    h+='<td><span class="badge">'+esc((d.type||'doc').replace(/_/g,' ').toUpperCase())+'</span></td>';
    h+='<td>'+esc(d.title||'Untitled')+'</td>';
    h+='<td>'+statusBadge+'</td>';
    h+='<td>'+(d.hasFile&&d.path?'<a href="'+esc(d.path)+'" target="_blank" style="color:#4B3FFF;font-weight:600">\\ud83d\\udd17 View</a>':'<span style="color:#94a3b8">Not available</span>')+'</td>';
    h+='</tr>';
  });
  h+='</tbody></table></div></div>';
  return h;
}

function renderForceAccount(){
  var labor=DATA.labor||[];
  var equip=DATA.equipment||[];
  var laborTotal=labor.reduce(function(s,l){return s+(parseFloat(l.cost)||0)},0);
  var equipTotal=equip.reduce(function(s,e){return s+(parseFloat(e.cost)||0)},0);
  var h='<div class="card"><h2>Force Account &mdash; City of Tampa DPW Labor &amp; Equipment</h2>';
  h+='<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px;margin-bottom:16px">';
  h+='<div class="stat-card"><div class="stat-value">'+labor.length+'</div><div class="stat-label">Labor Entries</div></div>';
  h+='<div class="stat-card"><div class="stat-value">'+equip.length+'</div><div class="stat-label">Equipment Uses</div></div>';
  h+='<div class="stat-card"><div class="stat-value">'+fmtM(laborTotal)+'</div><div class="stat-label">Labor Cost</div></div>';
  h+='<div class="stat-card"><div class="stat-value">'+fmtM(equipTotal)+'</div><div class="stat-label">Equipment Cost</div></div>';
  h+='<div class="stat-card"><div class="stat-value">'+fmtM(laborTotal+equipTotal)+'</div><div class="stat-label">Total FA Cost</div></div>';
  h+='</div>';
  if(labor.length>0){
    h+='<h3 style="font-size:14px;font-weight:700;color:#4B3FFF;margin:12px 0 8px">Labor Records</h3>';
    h+='<div class="scroll-table" style="max-height:40vh;margin-bottom:16px"><table><thead><tr>';
    ['Employee','Classification','Date','Reg Hrs','OT Hrs','Rate','Total Cost'].forEach(function(c){h+='<th>'+c+'</th>'});
    h+='</tr></thead><tbody>';
    labor.forEach(function(l){
      h+='<tr>';
      h+='<td style="font-weight:600">'+esc(l.name)+'</td>';
      h+='<td>'+esc(l.class)+'</td>';
      h+='<td>'+fmtDate(l.date)+'</td>';
      h+='<td class="mono text-right">'+l.hrs+'</td>';
      h+='<td class="mono text-right">'+(l.ot||0)+'</td>';
      h+='<td class="mono text-right">'+fmtMon(l.rate)+'</td>';
      h+='<td class="mono text-right fw-bold">'+fmtMon(l.cost)+'</td>';
      h+='</tr>';
    });
    h+='<tr class="total-row"><td colspan="5"><strong>Total</strong></td><td></td><td class="mono text-right fw-bold">'+fmtMon(laborTotal)+'</td></tr>';
    h+='</tbody></table></div>';
  }
  if(equip.length>0){
    h+='<h3 style="font-size:14px;font-weight:700;color:#4B3FFF;margin:12px 0 8px">Equipment Records</h3>';
    h+='<div class="scroll-table" style="max-height:40vh"><table><thead><tr>';
    ['Fleet #','Equipment Type','Date','Hours Used','Rate/hr','Total Cost'].forEach(function(c){h+='<th>'+c+'</th>'});
    h+='</tr></thead><tbody>';
    equip.forEach(function(e){
      h+='<tr>';
      h+='<td style="font-weight:600">'+esc(e.fleet)+'</td>';
      h+='<td>'+esc(e.type)+'</td>';
      h+='<td>'+fmtDate(e.date)+'</td>';
      h+='<td class="mono text-right">'+e.hrs+'</td>';
      h+='<td class="mono text-right">'+fmtMon(e.rate)+'</td>';
      h+='<td class="mono text-right fw-bold">'+fmtMon(e.cost)+'</td>';
      h+='</tr>';
    });
    h+='<tr class="total-row"><td colspan="4"><strong>Total</strong></td><td></td><td class="mono text-right fw-bold">'+fmtMon(equipTotal)+'</td></tr>';
    h+='</tbody></table></div>';
  }
  if(labor.length===0&&equip.length===0){
    h+='<p style="color:#94a3b8;padding:40px;text-align:center">No force account records in date range. Enter labor and equipment entries in the Debris module.</p>';
  }
  h+='</div>';
  return h;
}

function getSortedTickets(){
  var list=DATA.tickets.slice();
  if(!sortCol)return list;
  list.sort(function(a,b){
    var va=a[sortCol]||'',vb=b[sortCol]||'';
    if(sortCol==='cy'||sortCol==='rate'||sortCol==='cost'){
      va=parseFloat(va)||0;vb=parseFloat(vb)||0;
      return sortDir==='asc'?va-vb:vb-va;
    }
    var c=String(va).localeCompare(String(vb),undefined,{numeric:true,sensitivity:'base'});
    return sortDir==='asc'?c:-c;
  });
  return list;
}

function sortTickets(col){
  if(sortCol===col){sortDir=sortDir==='asc'?'desc':'asc'}
  else{sortCol=col;sortDir='asc'}
  render();
}

var mapInitialized=false;
function switchSection(sec){
  currentSection=sec;
  sortCol='';sortDir='asc';
  render();
  if(sec==='map'&&!mapInitialized&&typeof L!=='undefined'&&DATA.mapPoints.length>0){
    mapInitialized=true;
    setTimeout(initMap,100);
  }
}

function initMap(){
  var mapEl=document.getElementById('fema-map');
  if(!mapEl)return;
  var map=L.map('fema-map').setView([27.95,-82.45],11);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{
    attribution:'&copy; OpenStreetMap contributors',maxZoom:18
  }).addTo(map);

  // Disposal site markers (large red)
  DATA.siteCoords.forEach(function(s){
    L.circleMarker([s.lat,s.lng],{
      radius:10,fillColor:'#D33423',color:'#fff',weight:2,fillOpacity:0.9
    }).addTo(map).bindPopup('<strong>'+s.name+'</strong><br>Disposal Site');
  });

  // Ticket pickup markers (clustered)
  var markers=L.markerClusterGroup({
    maxClusterRadius:50,
    spiderfyOnMaxZoom:true,
    showCoverageOnHover:false,
    iconCreateFunction:function(cluster){
      var count=cluster.getChildCount();
      var size=count>500?'large':count>100?'medium':'small';
      var sizes={small:30,medium:40,large:50};
      var colors={small:'#4B3FFF',medium:'#2C01D6',large:'#1a0080'};
      return L.divIcon({
        html:'<div style="background:'+colors[size]+';color:white;border-radius:50%;width:'+sizes[size]+'px;height:'+sizes[size]+'px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)">'+count.toLocaleString()+'</div>',
        className:'',
        iconSize:[sizes[size],sizes[size]]
      });
    }
  });

  DATA.mapPoints.forEach(function(p){
    var marker=L.circleMarker([p.lat,p.lng],{
      radius:5,fillColor:p.color,color:'#fff',weight:1,fillOpacity:0.8
    });
    marker.bindPopup(
      '<strong>Ticket #'+esc(p.tn)+'</strong><br>'+
      'Type: '+esc(p.dt)+'<br>'+
      'CY: '+esc(p.cy)+'<br>'+
      'Sub: '+esc(p.sub)+'<br>'+
      'Site: '+esc(p.site)
    );
    markers.addLayer(marker);
  });
  map.addLayer(markers);

  // Fit bounds
  if(DATA.mapPoints.length>0){
    var bounds=L.latLngBounds(DATA.mapPoints.map(function(p){return[p.lat,p.lng]}));
    DATA.siteCoords.forEach(function(s){bounds.extend([s.lat,s.lng])});
    map.fitBounds(bounds,{padding:[20,20]});
  }
}

function bindEvents(){
  // Ticket search
  var searchEl=document.getElementById('search');
  if(searchEl){
    searchEl.addEventListener('input',function(e){
      var q=e.target.value.toLowerCase();
      var rows=document.querySelectorAll('.ticket-row');
      var visible=0;
      rows.forEach(function(row){
        var text=row.getAttribute('data-search-text')||'';
        var show=text.includes(q);
        row.style.display=show?'':'none';
        var detail=row.nextElementSibling;
        if(detail){var d=detail.querySelector('.detail');if(d)d.classList.remove('open')}
        if(show)visible++;
      });
      var vc=document.getElementById('visible-count');
      if(vc)vc.textContent=visible+' of '+DATA.tickets.length+' tickets';
    });
  }
  // Truck search
  var truckSearchEl=document.getElementById('truck-search');
  if(truckSearchEl){
    truckSearchEl.addEventListener('input',function(e){
      var q=e.target.value.toLowerCase();
      var rows=document.querySelectorAll('.truck-row');
      rows.forEach(function(row){
        var text=row.getAttribute('data-search-text')||'';
        row.style.display=text.includes(q)?'':'none';
        var detail=row.nextElementSibling;
        if(detail){var d=detail.querySelector('.detail');if(d)d.classList.remove('open')}
      });
    });
  }
  // Expand/collapse + scan overlay close — bind once only
  if(!_clickBound){
    _clickBound=true;
    document.addEventListener('click',function(e){
      var row=e.target.closest('.expandable');
      if(!row)return;
      var id=row.getAttribute('data-id');
      if(!id)return;
      var detail=document.getElementById('detail-'+id);
      if(detail)detail.classList.toggle('open');
    });
    document.addEventListener('keydown',function(e){
      if(e.key==='Escape')closeScan();
    });
  }
}

render();
</${'script'}>
</body>
</html>`;

      // Add the HTML guide to the ZIP
      zip.file('index.html', html);


      // ── Generate the ZIP ──────────────────────────────────
      setExportStatus('Compressing ZIP file...');
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEMA-Package-${(pkg.femaDisasterNumber || 'DR').replace(/[^a-zA-Z0-9-]/g, '')}-${(pkg.pwReference || 'PW').replace(/[^a-zA-Z0-9-]/g, '')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const sizeMB = (blob.size / 1024 / 1024).toFixed(1);
      notifications.show({ message: `FEMA Package exported (${sizeMB} MB) with ${fetchedDocs.filter(d => d.hasFile).length} documents`, color: 'var(--color-success)' });
      updateFemaPackage(pkg.id, { exportedAt: new Date().toISOString() }).catch(() => {});
    } catch (err) {
      console.error('[FEMA Package] Export failed:', err);
      notifications.show({ message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'var(--color-error)' });
    } finally {
      setHtmlExporting(false);
      setExportStatus('');
    }
  };

  // ── Helpers ───────────────────────────────────────────────
  const fmtMoney = (v: number) => v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';

  const thStyle = (col: string): React.CSSProperties => ({
    padding: '8px 12px',
    textAlign: 'left' as const,
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    background: '#f8fafc',
    cursor: 'pointer',
    userSelect: 'none' as const,
    color: sortField === col ? '#4B3FFF' : '#64748b',
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  });

  const checkStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 0', fontSize: '0.82rem', color: '#1e293b',
  };
  const checkIcon = <IconCheck size={14} color="var(--color-success)" style={{ flexShrink: 0 }} />;

  // ── Render ────────────────────────────────────────────────
  return (
    <div>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={3} c="black">FEMA Reimbursement Packages</Title>
          {ticketsLoading && <Text size="xs" c="dimmed">Loading all tickets from API...</Text>}
          {ticketsLoaded && <Text size="xs" c="dimmed">{allTickets.length.toLocaleString()} tickets loaded across all contractors</Text>}
        </div>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingFemaPackages} onClick={() => loadFemaPackages()}>Refresh</Button>
          {canEdit && (
            <Button size="xs" variant="light" color={indexesLoaded && filteredTicketPageIndex ? 'var(--color-success)' : '#C68700'}
              leftSection={<IconDatabase size={14} />}
              loading={indexBuilding}
              onClick={buildDocumentIndexes}
              title={filteredTicketPageIndex ? `Index loaded: ${Object.keys(filteredTicketPageIndex).length} ticket pages mapped` : 'Build page-level indexes for one-click document links in exports'}>
              {indexBuilding ? (indexStatus.length > 40 ? indexStatus.slice(0, 37) + '...' : indexStatus) : filteredTicketPageIndex ? `Indexes Ready (${Object.keys(filteredTicketPageIndex).length})` : 'Build Indexes'}
            </Button>
          )}
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Package</Button>}
        </Group>
      </Group>

      {/* Index building progress */}
      {indexBuilding && (
        <Paper p="sm" radius="md" withBorder mb="xs" style={{ background: '#f0f4ff' }}>
          <Group gap="sm" mb={4}>
            <IconDatabase size={14} color="#4B3FFF" />
            <Text size="xs" fw={600} c="#4B3FFF">Building Document Indexes</Text>
          </Group>
          <Text size="xs" c="dimmed" mb={4}>{indexStatus}</Text>
          <Progress value={indexProgress} size="sm" color="#4B3FFF" />
        </Paper>
      )}

      <Text size="xs" c="dimmed" mb="xs">{sorted.length} package{sorted.length !== 1 ? 's' : ''}</Text>

      {/* ── Package List Table ────────────────────────────── */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={thStyle('packageName')} onClick={() => toggleSort('packageName')}>
                  Package Name{sortIndicator('packageName')}
                </th>
                <th style={thStyle('femaDisasterNumber')} onClick={() => toggleSort('femaDisasterNumber')}>
                  FEMA Disaster #{sortIndicator('femaDisasterNumber')}
                </th>
                <th style={thStyle('pwReference')} onClick={() => toggleSort('pwReference')}>
                  PW Reference{sortIndicator('pwReference')}
                </th>
                <th style={thStyle('dateRangeStart')} onClick={() => toggleSort('dateRangeStart')}>
                  Date Range{sortIndicator('dateRangeStart')}
                </th>
                <th style={{ ...thStyle('contractorIds'), cursor: 'default', color: '#64748b' }}>
                  Contractors
                </th>
                <th style={{ ...thStyle('totalCubicYards'), textAlign: 'right' }} onClick={() => toggleSort('totalCubicYards')}>
                  Total CY{sortIndicator('totalCubicYards')}
                </th>
                <th style={{ ...thStyle('totalCost'), textAlign: 'right' }} onClick={() => toggleSort('totalCost')}>
                  Total Cost{sortIndicator('totalCost')}
                </th>
                <th style={thStyle('status')} onClick={() => toggleSort('status')}>
                  Status{sortIndicator('status')}
                </th>
                <th style={thStyle('version')} onClick={() => toggleSort('version')}>
                  Ver{sortIndicator('version')}
                </th>
                <th style={{ ...thStyle(''), textAlign: 'right', cursor: 'default', color: '#64748b' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingFemaPackages ? 'Loading...' : 'No FEMA packages yet. Create one to start assembling documentation.'}
                </td></tr>
              )}
              {sorted.map(pkg => {
                const isExpanded = expandedId === pkg.id;
                const detail = isExpanded ? getPackageDetail(pkg) : null;

                return (
                  <React.Fragment key={pkg.id}>
                    <tr style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer', background: isExpanded ? '#f8fafc' : undefined }}
                      onClick={() => setExpandedId(isExpanded ? null : pkg.id)}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{pkg.packageName || `#${pkg.id}`}</td>
                      <td style={{ padding: '8px 12px' }}>{pkg.femaDisasterNumber || '-'}</td>
                      <td style={{ padding: '8px 12px' }}>{pkg.pwReference || '-'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <Text size="xs">{fmtDate(pkg.dateRangeStart)} — {fmtDate(pkg.dateRangeEnd)}</Text>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Text size="xs">{pkg.contractorIds.map(id => contractorName(id)).join(', ') || '-'}</Text>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {pkg.totalCubicYards > 0 ? pkg.totalCubicYards.toLocaleString(undefined, { minimumFractionDigits: 1 }) : '-'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                        {fmtMoney(pkg.totalCost)}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <Badge size="sm" color={STATUS_COLORS[pkg.status] ?? '#939598'}>{STATUS_LABELS[pkg.status] || pkg.status}</Badge>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>v{pkg.version || 1}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        <Group gap={4} justify="flex-end">
                          <ActionIcon size="sm" variant="subtle" onClick={() => setExpandedId(isExpanded ? null : pkg.id)} title={isExpanded ? 'Collapse' : 'Expand'}>
                            {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="var(--color-success)"
                            onClick={() => exportZipBundle(pkg)} title="Export ZIP Bundle"
                            loading={exporting === pkg.id}>
                            <IconArchive size={14} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="#3b82f6"
                            onClick={() => generatePDF(pkg)} title="Export PDF Only">
                            <IconFileTypePdf size={14} />
                          </ActionIcon>
                          {canEdit && <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(pkg)} title="Edit">
                            <IconPencil size={14} />
                          </ActionIcon>}
                          {canEdit && <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(pkg)} title="Delete">
                            <IconTrash size={14} />
                          </ActionIcon>}
                        </Group>
                      </td>
                    </tr>

                    {/* ── Expanded Detail View ────────────── */}
                    {isExpanded && detail && (
                      <tr>
                        <td colSpan={10} style={{ padding: 0, background: '#fafbfc' }}>
                          <div style={{ padding: '16px 24px', borderBottom: '2px solid #cbd5e1' }}>
                            {/* Header + actions */}
                            <Group justify="space-between" mb="md">
                              <div>
                                <Text size="lg" fw={700}>FEMA Reimbursement Package — {pkg.femaDisasterNumber || 'No DR#'}</Text>
                                <Text size="sm" c="dimmed">
                                  PW Reference: {pkg.pwReference || '-'} | Period: {fmtDate(pkg.dateRangeStart)} — {fmtDate(pkg.dateRangeEnd)} | {pkg.contractorIds.map(id => contractorName(id)).join(', ')}
                                </Text>
                              </div>
                              <Group gap="xs">
                                <Select
                                  data={STATUS_OPTIONS}
                                  value={pkg.status}
                                  onChange={v => v && handleStatusChange(pkg, v)}
                                  size="xs"
                                  w={180}
                                />
                                <Button size="xs" leftSection={<IconFileTypePdf size={14} />} onClick={() => generatePDF(pkg)}
                                  disabled={!ticketsLoaded} title={ticketsLoaded ? '' : 'Waiting for tickets to load...'}>
                                  {ticketsLoading ? 'Loading tickets...' : 'Narrative PDF'}
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconArchive size={14} />}
                                  onClick={() => exportZipBundle(pkg)} loading={exporting === pkg.id}
                                  disabled={!ticketsLoaded}>
                                  Download Full ZIP Bundle
                                </Button>
                                <Button size="xs" variant="filled" color="#4B3FFF" leftSection={<IconArchive size={16} />}
                                  onClick={() => generateFEMAPackage(pkg)} loading={htmlExporting}
                                  disabled={!ticketsLoaded}>
                                  {htmlExporting ? (exportStatus || 'Building package...') : ticketsLoading ? 'Loading tickets...' : 'Export Complete FEMA Package'}
                                </Button>
                              </Group>
                            </Group>
                            {exportStatus && (
                              <Text size="xs" c="dimmed" mb="xs" style={{ fontStyle: 'italic' }}>{exportStatus}</Text>
                            )}

                            {/* ── Package Contents Checklist ──── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">PACKAGE CONTENTS</Text>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 24px' }}>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.totalTickets.toLocaleString()}</strong> Load Tickets ({fmtMoney(detail.clinTotalCost)} total across {detail.clinSummary.length} CLIN codes)</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.invoices.length}</strong> Contractor Invoice{detail.invoices.length !== 1 ? 's' : ''} ({fmtMoney(detail.invoiceTotalCost)})</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.faLabor.length}</strong> Force Account Labor records ({fmtMoney(detail.faLaborTotal)})</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.faEquip.length}</strong> Force Account Equipment records ({fmtMoney(detail.faEquipTotal)})</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span>Monitor Reconciliation: {fmtMoney(detail.reconVariance)} variance ({detail.reconVariancePct.toFixed(1)}%)</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.truckCertCount}</strong> Truck Certifications on file</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.sites.length}</strong> Disposal Sites documented</span>
                                </div>
                                <div style={checkStyle}>
                                  {checkIcon}
                                  <span><strong>{detail.totalDocs}</strong> Documentation Archive documents</span>
                                </div>
                              </div>
                            </Paper>

                            {/* ── Source Documents ────────────── */}
                            {docCache.length > 0 && (
                              <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                                <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">SOURCE DOCUMENTS (click to view)</Text>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {docCache.slice(0, 20).map(doc => (
                                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem' }}>
                                      <span style={{ color: '#64748b' }}>{doc.documentType?.replace(/_/g, ' ').toUpperCase() || 'DOC'}</span>
                                      <span style={{ fontWeight: 500 }}>{doc.title || `Document #${doc.id}`}</span>
                                      {doc.fileId && (
                                        <Button size="xs" variant="subtle" compact
                                          leftSection={<IconEye size={12} />}
                                          onClick={() => viewDocument(doc)}
                                          style={{ padding: '0 6px', height: 22, fontSize: '0.75rem' }}>
                                          View
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                  {docCache.length > 20 && (
                                    <Text size="xs" c="dimmed">... and {docCache.length - 20} more documents in Documentation Archive</Text>
                                  )}
                                </div>
                              </Paper>
                            )}

                            {/* ── CLIN Summary Table ─────────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">TICKET SUMMARY BY CLIN</Text>
                              {detail.clinSummary.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>CLIN Code</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Description</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Qty</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Total CY</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Rate</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.clinSummary.map(row => (
                                        <tr key={row.clinCode} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                          <td style={{ padding: '6px 8px', fontWeight: 500 }}>{clinLabel(row.clinCode)}</td>
                                          <td style={{ padding: '6px 8px' }}>{row.description}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.count.toLocaleString()}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtCY(row.totalCY)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{row.rate ? fmtMon(parseFloat(row.rate) || 0) : '-'}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMon(row.totalCost)}</td>
                                        </tr>
                                      ))}
                                      <tr style={{ borderTop: '2px solid #cbd5e1', background: '#f0f4f8' }}>
                                        <td style={{ padding: '6px 8px', fontWeight: 700 }} colSpan={2}>TOTAL</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{detail.totalTickets.toLocaleString()}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtCY(detail.clinSummary.reduce((s, r) => s + r.totalCY, 0))}</td>
                                        <td style={{ padding: '6px 8px' }}></td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMon(detail.clinTotalCost)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              ) : detail.ticketsByType.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Debris Type</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Tickets</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Total CY</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Avg CY/Ticket</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.ticketsByType.map(row => (
                                        <tr key={row.type} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                          <td style={{ padding: '6px 8px' }}>{row.label}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{row.count}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtCY(row.totalCY)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtCY(row.avgCY)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <Text size="xs" c="dimmed" mt="xs">No CLIN codes found on tickets. Showing debris type breakdown instead.</Text>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No tickets found for this period.</Text>
                              )}
                            </Paper>

                            {/* ── Per-Ticket Document Completeness ── */}
                            {(() => {
                              const ticketList = detail.matchedTickets;
                              const withHaulDoc = ticketList.filter(t => !!decodeTicketDocs(t.notes).haulTicketDocId).length;
                              const withMonitorDoc = ticketList.filter(t => !!decodeTicketDocs(t.notes).monitoringLogDocId).length;
                              if (ticketList.length === 0) return null;
                              return (
                                <Paper p="sm" radius="sm" withBorder mb="sm" style={{ background: '#f8fafc' }}>
                                  <Text size="xs" fw={600} mb={4}>Per-Ticket Document Completeness</Text>
                                  <Group gap={16}>
                                    <div>
                                      <Text size="xs" c="dimmed">Haul Tickets</Text>
                                      <Text size="sm" fw={600} style={{ color: withHaulDoc >= ticketList.length * 0.9 ? 'var(--color-success)' : '#C68700' }}>
                                        {withHaulDoc} / {ticketList.length}
                                      </Text>
                                    </div>
                                    <div>
                                      <Text size="xs" c="dimmed">Monitoring Logs</Text>
                                      <Text size="sm" fw={600} style={{ color: withMonitorDoc >= ticketList.length * 0.9 ? 'var(--color-success)' : '#C68700' }}>
                                        {withMonitorDoc} / {ticketList.length}
                                      </Text>
                                    </div>
                                    {withHaulDoc < ticketList.length * 0.5 && (
                                      <Text size="xs" c="orange">Tip: Use Ticket 360 to attach haul ticket PDFs per ticket.</Text>
                                    )}
                                  </Group>
                                </Paper>
                              );
                            })()}

                            {/* ── Disposal Site Summary ──────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">DISPOSAL SITE SUMMARY</Text>
                              {detail.sites.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Site Name</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Type</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Tickets Received</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>CY Received</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.sites.map(s => (
                                        <tr key={s.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                          <td style={{ padding: '6px 8px' }}>{s.siteName || '-'}</td>
                                          <td style={{ padding: '6px 8px' }}><Badge size="xs" variant="light">{(s.siteType || '-').toUpperCase()}</Badge></td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{s.ticketCount.toLocaleString()}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{s.receivedCY.toLocaleString(undefined, { minimumFractionDigits: 1 })}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No disposal sites used during this period.</Text>
                              )}
                            </Paper>

                            {/* ── Invoice Summary ────────────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">INVOICE SUMMARY</Text>
                              {detail.invoices.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Invoice #</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Contractor</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Period</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Amount</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.invoices.map(inv => (
                                        <tr key={inv.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                          <td style={{ padding: '6px 8px' }}>{inv.invoiceNumber || `#${inv.id}`}</td>
                                          <td style={{ padding: '6px 8px' }}>{contractorName(inv.contractorId)}</td>
                                          <td style={{ padding: '6px 8px' }}>{fmtDate(inv.periodStart)} - {fmtDate(inv.periodEnd)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(parseFloat(inv.totalAmount) || 0)}</td>
                                          <td style={{ padding: '6px 8px' }}>
                                            <Badge size="xs" color={inv.status === 'approved' ? 'var(--color-success)' : inv.status === 'draft' ? '#939598' : '#3b82f6'}>{inv.status}</Badge>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No invoices found for this period.</Text>
                              )}
                            </Paper>

                            {/* ── Sub-Contractor Invoices ───────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">SUB-CONTRACTOR INVOICES</Text>
                              {(() => {
                                const contractorSubInvs = subInvoices.filter(s =>
                                  pkg.contractorIds?.includes(String(s.primeContractorId)) ||
                                  pkg.contractorIds?.includes(s.primeContractorId)
                                );
                                if (contractorSubInvs.length === 0) return (
                                  <div style={{ padding: '8px 0', color: '#94a3b8', fontSize: 13 }}>
                                    No sub-contractor invoices linked. Add them via Invoices → Sub-Contractor Invoices tab.
                                  </div>
                                );
                                return (
                                  <div>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            {['Sub Name', 'Invoice #', 'Date', 'Amount', 'Work Type', 'Doc'].map(h => (
                                              <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>{h}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {contractorSubInvs.map(sub => (
                                            <tr key={sub.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                              <td style={{ padding: '6px 10px', fontWeight: 500 }}>{sub.subName || '-'}</td>
                                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 12 }}>{sub.subInvoiceNumber || '-'}</td>
                                              <td style={{ padding: '6px 10px', color: '#64748b' }}>{sub.subInvoiceDate ? sub.subInvoiceDate.slice(0, 10) : '-'}</td>
                                              <td style={{ padding: '6px 10px', fontWeight: 600 }}>{sub.subAmount > 0 ? `$${sub.subAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}</td>
                                              <td style={{ padding: '6px 10px', color: '#64748b' }}>{sub.workType || '-'}</td>
                                              <td style={{ padding: '6px 10px' }}>
                                                {sub.documentId ? (
                                                  <Badge size="xs" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>PDF</Badge>
                                                ) : (
                                                  <Badge size="xs" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>Missing</Badge>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <Text size="xs" c="dimmed" mt={4}>
                                      Total sub-contractor: ${contractorSubInvs.reduce((s, i) => s + i.subAmount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </Text>
                                  </div>
                                );
                              })()}
                            </Paper>

                            {/* ── Reconciliation Summary ─────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">RECONCILIATION SUMMARY</Text>
                              {detail.reconciliations.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Contractor</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Period</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Invoiced</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Verified</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Variance %</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.reconciliations.map(rec => (
                                        <tr key={rec.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                          <td style={{ padding: '6px 8px' }}>{contractorName(rec.contractorId)}</td>
                                          <td style={{ padding: '6px 8px' }}>{fmtDate(rec.periodStart)} - {fmtDate(rec.periodEnd)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(rec.totalInvoiced)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(rec.totalVerified)}</td>
                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: Math.abs(rec.variancePercent) > 5 ? 'var(--color-error)' : 'inherit' }}>
                                            {rec.variancePercent.toFixed(1)}%
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No reconciliations found for this period.</Text>
                              )}
                            </Paper>

                            {/* ── Force Account Labor ────────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Group justify="space-between" mb="xs">
                                <Text size="sm" fw={700} c="var(--color-primary-700)">
                                  FORCE ACCOUNT — LABOR
                                  {detail.faLabor.length > 0 && (
                                    <Text span size="xs" c="dimmed" ml={8}>{detail.faLabor.length} records · {fmtMoney(detail.faLaborTotal)}</Text>
                                  )}
                                </Text>
                                {detail.faLabor.length > 0 && (
                                  <Button size="xs" variant="light" leftSection={<IconDownload size={12} />}
                                    onClick={() => {
                                      downloadCSV(generateLaborCSV(detail.faLabor), `fa_labor_${detail.faLabor.length}_records.csv`);
                                      notifications.show({ message: 'Labor CSV downloaded', color: 'var(--color-success)' });
                                    }}>
                                    Download CSV
                                  </Button>
                                )}
                              </Group>
                              {detail.faLabor.length > 0 ? (
                                <div style={{ overflowX: 'auto', maxHeight: 320, overflowY: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Employee</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Classification</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Date</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Reg Hrs</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>OT Hrs</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Rate</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Total</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b' }}>Docs</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.faLabor.map((l, i) => {
                                        const faDocs = decodeFaDocs(l.notes);
                                        const hasTS = !!faDocs.timesheetDocId;
                                        const hasPR = !!faDocs.payrollDocId;
                                        return (
                                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '6px 8px', fontWeight: 500 }}>{l.employeeName || '—'}</td>
                                            <td style={{ padding: '6px 8px', color: '#64748b', fontSize: '0.75rem' }}>{l.classification || '—'}</td>
                                            <td style={{ padding: '6px 8px' }}>{fmtDate(l.dateWorked)}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{l.hoursRegular > 0 ? l.hoursRegular.toFixed(1) : '—'}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: l.hoursOvertime > 0 ? '#d97706' : '#94a3b8' }}>
                                              {l.hoursOvertime > 0 ? l.hoursOvertime.toFixed(1) : '—'}
                                            </td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{l.hourlyRate > 0 ? fmtMon(l.hourlyRate) : '—'}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMon(l.totalCost)}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                              <Group gap={3} justify="center">
                                                <Badge size="xs" style={{ background: hasTS ? '#dcfce7' : '#fef3c7', color: hasTS ? '#166534' : '#92400e', border: `1px solid ${hasTS ? '#bbf7d0' : '#fde68a'}` }}>TS</Badge>
                                                <Badge size="xs" style={{ background: hasPR ? '#dcfce7' : '#fef3c7', color: hasPR ? '#166534' : '#92400e', border: `1px solid ${hasPR ? '#bbf7d0' : '#fde68a'}` }}>PR</Badge>
                                              </Group>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #cbd5e1', background: '#f0f4f8' }}>
                                        <td colSpan={3} style={{ padding: '6px 8px', fontWeight: 700 }}>TOTAL</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                                          {detail.faLabor.reduce((s, l) => s + l.hoursRegular, 0).toFixed(1)}
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#d97706' }}>
                                          {detail.faLabor.reduce((s, l) => s + l.hoursOvertime, 0).toFixed(1)}
                                        </td>
                                        <td />
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMon(detail.faLaborTotal)}</td>
                                        <td />
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No force account labor records found for this period.</Text>
                              )}
                            </Paper>

                            {/* ── Force Account Equipment ────── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Group justify="space-between" mb="xs">
                                <Text size="sm" fw={700} c="var(--color-primary-700)">
                                  FORCE ACCOUNT — EQUIPMENT
                                  {detail.faEquip.length > 0 && (
                                    <Text span size="xs" c="dimmed" ml={8}>{detail.faEquip.length} records · {fmtMoney(detail.faEquipTotal)}</Text>
                                  )}
                                </Text>
                                {detail.faEquip.length > 0 && (
                                  <Button size="xs" variant="light" leftSection={<IconDownload size={12} />}
                                    onClick={() => {
                                      downloadCSV(generateEquipmentCSV(detail.faEquip), `fa_equipment_${detail.faEquip.length}_records.csv`);
                                      notifications.show({ message: 'Equipment CSV downloaded', color: 'var(--color-success)' });
                                    }}>
                                    Download CSV
                                  </Button>
                                )}
                              </Group>
                              {detail.faEquip.length > 0 ? (
                                <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                      <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Fleet #</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Equipment Type</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748b' }}>Date</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Hours</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Rate/Hr</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>Total</th>
                                        <th style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b' }}>Docs</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {detail.faEquip.map((e, i) => {
                                        const faDocs = decodeFaDocs(e.notes);
                                        const hasRate = !!faDocs.femaRateDocId;
                                        return (
                                          <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '6px 8px', fontWeight: 500 }}>{e.fleetNumber || '—'}</td>
                                            <td style={{ padding: '6px 8px', color: '#64748b', fontSize: '0.75rem' }}>{e.equipmentType || '—'}</td>
                                            <td style={{ padding: '6px 8px' }}>{fmtDate(e.dateUsed)}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{e.hoursUsed > 0 ? e.hoursUsed.toFixed(1) : '—'}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{e.hourlyRate > 0 ? fmtMon(e.hourlyRate) : '—'}</td>
                                            <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMon(e.totalCost)}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                              <Badge size="xs" style={{ background: hasRate ? '#dcfce7' : '#fef3c7', color: hasRate ? '#166534' : '#92400e', border: `1px solid ${hasRate ? '#bbf7d0' : '#fde68a'}` }}>Rate</Badge>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #cbd5e1', background: '#f0f4f8' }}>
                                        <td colSpan={3} style={{ padding: '6px 8px', fontWeight: 700 }}>TOTAL</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                                          {detail.faEquip.reduce((s, e) => s + e.hoursUsed, 0).toFixed(1)}
                                        </td>
                                        <td />
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtMon(detail.faEquipTotal)}</td>
                                        <td />
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              ) : (
                                <Text size="xs" c="dimmed">No force account equipment records found for this period.</Text>
                              )}
                            </Paper>

                            {/* ── Pre-Submission Audit ──────── */}
                            {(() => {
                              const auditIssues: { type: 'warning' | 'error' | 'info'; message: string; detail: string }[] = [];

                              // Missing disposal sites
                              if (detail.sites.length === 0 && detail.totalTickets > 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: 'No disposal sites linked to this package',
                                  detail: 'Ensure disposal site records exist and tickets reference them.',
                                });
                              }

                              // Reconciliation missing
                              if (detail.reconciliations.length === 0 && detail.invoices.length > 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: 'No monitor reconciliations found',
                                  detail: 'FEMA requires independent monitor reconciliation for each invoice period.',
                                });
                              }

                              // High recon variance
                              if (detail.reconciliations.length > 0 && Math.abs(detail.reconVariancePct) > 5) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: `Reconciliation variance exceeds 5% (${detail.reconVariancePct.toFixed(1)}%)`,
                                  detail: 'FEMA may flag reconciliations with variance above 5%. Prepare a written explanation.',
                                });
                              }

                              // FA labor missing timesheet docs
                              const laborMissingTS = detail.faLabor.filter(l => !decodeFaDocs(l.notes).timesheetDocId);
                              if (laborMissingTS.length > 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: `${laborMissingTS.length} FA labor record${laborMissingTS.length !== 1 ? 's' : ''} missing timesheet document`,
                                  detail: 'FA labor rows need timesheet PDFs attached before FEMA submission.',
                                });
                              }

                              // FA labor missing payroll docs
                              const laborMissingPR = detail.faLabor.filter(l => !decodeFaDocs(l.notes).payrollDocId);
                              if (laborMissingPR.length > 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: `${laborMissingPR.length} FA labor record${laborMissingPR.length !== 1 ? 's' : ''} missing payroll document`,
                                  detail: 'FA labor rows need payroll register PDFs attached for FEMA reimbursement.',
                                });
                              }

                              // FA equipment missing rate docs
                              const equipMissingRate = detail.faEquip.filter(e => !decodeFaDocs(e.notes).femaRateDocId);
                              if (equipMissingRate.length > 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: `${equipMissingRate.length} FA equipment record${equipMissingRate.length !== 1 ? 's' : ''} missing FEMA rate document`,
                                  detail: 'FA equipment rows need FEMA rate schedule docs attached.',
                                });
                              }

                              // No evidence documents
                              if (detail.totalDocs === 0) {
                                auditIssues.push({
                                  type: 'warning',
                                  message: 'No documentation archive documents found',
                                  detail: 'Upload supporting documents (invoices, reconciliations, site authorizations) to the Documentation Archive.',
                                });
                              }

                              // Contractors using subs but with no sub invoice records
                              const contractorIdsInPkg = pkg.contractorIds?.map((c: string) => String(c)) ?? [];
                              const primeContractorsWithSubs = subInvoices.filter(s => contractorIdsInPkg.includes(String(s.primeContractorId)));
                              if (contractorIdsInPkg.length > 0 && primeContractorsWithSubs.length === 0) {
                                auditIssues.push({
                                  type: 'info',
                                  message: 'No sub-contractor invoices recorded for any prime contractor',
                                  detail: 'If primes used subcontractors, add sub invoices via Invoices → Sub-Contractor Invoices tab.',
                                });
                              }

                              // ── TIER 1: Invoice ↔ Ticket CY reconciliation ──────
                              // For each invoice that has linked ticket IDs, compare the invoice's
                              // stated CY against the sum of CY from those tickets.
                              const ticketById = new Map(detail.matchedTickets.map(t => [String(t.id), t]));
                              const cyMismatches: { invoiceNumber: string; invoicedCY: number; ticketCY: number; diffPct: number }[] = [];
                              detail.invoices.forEach(inv => {
                                const invoicedCY = parseFloat(inv.totalCubicYards || '0');
                                if (!invoicedCY) return; // no CY on invoice, skip
                                let ticketIds: string[] = [];
                                try { ticketIds = JSON.parse(inv.ticketIds || '[]'); } catch { /* */ }
                                if (ticketIds.length === 0) return; // no linked tickets, skip
                                const ticketCY = ticketIds.reduce((s, id) => {
                                  const t = ticketById.get(String(id));
                                  return s + (t ? parseFloat(t.calculatedCubicYards || '0') : 0);
                                }, 0);
                                if (ticketCY === 0) return;
                                const diffPct = Math.abs((invoicedCY - ticketCY) / ticketCY) * 100;
                                if (diffPct > 2) { // allow 2% rounding tolerance
                                  cyMismatches.push({ invoiceNumber: inv.invoiceNumber, invoicedCY, ticketCY, diffPct });
                                }
                              });
                              if (cyMismatches.length > 0) {
                                cyMismatches.forEach(mm => {
                                  auditIssues.push({
                                    type: 'error',
                                    message: `Invoice ${mm.invoiceNumber}: CY mismatch — invoiced ${mm.invoicedCY.toLocaleString(undefined, { maximumFractionDigits: 1 })} CY vs ${mm.ticketCY.toLocaleString(undefined, { maximumFractionDigits: 1 })} CY from tickets (${mm.diffPct.toFixed(1)}% off)`,
                                    detail: 'FEMA requires the billed quantity to match the sum of underlying load ticket quantities. Reconcile the invoice or re-link the correct tickets.',
                                  });
                                });
                              } else if (detail.invoices.length > 0) {
                                // Invoices present but none have linked tickets — flag as a gap
                                const invoicesWithTickets = detail.invoices.filter(inv => {
                                  try { return JSON.parse(inv.ticketIds || '[]').length > 0; } catch { return false; }
                                });
                                if (invoicesWithTickets.length === 0) {
                                  auditIssues.push({
                                    type: 'warning',
                                    message: `${detail.invoices.length} invoice${detail.invoices.length !== 1 ? 's' : ''} have no linked load tickets`,
                                    detail: 'Link load tickets to invoices in the Invoice editor so quantity can be reconciled against billed amounts.',
                                  });
                                }
                              }

                              // ── TIER 1: Rate compliance check ────────────────────
                              // For each ticket, verify extendedCost ≈ calculatedCubicYards × transactionRate.
                              // Flag tickets where the math doesn't match (>2% tolerance).
                              const rateErrors: { ticketNumber: string; expected: number; actual: number; diffPct: number }[] = [];
                              detail.matchedTickets.forEach(t => {
                                const cy   = parseFloat(t.calculatedCubicYards || '0');
                                const rate = parseFloat(t.transactionRate || '0');
                                const cost = parseFloat(t.extendedCost || '0');
                                if (!cy || !rate || !cost) return; // skip if any value missing
                                const expected = cy * rate;
                                const diffPct = Math.abs((cost - expected) / expected) * 100;
                                if (diffPct > 2) {
                                  rateErrors.push({ ticketNumber: t.ticketNumber || String(t.id), expected, actual: cost, diffPct });
                                }
                              });
                              if (rateErrors.length > 0) {
                                const sample = rateErrors.slice(0, 3);
                                auditIssues.push({
                                  type: 'error',
                                  message: `${rateErrors.length} ticket${rateErrors.length !== 1 ? 's' : ''} have extended cost that doesn't match CY × rate`,
                                  detail: `Examples: ${sample.map(e => `#${e.ticketNumber} (expected $${e.expected.toFixed(2)}, billed $${e.actual.toFixed(2)}, ${e.diffPct.toFixed(1)}% off)`).join('; ')}. FEMA will recalculate and reduce payment to the computed amount.`,
                                });
                              }

                              // ── TIER 1: Truck certification coverage ─────────────
                              // Find every unique truck number in matched tickets, then check how
                              // many of those trucks have a cert record in the truck registry.
                              const trucksInPkg = new Set(detail.matchedTickets.map(t => t.truckNumber).filter(Boolean));
                              if (trucksInPkg.size > 0) {
                                const certifiedTruckNums = new Set(
                                  detail.trucks
                                    .filter(t => t.certStatus === 'certified' || t.certStatus === 'active' || !!t.certifiedDate)
                                    .map(t => t.truckNumber)
                                );
                                const uncertified = [...trucksInPkg].filter(n => !certifiedTruckNums.has(n));
                                if (uncertified.length > 0) {
                                  auditIssues.push({
                                    type: 'error',
                                    message: `${uncertified.length} of ${trucksInPkg.size} trucks in this package have no certification on file`,
                                    detail: `Uncertified trucks: ${uncertified.slice(0, 5).join(', ')}${uncertified.length > 5 ? ` +${uncertified.length - 5} more` : ''}. FEMA requires a stamped capacity cert for every truck. Upload certs via the Package Processor (Truck Cert document type).`,
                                  });
                                }
                              }

                              // Per-ticket document completeness (any missing = warn, not just >50%)
                              const pkgTickets = detail.matchedTickets;
                              if (pkgTickets.length > 0) {
                                const missingHaul = pkgTickets.filter(t => !decodeTicketDocs(t.notes).haulTicketDocId).length;
                                if (missingHaul > 0) {
                                  auditIssues.push({
                                    type: missingHaul > pkgTickets.length * 0.2 ? 'warning' : 'info',
                                    message: `${missingHaul} of ${pkgTickets.length} tickets missing haul ticket document`,
                                    detail: 'Attach haul ticket PDFs via Ticket 360 for each load ticket.',
                                  });
                                }
                              }

                              const errorCount = auditIssues.filter(i => i.type === 'error').length;
                              const warnCount = auditIssues.filter(i => i.type === 'warning').length;
                              const infoCount = auditIssues.filter(i => i.type === 'info').length;
                              const allClear = errorCount === 0 && warnCount === 0;

                              return (
                                <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: allClear ? '#f0fdf4' : '#fffbeb', borderColor: allClear ? '#bbf7d0' : '#fde68a' }}>
                                  <Group justify="space-between" mb={allClear && infoCount === 0 ? 0 : 'xs'}>
                                    <Text size="sm" fw={700} style={{ color: allClear ? 'var(--color-success)' : '#92400e' }}>
                                      PRE-SUBMISSION AUDIT
                                    </Text>
                                    {allClear && infoCount === 0 ? (
                                      <Badge size="sm" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>All checks passed</Badge>
                                    ) : (
                                      <Badge size="sm" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                                        {errorCount > 0 ? `${errorCount} error${errorCount !== 1 ? 's' : ''}, ` : ''}{warnCount > 0 ? `${warnCount} warning${warnCount !== 1 ? 's' : ''}` : ''}{infoCount > 0 && (warnCount > 0 || errorCount > 0) ? ', ' : ''}{infoCount > 0 ? `${infoCount} info` : ''}
                                      </Badge>
                                    )}
                                  </Group>
                                  {allClear && infoCount === 0 && (
                                    <Text size="xs" c="dimmed">No issues found. This package appears ready for FEMA submission.</Text>
                                  )}
                                  {auditIssues.length > 0 && (
                                    <Stack gap={6}>
                                      {auditIssues.map((issue, idx) => (
                                        <div key={idx} style={{
                                          padding: '8px 10px',
                                          borderRadius: 6,
                                          background: issue.type === 'error' ? '#fef2f2' : issue.type === 'info' ? '#eff6ff' : '#fffbeb',
                                          borderLeft: `3px solid ${issue.type === 'error' ? '#D33423' : issue.type === 'info' ? '#3b82f6' : '#d97706'}`,
                                        }}>
                                          <Text size="xs" fw={600} style={{ color: issue.type === 'error' ? '#D33423' : issue.type === 'info' ? '#1d4ed8' : '#92400e' }}>{issue.message}</Text>
                                          <Text size="xs" c="dimmed">{issue.detail}</Text>
                                        </div>
                                      ))}
                                    </Stack>
                                  )}
                                </Paper>
                              );
                            })()}

                            {/* ── Supporting Data Downloads ── */}
                            <Paper p="sm" radius="md" withBorder mb="sm" style={{ background: '#fff' }}>
                              <Text size="sm" fw={700} mb="xs" c="var(--color-primary-700)">SUPPORTING DATA DOWNLOADS</Text>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.totalTickets === 0}
                                  onClick={() => {
                                    const csv = generateTicketCSV(detail.matchedTickets);
                                    downloadCSV(csv, `ticket_data_${detail.totalTickets}_records.csv`);
                                    notifications.show({ message: 'Ticket data CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download Ticket Data CSV
                                  <Text span size="xs" c="dimmed" ml={6}>{detail.totalTickets.toLocaleString()} tickets - all fields</Text>
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.truckCertCount === 0}
                                  onClick={() => {
                                    const csv = generateTruckManifestCSV(detail.trucks);
                                    downloadCSV(csv, `truck_manifest_${detail.truckCertCount}_trucks.csv`);
                                    notifications.show({ message: 'Truck manifest CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download Truck Manifest CSV
                                  <Text span size="xs" c="dimmed" ml={6}>{detail.truckCertCount} trucks - cert details + AI-extracted data</Text>
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.reconciliations.length === 0}
                                  onClick={() => {
                                    const csv = generateReconCSV(detail.reconciliations);
                                    downloadCSV(csv, `reconciliation_data.csv`);
                                    notifications.show({ message: 'Reconciliation CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download Reconciliation CSV
                                  <Text span size="xs" c="dimmed" ml={6}>Per-CLIN comparison data</Text>
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.sites.length === 0}
                                  onClick={() => {
                                    const csv = generateSiteSummaryCSV(detail.sites);
                                    downloadCSV(csv, `site_summary_${detail.sites.length}_sites.csv`);
                                    notifications.show({ message: 'Site summary CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download Site Summary CSV
                                  <Text span size="xs" c="dimmed" ml={6}>Disposal site details</Text>
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.faLabor.length === 0}
                                  onClick={() => {
                                    downloadCSV(generateLaborCSV(detail.faLabor), `fa_labor_${detail.faLabor.length}_records.csv`);
                                    notifications.show({ message: 'FA Labor CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download FA Labor CSV
                                  <Text span size="xs" c="dimmed" ml={6}>{detail.faLabor.length} records · {fmtMoney(detail.faLaborTotal)}</Text>
                                </Button>
                                <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
                                  disabled={detail.faEquip.length === 0}
                                  onClick={() => {
                                    downloadCSV(generateEquipmentCSV(detail.faEquip), `fa_equipment_${detail.faEquip.length}_records.csv`);
                                    notifications.show({ message: 'FA Equipment CSV downloaded', color: 'var(--color-success)' });
                                  }}>
                                  Download FA Equipment CSV
                                  <Text span size="xs" c="dimmed" ml={6}>{detail.faEquip.length} records · {fmtMoney(detail.faEquipTotal)}</Text>
                                </Button>
                              </div>
                            </Paper>

                            {/* Notes */}
                            {pkg.notes && (
                              <Paper p="sm" radius="md" withBorder mt="sm" style={{ background: '#fff' }}>
                                <Text size="xs" c="dimmed" fw={600}>Notes</Text>
                                <Text size="sm">{pkg.notes}</Text>
                              </Paper>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* ── Create / Edit Modal — 3-step wizard ─────────────── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit FEMA Package' : 'New FEMA Package'} centered size="lg">
        <Stack gap={20}>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            {[
              { n: 1, label: 'Identity' },
              { n: 2, label: 'Scope' },
              { n: 3, label: 'Review' },
            ].map(({ n, label }) => {
              const done = wizStep > n;
              const active = wizStep === n;
              return (
                <button
                  key={n}
                  onClick={() => { if (done || active) setWizStep(n); }}
                  style={{
                    flex: 1, padding: '10px 8px', border: 'none', cursor: done ? 'pointer' : 'default',
                    background: active ? '#4B3FFF' : done ? '#eff6ff' : '#f8fafc',
                    color: active ? '#fff' : done ? '#4B3FFF' : '#94a3b8',
                    fontWeight: active ? 700 : 500, fontSize: 13,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    borderRight: n < 3 ? '1px solid #e2e8f0' : 'none',
                    transition: 'background 0.15s',
                  }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                    background: active ? 'rgba(255,255,255,0.25)' : done ? '#4B3FFF' : '#e2e8f0',
                    color: active ? '#fff' : done ? '#fff' : '#94a3b8',
                    flexShrink: 0,
                  }}>
                    {done ? '✓' : n}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>

          {/* ── Step 1: Identity ── */}
          {wizStep === 1 && (
            <Stack gap={14}>
              <div>
                <Text fw={600} size="sm" mb={2}>What is this package for?</Text>
                <Text size="xs" c="dimmed">These identifiers appear on every exported document and tie back to your Project Worksheet in the FEMA Grants Portal.</Text>
              </div>

              <TextInput
                label="Package Name"
                required
                value={form.packageName}
                onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, packageName: v })); }}
                placeholder="e.g., PW-001 Vegetative Debris — Pass 1 (Helene)"
                description="A clear, descriptive name. Include the PW number and pass if applicable."
              />

              <SimpleGrid cols={2} style={{ gap: 12 }}>
                <TextInput
                  label="FEMA Disaster Number"
                  value={form.femaDisasterNumber}
                  onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, femaDisasterNumber: v })); }}
                  placeholder="DR-4834-FL"
                  description="From your FEMA declaration letter"
                />
                <TextInput
                  label="Project Worksheet (PW) #"
                  value={form.pwReference}
                  onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, pwReference: v })); }}
                  placeholder="PW-0012"
                  description="Your PA project reference number"
                />
              </SimpleGrid>

              <Group justify="flex-end" mt={4}>
                <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button disabled={!form.packageName.trim()} onClick={() => setWizStep(2)}>
                  Next: Define Scope →
                </Button>
              </Group>
            </Stack>
          )}

          {/* ── Step 2: Scope ── */}
          {wizStep === 2 && (
            <Stack gap={14}>
              <div>
                <Text fw={600} size="sm" mb={2}>Which tickets and contractors are in scope?</Text>
                <Text size="xs" c="dimmed">The package will pull all matching load tickets and automatically calculate totals. You'll see a live preview below.</Text>
              </div>

              <div>
                <Group justify="space-between" mb={4}>
                  <Text size="sm" fw={500}>Contractors</Text>
                  <Button
                    variant="subtle" size="xs"
                    onClick={() => {
                      const allIds = contractorSelect.map(c => c.value);
                      const allSelected = allIds.every(id => form.contractorIds.includes(id));
                      setForm(prev => ({ ...prev, contractorIds: allSelected ? [] : allIds }));
                    }}
                  >
                    {contractorSelect.every(c => form.contractorIds.includes(c.value)) ? 'Deselect all' : 'Select all'}
                  </Button>
                </Group>
                <MultiSelect
                  description="Which prime contractors does this package cover?"
                  data={contractorSelect}
                  value={form.contractorIds}
                  onChange={(val: string[]) => setForm(prev => ({ ...prev, contractorIds: val }))}
                  searchable
                  placeholder="Select one or more contractors..."
                />
              </div>

              <SimpleGrid cols={2} style={{ gap: 12 }}>
                <TextInput
                  label="Period Start"
                  type="date"
                  description="First date of work included"
                  value={(form.dateRangeStart || '').slice(0, 10)}
                  onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, dateRangeStart: v })); }}
                />
                <TextInput
                  label="Period End"
                  type="date"
                  description="Last date of work included"
                  value={(form.dateRangeEnd || '').slice(0, 10)}
                  onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, dateRangeEnd: v })); }}
                />
              </SimpleGrid>

              <MultiSelect
                label="Zones (optional)"
                description="Leave blank to include all zones. Use only if this PW covers a specific geographic area."
                data={zoneSelect}
                value={form.zoneIds}
                onChange={(val: string[]) => setForm(prev => ({ ...prev, zoneIds: val }))}
                searchable
                placeholder="All zones (default)"
              />

              {/* Live preview — updates as fields change */}
              {form.contractorIds.length > 0 && form.dateRangeStart && form.dateRangeEnd && (() => {
                const totals = computeTotals(form.contractorIds, form.dateRangeStart, form.dateRangeEnd);
                const hasAnything = totals.ticketCount > 0 || totals.invoiceCount > 0 || totals.laborCount > 0 || totals.equipCount > 0;
                return (
                  <Paper p="md" withBorder style={{ borderRadius: 8, background: hasAnything ? '#f0fdf4' : '#fffbeb', borderColor: hasAnything ? '#bbf7d0' : '#fde68a' }}>
                    <Text size="xs" fw={700} style={{ color: hasAnything ? 'var(--color-success)' : '#92400e' }} mb={10}>
                      {hasAnything ? 'Scope preview (live)' : '⚠ No matching data found for this scope'}
                    </Text>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                      <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, borderLeft: '3px solid #4B3FFF' }}>
                        <Text size="xs" c="dimmed">Load Tickets</Text>
                        <Text size="sm" fw={700}>{totals.ticketCount.toLocaleString()} tickets · {totals.totalCY.toLocaleString(undefined, { maximumFractionDigits: 1 })} CY</Text>
                      </div>
                      <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, borderLeft: '3px solid #0284c7' }}>
                        <Text size="xs" c="dimmed">Contractor Invoices</Text>
                        <Text size="sm" fw={700}>{totals.invoiceCount} invoice{totals.invoiceCount !== 1 ? 's' : ''} · {fmtMoney(totals.invoiceCost)}</Text>
                      </div>
                      <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, borderLeft: '3px solid #d97706' }}>
                        <Text size="xs" c="dimmed">Force Account — Labor</Text>
                        <Text size="sm" fw={700}>{totals.laborCount} record{totals.laborCount !== 1 ? 's' : ''} · {fmtMoney(totals.laborCost)}</Text>
                      </div>
                      <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, borderLeft: '3px solid #7c3aed' }}>
                        <Text size="xs" c="dimmed">Force Account — Equipment</Text>
                        <Text size="sm" fw={700}>{totals.equipCount} record{totals.equipCount !== 1 ? 's' : ''} · {fmtMoney(totals.equipCost)}</Text>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #d1fae5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text size="xs" c="dimmed">{allTickets.length.toLocaleString()} total tickets in system</Text>
                      <Text size="sm" fw={800} style={{ color: 'var(--color-success)' }}>Grand Total: {fmtMoney(totals.grandTotal)}</Text>
                    </div>
                  </Paper>
                );
              })()}

              {form.contractorIds.length === 0 && (
                <Paper p="sm" withBorder style={{ background: '#f8fafc', borderRadius: 8, borderColor: '#e2e8f0', textAlign: 'center' }}>
                  <Text size="xs" c="dimmed">Select contractors and a date range to see a live ticket preview</Text>
                </Paper>
              )}

              <Group justify="space-between" mt={4}>
                <Button variant="subtle" onClick={() => setWizStep(1)}>← Back</Button>
                <Group gap={8}>
                  <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
                  <Button
                    disabled={form.contractorIds.length === 0 || !form.dateRangeStart || !form.dateRangeEnd}
                    onClick={() => setWizStep(3)}>
                    Next: Review →
                  </Button>
                </Group>
              </Group>
            </Stack>
          )}

          {/* ── Step 3: Review & Save ── */}
          {wizStep === 3 && (() => {
            const totals = computeTotals(form.contractorIds, form.dateRangeStart, form.dateRangeEnd);
            const selectedContractorNames = form.contractorIds.map(id => contractors.find(c => String(c.id) === id)?.companyName || id);
            return (
              <Stack gap={14}>
                <div>
                  <Text fw={600} size="sm" mb={2}>Review and save</Text>
                  <Text size="xs" c="dimmed">Confirm the details below, add any notes, then save.</Text>
                </div>

                {/* Identity summary */}
                <Paper p="md" withBorder style={{ borderRadius: 8, background: '#f8fafc' }}>
                  <Stack gap={8}>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={600}>PACKAGE NAME</Text>
                      <Text size="sm" fw={700}>{form.packageName}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={600}>DISASTER / PW</Text>
                      <Text size="sm">{[form.femaDisasterNumber, form.pwReference].filter(Boolean).join(' · ') || '—'}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={600}>PERIOD</Text>
                      <Text size="sm">{form.dateRangeStart} → {form.dateRangeEnd}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={600}>CONTRACTORS</Text>
                      <Text size="sm" style={{ textAlign: 'right', maxWidth: 280 }}>{selectedContractorNames.join(', ') || '—'}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={600}>ZONES</Text>
                      <Text size="sm">{form.zoneIds.length > 0 ? `${form.zoneIds.length} zone(s)` : 'All zones'}</Text>
                    </Group>
                  </Stack>
                </Paper>

                {/* Cost breakdown */}
                <Paper p="md" withBorder style={{ borderRadius: 8, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <Text size="xs" fw={700} c="dimmed" mb={10}>COST BREAKDOWN</Text>
                  <Stack gap={6}>
                    {[
                      { label: 'Contractor Invoices', count: `${totals.invoiceCount} invoices`, cost: totals.invoiceCost, color: '#0284c7' },
                      { label: 'Force Account — Labor', count: `${totals.laborCount} records`, cost: totals.laborCost, color: '#d97706' },
                      { label: 'Force Account — Equipment', count: `${totals.equipCount} records`, cost: totals.equipCost, color: '#7c3aed' },
                    ].map(row => (
                      <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: 'rgba(255,255,255,0.7)', borderRadius: 6, borderLeft: `3px solid ${row.color}` }}>
                        <div>
                          <Text size="xs" fw={600} style={{ color: row.color }}>{row.label}</Text>
                          <Text size="xs" c="dimmed">{row.count}</Text>
                        </div>
                        <Text size="sm" fw={700} ff="monospace">{fmtMoney(row.cost)}</Text>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#dcfce7', borderRadius: 6, marginTop: 4 }}>
                      <Text size="sm" fw={700}>Grand Total</Text>
                      <Text size="lg" fw={800} ff="monospace" style={{ color: 'var(--color-success)' }}>{fmtMoney(totals.grandTotal)}</Text>
                    </div>
                  </Stack>
                  <Text size="xs" c="dimmed" mt={8}>Volume: {totals.ticketCount.toLocaleString()} load tickets · {totals.totalCY.toLocaleString(undefined, { maximumFractionDigits: 1 })} CY</Text>
                </Paper>

                <Select
                  label="Status"
                  description={editTarget ? 'Update the lifecycle status of this package' : 'New packages start as Draft — advance when ready for FEMA submission'}
                  data={STATUS_OPTIONS}
                  value={form.status}
                  onChange={v => setForm(prev => ({ ...prev, status: (v as FEMAPackageStatus) ?? 'draft' }))}
                />

                <Textarea
                  label="Notes (optional)"
                  description="Internal notes about this package — audit trail, reviewer comments, etc."
                  value={form.notes}
                  onChange={(e: any) => { const v = e?.currentTarget?.value ?? e?.target?.value ?? ''; setForm(prev => ({ ...prev, notes: v })); }}
                  autosize minRows={2}
                />

                <Group justify="space-between" mt={4}>
                  <Button variant="subtle" onClick={() => setWizStep(2)}>← Back</Button>
                  <Group gap={8}>
                    <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button loading={saving} onClick={handleSave}>
                      {editTarget ? 'Save Changes' : 'Create Package'}
                    </Button>
                  </Group>
                </Group>
              </Stack>
            );
          })()}

        </Stack>
      </Modal>

      {/* ── Delete Confirmation ───────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Package" centered size="sm">
        <Stack>
          <Text size="sm">Delete FEMA package <strong>{deleteTarget?.packageName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
